import MuleBadge from '../../components/MuleBadge';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import 'gridstack/dist/gridstack.min.css';
import {
  SparklesIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  CheckIcon,
  XMarkIcon,
  LinkIcon,
  DocumentCheckIcon,
  LightBulbIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  UsersIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import DateTimePicker from '../../components/DateTimePicker';
import { Drawer } from '../../components/ui/Drawer';
import { Tooltip } from '../../components/ui/Tooltip';
import InfoModal from '../../components/common/InfoModal';
import { useDeadlineCheck } from '../../hooks/useDeadlineCheck';
import ReasonNoteModal from '../../components/ReasonNoteModal';
import dispatchRemovalService from '../../services/dispatchRemoval.service';
import { usePermissions } from '../../contexts/PermissionContext';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useIconLegend } from '../../contexts/IconLegendContext';
import { dispatchLegendSections } from '../../utils/dispatchLegendSections';
import preplanningService from '../../services/preplanning.service';
import ordersService from '../../services/orders.service';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import SmartDispatchConfigModal from '../../components/dispatching/SmartDispatchConfigModal';
import { config } from '../../config/config';
import PermissionsContainer, { useResolvedAccessLevel } from '../../components/PermissionsContainer';
import { PermissionAwareInput, PermissionAwareButton, PermissionAwareSelect } from '../../contexts/PermissionAccessContext';
import {
  GeneratePrePlanResponse,
  GeneratePrePlanRequest,
  DriverDayPlan,
  PlannedMove,
  UnassignedMove,
  ScoreComponents,
  AvailableDriver,
  NearMiss,
} from '../../types/preplanning';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';
import { saveBlob } from '../../utils/saveFile';
import { destinationStop } from './preplan-v2/destinationStop';
import { driverRemoved, planUndo, reuseError, type UndoableMove } from './preplan-v2/undoPlan';
import { locationPreferenceTone } from './preplan-v2/locationPreference';
import { LOAD_FILTERS, matchesLoadFilters, type LoadFilter } from './preplan-v2/loadFilters';
import { scoreBoosts, weightedScore } from './preplan-v2/scoreModel';
import { applyError, splitApplyResults, type AppliedMove, type ApplyResultRow, type HeldMove } from './preplan-v2/applyResults';
import { applyBatches, progressPercent, timeLeft, type ApplyProgress } from './preplan-v2/applyBatches';

// ============================================================================
// Types for Plan Modification Tracking
// ============================================================================

/** Represents a move or group of moves that can be dragged */
interface DraggableItem {
  id: string; // e.g., "4867889-2" or "group_abc123" for grouped moves
  moves: PlannedMove[];
  isGroup: boolean;
  dispatch_group_id?: string;
}

/** Modified assignment - tracks changes from original plan */
interface ModifiedAssignment {
  pronumber: number;
  legnum: number;
  originalDriver: string | null; // null if was unassigned
  newDriver: string | null; // null if now unassigned
  newSequence: number;
}

/** Local state for a driver's assigned moves */
interface LocalDriverPlan {
  unitnumber: string;
  driver_name: string;
  isMule?: boolean;
  items: DraggableItem[]; // Ordered list of draggable items
  // Qualification flags
  has_hazmat?: boolean;
  has_tank?: boolean;
  has_93k?: boolean;
  is_daycab?: boolean;
  is_light_truck?: boolean;
  has_ppe?: boolean;
  // Location preferences (cms.locations.recno) — tint the driver's cards
  preferred_location_ids?: number[];
  not_preferred_location_ids?: number[];
  // Near misses for empty drivers
  near_misses?: NearMiss[];
}

// ============================================================================
// Helper Functions
// ============================================================================

const formatScore = (score: number | undefined): string => {
  if (score === undefined || score === null) return '-';
  return `${Math.round(score * 100)}%`;
};

const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return '-';
  return `$${value.toFixed(2)}`;
};

// Reusable component for displaying rejected driver categories
const RejectionCategory: React.FC<{
  title: string;
  reasons: string[] | undefined;
}> = ({ title, reasons }) => {
  if (!reasons || reasons.length === 0) return null;
  return (
    <div>
      <div className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-2">
        {title} ({reasons.length})
      </div>
      <div className="space-y-1 max-h-32 overflow-y-auto">
        {reasons.map((reason, idx) => (
          <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 pl-2 border-l-2 border-orange-300 dark:border-orange-700">
            {reason}
          </div>
        ))}
      </div>
    </div>
  );
};

const getScoreColor = (score: number): string => {
  if (score >= 0.7) return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
  if (score >= 0.5) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
  return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30';
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '-';
  }
};

/** Format a Date as YYYY-MM-DD in the user's local timezone (avoids UTC shift from toISOString). */
const formatLocalDate = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = (dateTimeStr?: string): string => {
  if (!dateTimeStr) return '-';
  try {
    const date = new Date(dateTimeStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '-';
  }
};

const formatDuration = (minutes?: number): string => {
  if (!minutes) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

/** The stop after a leg, under its drive time: where the planner holds the driver
 * until their next leg can start (preplan-v2/destinationStop). */
const DestinationStopLine: React.FC<{ move: PlannedMove }> = ({ move }) => {
  const stop = destinationStop(move);
  if (!stop) return null;
  return (
    <div className="flex items-center justify-between" title={stop.title}>
      <span className="truncate">At dest · {stop.label}</span>
      <span className="text-label tabular-nums shrink-0 ml-1">{stop.window}</span>
    </div>
  );
};

/**
 * Groups moves by dispatch_group_id and creates draggable items.
 * Moves with the same dispatch_group_id are combined into a single draggable item.
 */
const createDraggableItems = (moves: PlannedMove[]): DraggableItem[] => {
  const items: DraggableItem[] = [];
  const processedGroups = new Set<string>();

  for (const move of moves) {
    // Check if this move is part of a dispatch group
    if (move.dispatch_group_id && move.group_position !== 'only') {
      // Skip if we've already processed this group
      if (processedGroups.has(move.dispatch_group_id)) continue;
      processedGroups.add(move.dispatch_group_id);

      // Find all moves in this group
      const groupMoves = moves.filter(m => m.dispatch_group_id === move.dispatch_group_id);
      // Sort by sequence to maintain order within group
      groupMoves.sort((a, b) => a.sequence - b.sequence);

      items.push({
        id: `group_${move.dispatch_group_id}`,
        moves: groupMoves,
        isGroup: true,
        dispatch_group_id: move.dispatch_group_id,
      });
    } else {
      // Single move (not grouped)
      items.push({
        id: `${move.pronumber}-${move.legnum}`,
        moves: [move],
        isGroup: false,
      });
    }
  }

  // Sort items by the first move's sequence
  items.sort((a, b) => a.moves[0].sequence - b.moves[0].sequence);

  return items;
};

/**
 * Converts the generated plan to local editable state
 */
const convertToLocalState = (
  driverPlans: DriverDayPlan[],
  unassignedMoves: UnassignedMove[],
  availableDrivers?: AvailableDriver[]
): { drivers: LocalDriverPlan[]; unassigned: DraggableItem[] } => {
  // Build lookup map for qualification data
  const qualificationMap = new Map<string, AvailableDriver>();
  if (availableDrivers) {
    for (const ad of availableDrivers) {
      qualificationMap.set(ad.unitnumber, ad);
    }
  }

  // Convert driver plans with qualification data
  const drivers: LocalDriverPlan[] = driverPlans.map(dp => {
    // Use qualification data from driver plan directly (preferred), with fallback to available_drivers
    const qualData = qualificationMap.get(dp.unitnumber);
    return {
      unitnumber: dp.unitnumber,
      driver_name: dp.driver_name,
      isMule: dp.isMule ?? qualData?.isMule,
      items: createDraggableItems(dp.moves),
      // Include qualification data - prefer driver plan fields, fallback to available_drivers
      has_hazmat: dp.has_hazmat ?? qualData?.has_hazmat,
      has_tank: dp.has_tank ?? qualData?.has_tank,
      has_93k: dp.has_93k ?? qualData?.has_93k,
      is_daycab: dp.is_daycab ?? qualData?.is_daycab,
      is_light_truck: dp.is_light_truck ?? qualData?.is_light_truck,
      has_ppe: dp.has_ppe ?? qualData?.has_ppe,
      preferred_location_ids: dp.preferred_location_ids ?? qualData?.preferred_location_ids,
      not_preferred_location_ids: dp.not_preferred_location_ids ?? qualData?.not_preferred_location_ids,
    };
  });

  // Convert unassigned moves to draggable items
  // Note: UnassignedMove has a different structure, so we create minimal PlannedMove objects
  const unassignedAsMoves: PlannedMove[] = unassignedMoves.map((um, idx) => ({
    pronumber: um.pronumber,
    legnum: um.legnum,
    sequence: idx + 1,
    origin_name: um.origin_name,
    origin_city: um.origin_city,
    dest_name: um.dest_name,
    dest_city: um.dest_city,
    dest_appt_date: um.dest_appt_date,
    dest_appt_time1: um.dest_appt_time1,
    dest_appt_time2: um.dest_appt_time2,
    origcode: um.origcode,
    destcode: um.destcode,
    origin_appt_time: um.origin_appt_time,
    origin_appt_time2: um.origin_appt_time2,
    move_category: um.move_category as PlannedMove['move_category'],
    delivery_leg: um.delivery_leg,
    is_early_appointment: um.is_early_appointment,
    estimated_duration_minutes: 0,
    travel_time_from_previous: 0,
    is_existing: false,
    has_hard_appointment: false,
    legdrop: um.legdrop || 'N',
  }));

  const unassigned = createDraggableItems(unassignedAsMoves);

  return { drivers, unassigned };
};

/**
 * Converts local editable state back to driver_plans format for AI analysis.
 * This ensures AI analyzes the current state after user modifications.
 */
const buildCurrentDriverPlans = (
  localDrivers: LocalDriverPlan[]
): Record<string, unknown>[] => {
  return localDrivers
    .filter(d => d.items.length > 0) // Only include drivers with assignments
    .map(driver => {
      // Flatten all moves from all items
      const allMoves = driver.items.flatMap((item, itemIdx) =>
        item.moves.map((move, moveIdx) => ({
          ...move,
          // Update sequence based on current order
          sequence: itemIdx * 10 + moveIdx + 1,
        }))
      );

      return {
        unitnumber: driver.unitnumber,
        driver_name: driver.driver_name,
        isMule: driver.isMule,
        moves: allMoves,
        total_moves: allMoves.length,
        existing_moves: allMoves.filter(m => m.is_existing).length,
        new_moves: allMoves.filter(m => !m.is_existing).length,
      };
    });
};

/**
 * Converts local unassigned items back to unassigned_moves format for AI analysis.
 */
const buildCurrentUnassignedMoves = (
  localUnassigned: DraggableItem[]
): Record<string, unknown>[] => {
  return localUnassigned.flatMap(item =>
    item.moves.map(move => ({
      pronumber: move.pronumber,
      legnum: move.legnum,
      origin_name: move.origin_name,
      origin_city: move.origin_city,
      dest_name: move.dest_name,
      dest_city: move.dest_city,
      dest_appt_date: move.dest_appt_date,
      dest_appt_time1: move.dest_appt_time1,
      dest_appt_time2: move.dest_appt_time2,
    }))
  );
};

// ============================================================================
// Sub-Components
// ============================================================================

interface ScoreBreakdownProps {
  score: number;
  components?: ScoreComponents;
}

const ScoreBreakdown: React.FC<ScoreBreakdownProps> = ({ score, components }) => (
  <div className="text-xs space-y-1 min-w-[180px]">
    <div className="font-semibold border-b border-rule pb-1 mb-1">
      Score Breakdown
    </div>
    <div className="flex justify-between">
      <span className="text-muted">Overall:</span>
      <span className="font-medium">{formatScore(score)}</span>
    </div>
    {components && (
      <>
        <div className="border-t border-rule pt-1 mt-1">
          <div className="flex justify-between">
            <span className="text-muted">Distance:</span>
            <span>{formatScore(components.distance)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted">Workload:</span>
            <span>{formatScore(components.workload)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted">Profitability:</span>
            <span>{formatScore(components.profitability)}</span>
          </div>
          {components.driver_score !== undefined && (
            <div className="flex justify-between">
              <span className="text-muted">Driver Score:</span>
              <span>{formatScore(components.driver_score)}</span>
            </div>
          )}
          {scoreBoosts(components).map(boost => (
            <div key={boost.label} className="flex justify-between">
              <span className="text-muted">{boost.label}:</span>
              <span>×{boost.factor}</span>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
);

interface AssignmentReasoningProps {
  move: PlannedMove;
}

const AssignmentReasoning: React.FC<AssignmentReasoningProps> = ({ move }) => {
  const ctx = move.driver_context;
  const decisionPath = ctx?.decision_path;

  // Debug log to see what data we're receiving
  console.log('AssignmentReasoning - driver_context:', ctx);

  // Tree line characters for monospace rendering
  const BRANCH = '├──';
  const LAST_BRANCH = '└──';
  const PIPE = '│';

  return (
    <div className="text-xs min-w-[320px] max-w-[400px] bg-gray-900 dark:bg-gray-950 text-gray-100 p-3 rounded">
      <div className="font-bold text-white mb-2 font-sans">Decision Tree</div>

      {decisionPath && decisionPath.length > 0 ? (
        <div className="space-y-0 whitespace-pre leading-tight">
          {decisionPath.map((step, idx) => {
            const isLast = idx === decisionPath.length - 1;
            const branch = isLast ? LAST_BRANCH : BRANCH;
            const hasOthersFailed = step.others_failed > 0;

            return (
              <div key={idx}>
                {/* Question line */}
                <div className="text-cyan-300">
                  {branch} {step.question}
                </div>
                {/* Yes branch (this driver passed) */}
                <div className="text-green-400">
                  {isLast ? '    ' : `${PIPE}   `}{LAST_BRANCH} <span className="text-green-300">Yes</span> → {step.detail}
                </div>
                {/* Show others who failed if any */}
                {hasOthersFailed && (
                  <div className="text-red-400">
                    {isLast ? '    ' : `${PIPE}   `}    <span className="text-gray-500">({step.others_failed} drivers failed this check)</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* Final selection result */}
          <div className="mt-2 pt-2 border-t border-gray-700">
            <span className="text-yellow-400">★ SELECTED</span>
            <span className="text-gray-400"> - Best of {ctx?.qualified_drivers || '?'} qualified drivers</span>
          </div>
        </div>
      ) : (
        /* Fallback to simple display if no decision path */
        <div className="space-y-1 font-sans">
          {ctx ? (
            <>
              <div className="text-green-400">Driver selected based on:</div>
              <div className="pl-2 text-gray-300">
                {ctx.distance_miles !== undefined && <div>• Distance: {ctx.distance_miles.toFixed(1)} mi</div>}
                {ctx.remaining_dispatches !== undefined && <div>• Capacity: {ctx.remaining_dispatches} slots</div>}
                {ctx.daily_goal && ctx.daily_goal > 0 && (
                  <div>• Goal progress: {((ctx.current_pay / ctx.daily_goal) * 100).toFixed(0)}%</div>
                )}
              </div>
            </>
          ) : (
            <div className="text-gray-500 italic">No decision details available</div>
          )}
        </div>
      )}
    </div>
  );
};

interface MoveRowProps {
  move: PlannedMove;
  onDebugClick: () => void;
  onProClick?: (pronumber: number, legnum: number) => void;
}

// Helper to get border classes for dispatch group styling
// Uses individual border-color classes to override the parent's divide-y styling
const getGroupBorderClasses = (groupPosition?: string): string => {
  if (!groupPosition || groupPosition === 'only') return '';

  // Use individual border color classes (border-l-*, border-r-*, etc.) instead of
  // shorthand border-color to ensure they override the divide-y parent styling
  const baseClasses = 'border-l-4 border-l-purple-400 dark:border-l-purple-500 border-r-4 border-r-purple-400 dark:border-r-purple-500';

  switch (groupPosition) {
    case 'first':
      return `${baseClasses} border-t-4 border-t-purple-400 dark:border-t-purple-500 rounded-t-lg`;
    case 'middle':
      return `${baseClasses} border-t-0`;
    case 'last':
      return `${baseClasses} border-t-0 border-b-4 border-b-purple-400 dark:border-b-purple-500 rounded-b-lg`;
    default:
      return '';
  }
};

const MoveRow: React.FC<MoveRowProps> = ({ move, onDebugClick, onProClick }) => {
  const isExisting = move.is_existing;
  const isGrouped = move.group_position && move.group_position !== 'only';
  const groupBorderClasses = getGroupBorderClasses(move.group_position);

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 ${
        isExisting
          ? isGrouped
            ? 'bg-blue-50/70 dark:bg-blue-900/20'
            : 'bg-gray-50 dark:bg-gray-700/30'
          : isGrouped
            ? 'bg-purple-50/50 dark:bg-purple-900/10'
            : 'bg-panel hover:bg-gray-50 dark:hover:bg-gray-750'
      } ${
        isGrouped
          ? groupBorderClasses
          : 'border-b border-gray-100 dark:border-gray-700 last:border-b-0'
      }`}
    >
      {/* Sequence Number */}
      <span className="w-6 text-center text-xs font-medium text-gray-400 dark:text-gray-500">
        #{move.sequence}
      </span>

      {/* PRO Number - clickable to open drawer, with external link to legacy CMS */}
      <div className="flex items-center gap-1 w-28">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onProClick?.(move.pronumber, move.legnum);
          }}
          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
        >
          {move.pronumber}-{move.legnum}
        </button>
        <a
          href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${move.pronumber}&searchtype=pronumber`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400"
          title="Open in Legacy CMS"
        >
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </a>
      </div>

      {/* THIS LEG IS SOMEBODY ELSE'S RIGHT NOW. Only a replan can produce one, and
          applying it takes the work out of the named driver's day — the one change on
          a board that costs a person something. It looked identical to new work. */}
      {move.reassigned_from && (
        <span
          className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold text-white bg-amber-500 ring-1 ring-amber-600"
          title={`Currently dispatched to driver ${move.reassigned_from}. Applying this plan moves it off their day.`}
        >
          from {move.reassigned_from}
        </span>
      )}

      {/* Same reason as the badge above, and the same rule: a move-level indicator
          has to be on BOTH this row and DraggableMoveCard, or it is invisible on
          whichever board the dispatcher happens to be looking at. */}
      {move.street_turn_export && (
        <span
          className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold text-white bg-violet-600 ring-1 ring-violet-700"
          title={`Proposed street turn: this container goes straight to the shipper on export ${move.street_turn_export} instead of back to the terminal.`}
        >
          turn &#8594; {move.street_turn_export}
        </span>
      )}

      {/* Linked Leg Indicator */}
      {isGrouped && (
        <Tooltip
          content={
            <div className="text-xs">
              <div className="font-semibold mb-1">Linked Dispatch Group</div>
              <div>Leg {move.group_position === 'first' ? '1' : move.group_position === 'last' ? move.group_leg_count : ''} of {move.group_leg_count}</div>
              {move.group_total_miles !== undefined && (
                <div>Group total: {move.group_total_miles.toFixed(1)} mi</div>
              )}
            </div>
          }
          placement="top"
        >
          <span className="flex items-center">
            <LinkIcon className="h-4 w-4 text-purple-500 dark:text-purple-400" />
          </span>
        </Tooltip>
      )}

      {/* Route */}
      <div className="flex-1 min-w-0">
        <div
          className="text-sm text-heading truncate"
          title={`${move.origin_name}${move.fallback_origin_name ? ` (${move.fallback_origin_name})` : ''} → ${move.dest_name}${move.fallback_dest_name ? ` (${move.fallback_dest_name})` : ''}`}
        >
          {move.origin_name}
          {move.fallback_origin_name && (
            <span className="text-orange-500 dark:text-orange-400 text-xs ml-1">({move.fallback_origin_name})</span>
          )}
          {' → '}
          {move.dest_name}
          {move.fallback_dest_name && (
            <span className="text-orange-500 dark:text-orange-400 text-xs ml-1">({move.fallback_dest_name})</span>
          )}
        </div>
        <div
          className="text-xs text-muted truncate"
          title={`${move.origin_city}${move.origin_state ? `, ${move.origin_state}` : ''}${move.fallback_origin_display ? ` (${move.fallback_origin_display})` : ''} → ${move.dest_city}${move.dest_state ? `, ${move.dest_state}` : ''}${move.fallback_dest_display ? ` (${move.fallback_dest_display})` : ''}`}
        >
          {move.origin_city}{move.origin_state ? `, ${move.origin_state}` : ''}
          {move.fallback_origin_display && (
            <span className="text-orange-500 dark:text-orange-400"> ({move.fallback_origin_display})</span>
          )}
          {' → '}
          {move.dest_city}{move.dest_state ? `, ${move.dest_state}` : ''}
          {move.fallback_dest_display && (
            <span className="text-orange-500 dark:text-orange-400"> ({move.fallback_dest_display})</span>
          )}
          {move.is_return_move && move.distance_from_delivery !== undefined ? (
            <span className="ml-2 text-yellow-600 dark:text-yellow-400">
              ({move.distance_from_delivery.toFixed(1)} mi from delivery)
            </span>
          ) : move.leg_miles !== undefined && move.leg_miles > 0 && (
            <span className="ml-2 text-gray-400">({move.leg_miles.toFixed(1)} mi)</span>
          )}
        </div>
        {(move.chassis_type || move.chassis_size) && (
          <div className="text-xs text-gray-400 dark:text-gray-500">
            Chassis: {move.chassis_size}{move.chassis_size && move.chassis_type && ' / '}{move.chassis_type}
          </div>
        )}
      </div>

      {/* Estimated Times */}
      <div className="w-28 text-right">
        {move.estimated_start && (
          <div className="text-xs text-heading">
            {formatTime(move.estimated_start)} - {formatTime(move.estimated_end)}
          </div>
        )}
        <div className="text-xs text-muted">
          {move.travel_time_from_previous > 0 && (
            <span className="text-orange-500 dark:text-orange-400">
              +{formatDuration(move.travel_time_from_previous)} travel
            </span>
          )}
          {!move.estimated_start && !move.travel_time_from_previous && (
            <span className="italic">-</span>
          )}
        </div>
      </div>

      {/* Appointment */}
      <div className="w-24 text-right">
        <div className="text-xs text-heading">
          {formatDate(move.dest_appt_date)}
        </div>
        {move.dest_appt_time1 && (
          <div className="text-xs text-muted">
            {move.dest_appt_time1}
            {move.dest_appt_time2 && ` - ${move.dest_appt_time2}`}
          </div>
        )}
        {move.origin_appt_time && (
          <div className="text-[0.625rem] text-gray-400 dark:text-gray-500">
            PU: {move.origin_appt_time}{move.origin_appt_time2 && ` - ${move.origin_appt_time2}`}
          </div>
        )}
      </div>

      {/* Type Badge */}
      <Tooltip content={
        move.move_category?.includes('prepull') ? 'Pre-Pull'
        : move.move_category === 'export-return' ? 'Export Terminate'
        : move.is_return_move ? `Return Move${move.distance_from_delivery !== undefined ? ` (${move.distance_from_delivery.toFixed(1)} mi from delivery)` : ''}`
        : move.legdrop === 'Y' ? 'Drop Delivery'
        : 'Live Delivery'
      }>
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded ${
            move.move_category?.includes('prepull')
              ? 'text-cyan-700 dark:text-cyan-300 bg-cyan-100 dark:bg-cyan-900/30'
              : move.move_category === 'export-return'
              ? 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30'
              : move.is_return_move
              ? 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30'
              : move.legdrop === 'Y'
              ? 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30'
              : 'text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30'
          }`}
        >
          {move.move_category?.includes('prepull') ? 'Pre-Pull' : move.move_category === 'export-return' ? 'Term' : move.is_return_move ? 'Return' : move.legdrop === 'Y' ? 'Drop' : 'Live'}
        </span>
      </Tooltip>

      {/* Score - don't display for existing moves or regular return moves (export-returns can have scores) */}
      {isExisting ? (
        <span className="w-16 text-center text-xs text-gray-400 dark:text-gray-500 italic">
          existing
        </span>
      ) : (move.is_return_move && move.move_category !== 'export-return') ? (
        <span className="w-16" />
      ) : move.assignment_score !== undefined ? (
        <div className="flex items-center gap-1">
          <Tooltip
            content={<ScoreBreakdown score={move.assignment_score} components={move.score_components} />}
            placement="left"
          >
            <span
              className={`w-16 text-center px-2 py-0.5 rounded text-xs font-medium cursor-help ${getScoreColor(
                move.assignment_score
              )}`}
            >
              {formatScore(move.assignment_score)}
            </span>
          </Tooltip>
          <Tooltip
            content={<AssignmentReasoning move={move} />}
            placement="left"
          >
            <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
          </Tooltip>
        </div>
      ) : (
        <span className="w-16" />
      )}

      {/* Debug Button */}
      {!isExisting && (
        <button
          onClick={onDebugClick}
          className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          title="View details"
        >
          <InformationCircleIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

// ============================================================================
// Draggable Move Card Component (for GridStack/Kanban view)
// ============================================================================

interface DraggableMoveCardProps {
  item: DraggableItem;
  driverUnitnumber: string | null; // null if unassigned
  priority: number; // 1-based priority/sequence number
  onDragStart: (e: React.DragEvent, item: DraggableItem, sourceDriver: string | null) => void;
  onDebugClick: (move: PlannedMove) => void;
  onProClick?: (pronumber: number, legnum: number) => void;
  appliedMoves?: Set<string>; // Track which moves have been applied
  onQuickAssign?: (item: DraggableItem, targetUnitnumber: string) => void; // Quick assign to driver
  onSendToStaging?: (item: DraggableItem) => void; // Pull the move off its lane into the staging drawer
  /** Driver's feeling about the card's locations; tints the card green or red. */
  preference?: ReturnType<typeof locationPreferenceTone>;
  /** Set while a Live / Drop / Early appt quick filter is on: whether a move is one it asks for. */
  loadFilterMatch?: (move: PlannedMove) => boolean;
  // Multi-select props
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
}

const DraggableMoveCard: React.FC<DraggableMoveCardProps> = ({
  item,
  driverUnitnumber,
  priority,
  onDragStart,
  onDebugClick,
  onProClick,
  appliedMoves = new Set(),
  onQuickAssign,
  onSendToStaging,
  preference = null,
  loadFilterMatch,
  isSelected = false,
  onSelectionChange,
}) => {
  const [showQuickAssign, setShowQuickAssign] = useState(false);
  const [quickAssignInput, setQuickAssignInput] = useState('');
  const quickAssignRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);

  const isGrouped = item.isGroup;
  const hasExistingMove = item.moves.some(m => m.is_existing);
  // Check if ALL moves in this item have been applied (either in this session or by a previous preplan)
  const isApplied = item.moves.every(m =>
    appliedMoves.has(`${m.pronumber}-${m.legnum}`) || m.was_applied_by_preplan
  );
  // null when no quick filter is on; otherwise whether this card holds a move it asks for.
  const filterHit = loadFilterMatch ? item.moves.some(loadFilterMatch) : null;

  // Fixed card height for consistent layout
  const cardHeight = 140;

  // Close quick assign popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        quickAssignRef.current && !quickAssignRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setShowQuickAssign(false);
        setQuickAssignInput('');
      }
    };
    if (showQuickAssign) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus input when popup opens
      setTimeout(() => inputRef.current?.focus(), 0);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQuickAssign]);

  const handleQuickAssignSubmit = () => {
    if (quickAssignInput.trim() && onQuickAssign) {
      onQuickAssign(item, quickAssignInput.trim());
      setShowQuickAssign(false);
      setQuickAssignInput('');
    }
  };

  return (
    <div
      draggable={!isApplied}
      onDragStart={(e) => !isApplied && onDragStart(e, item, driverUnitnumber)}
      className={`
        relative rounded-lg shadow-sm border-2
        transition-all group
        ${isApplied
          ? isSelected
            ? 'opacity-100 cursor-default border-orange-400 dark:border-orange-500'
            : 'opacity-50 hover:opacity-100 cursor-default border-green-400 dark:border-green-600'
          : 'cursor-move hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 border-gray-200 dark:border-gray-700'
        }
        ${isGrouped ? '' : 'w-full'}
        ${isApplied
          ? 'bg-gray-100 dark:bg-gray-800/50'
          : preference?.tone === 'not_preferred'
            ? 'bg-red-50 dark:bg-red-900/20'
            : preference?.tone === 'preferred'
              ? 'bg-green-50 dark:bg-green-900/20'
              : hasExistingMove
                ? 'bg-blue-50 dark:bg-blue-900/20'
                : 'bg-panel'
        }
        ${!isApplied && isGrouped
          ? hasExistingMove
            ? 'border-blue-400 dark:border-blue-500'
            : 'border-purple-400 dark:border-purple-500'
          : ''
        }
        ${!isApplied && !isGrouped && hasExistingMove
          ? 'border-blue-400 dark:border-blue-500'
          : ''
        }
        ${filterHit ? 'ring-2 ring-sky-500 dark:ring-sky-400 ring-offset-1 dark:ring-offset-gray-900' : ''}
        ${filterHit === false && !isApplied ? 'opacity-50 hover:opacity-100' : ''}
      `}
      style={{ height: `${cardHeight}px` }}
      title={!isApplied && preference ? preference.title : undefined}
    >
      {/* Applied badge - top right corner */}
      {isApplied && (
        <div className="absolute top-1 right-1 z-10">
          <div className={`px-1.5 py-0.5 text-white text-[0.625rem] font-semibold rounded ${isSelected ? 'bg-orange-500' : 'bg-green-500'}`}>
            {isSelected ? 'REMOVING' : 'APPLIED'}
          </div>
        </div>
      )}

      {/* Remove from plan button - bottom center, shown on hover for applied moves */}
      {isApplied && onSelectionChange && driverUnitnumber && (
        <div className={`absolute bottom-2 left-1/2 -translate-x-1/2 z-20 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelectionChange(!isSelected);
            }}
            className={`px-3 py-1 text-xs font-medium rounded-full shadow-md transition-colors whitespace-nowrap ${
              isSelected
                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                : 'bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-500'
            }`}
          >
            {isSelected ? 'Selected For Removal' : 'Remove From Plan'}
          </button>
        </div>
      )}
      {/* Priority number - floating badge */}
      <div className={`absolute -top-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center shadow-sm z-10 ${isApplied ? 'bg-green-600 dark:bg-green-700' : 'bg-gray-600 dark:bg-gray-500'}`}>
        <span className="text-xs font-bold text-white">
          {priority}
        </span>
      </div>

      {/* Main content */}
      <div className={`h-full ${isGrouped ? '' : 'min-w-0 overflow-hidden'}`}>
        {/* Move(s) content - horizontal layout for grouped legs */}
        <div className={`p-2 pt-3 h-full ${item.moves.length > 1 ? 'flex gap-2' : ''}`}>
          {item.moves.map((move, idx) => (
            <div
              key={`${move.pronumber}-${move.legnum}`}
              className={`
                ${item.moves.length > 1
                  ? 
                    hasExistingMove ? 'w-[260px] flex-shrink-0 p-1.5 bg-blue-50 dark:bg-gray-700/50 rounded border border-rule'
                    : 'w-[260px] flex-shrink-0 p-1.5 bg-grey-50 dark:bg-gray-700/50 rounded border border-rule'
                  : ''
                }
                ${item.moves.length > 1 && loadFilterMatch?.(move) ? 'ring-2 ring-sky-500 dark:ring-sky-400' : ''}
              `}
            >
              {/* PRO Number row */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onProClick?.(move.pronumber, move.legnum);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                  >
                    {move.pronumber}-{move.legnum}
                  </button>
                  <a
                    href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${move.pronumber}&searchtype=pronumber`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400"
                    title="Open in Legacy CMS"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </a>
                </div>
                <div className="flex items-center gap-0.5">
                  {/* THIS LEG IS SOMEBODY ELSE'S RIGHT NOW. Only a replan can produce
                      one, and applying it takes the work out of the named driver's day
                      — the one change on a board that costs a person something.
                      MoveRow carries the same badge, but the review board the dispatcher
                      actually applies from renders THIS card, so the warning has to be
                      here too or 79 reassignments stay invisible (test plan 65). */}
                  {move.reassigned_from && (
                    <Tooltip content={`Currently dispatched to driver ${move.reassigned_from}. Applying this plan moves it off their day.`}>
                      <span className="px-1 py-0.5 text-[0.5625rem] font-bold rounded text-white bg-amber-500 ring-1 ring-amber-600">
                        &#8592;{move.reassigned_from}
                      </span>
                    </Tooltip>
                  )}
                  {/* A STREET TURN LOOKS LIKE AN ORDINARY EMPTY LEG. Nothing on the
                      board said 5016754-3 was going to an export shipper rather than
                      back to the terminal, and the export it pairs with is not on the
                      board at all — it only appears once the reuse is performed. The
                      first time a dispatcher learnt this was the apply dialog, after
                      they had already chosen the drivers (Lee, 2026-10-22). */}
                  {move.street_turn_export && (
                    <Tooltip content={`Proposed street turn: this container goes straight to the shipper on export ${move.street_turn_export} instead of back to the terminal. Tick it in the apply dialog — un-ticked, neither the reuse nor this leg is applied.`}>
                      <span className="px-1 py-0.5 text-[0.5625rem] font-bold rounded text-white bg-violet-600 ring-1 ring-violet-700">
                        TURN&#8594;{move.street_turn_export}
                      </span>
                    </Tooltip>
                  )}
                  {/* Move Type Badge (Import/Export/Rail) */}
                  {move.movetype && (
                    <Tooltip content={move.movetype}>
                      <span
                        className={`px-1 py-0.5 text-[0.5625rem] font-medium rounded ${
                          move.movetype === 'IMPORT'
                            ? 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
                            : move.movetype === 'EXPORT'
                            ? 'text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30'
                            : move.movetype === 'RAIL'
                            ? 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30'
                            : 'text-label bg-gray-100 dark:bg-gray-700/30'
                        }`}
                      >
                        {move.movetype === 'IMPORT' ? 'Imp' : move.movetype === 'EXPORT' ? 'Exp' : move.movetype === 'RAIL' ? 'Rail' : move.movetype.substring(0, 3)}
                      </span>
                    </Tooltip>
                  )}
                  {/* Delivery Type Badge */}
                  <Tooltip content={
                    move.move_category?.includes('prepull') ? 'Pre-Pull'
                    : move.move_category === 'export-return' ? 'Export Terminate'
                    : move.is_return_move ? (move.movetype === 'EXPORT' ? 'Full Return' : 'Empty Return')
                    : move.legdrop === 'Y' ? 'Drop Delivery'
                    : 'Live Delivery'
                  }>
                    <span
                      className={`px-1 py-0.5 text-[0.5625rem] font-medium rounded ${
                        move.move_category?.includes('prepull')
                          ? 'text-cyan-700 dark:text-cyan-300 bg-cyan-100 dark:bg-cyan-900/30'
                          : move.move_category === 'export-return'
                          ? 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30'
                          : move.is_return_move
                          ? (move.movetype === 'EXPORT'
                            ? 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
                            : 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30')
                          : move.legdrop === 'Y'
                          ? 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30'
                          : 'text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30'
                      }`}
                    >
                      {move.move_category?.includes('prepull') ? 'Pre' : move.move_category === 'export-return' ? 'Term' : move.is_return_move ? (move.movetype === 'EXPORT' ? 'Full' : 'Empty') : move.legdrop === 'Y' ? 'Drop' : 'Live'}
                    </span>
                  </Tooltip>
                </div>
              </div>

              {/* Requirement Icons and Score/Debug row */}
              {(move.requires_93k || move.requires_hazmat || move.requires_tank || move.requires_daycab || move.requires_ppe ||
                (item.moves.length === 1 && (move.assignment_score !== undefined || !move.is_existing))) && (
                <div className="flex items-center gap-1 mb-1">
                  {move.requires_93k && (
                    <Tooltip content="Requires 93K Permit">
                      <span className="gw-icon text-purple-600 dark:text-purple-400" style={{ fontSize: '11px' }}>&#xE9E5;</span>
                    </Tooltip>
                  )}
                  {move.requires_hazmat && (
                    <Tooltip content="Requires Hazmat">
                      <span className="gw-icon gw-icon-hazmat text-orange-500" style={{ fontSize: '11px' }}></span>
                    </Tooltip>
                  )}
                  {move.requires_tank && (
                    <Tooltip content="Requires Tank Endorsement">
                      <span className="gw-icon gw-icon-tank text-purple-600 dark:text-purple-400" style={{ fontSize: '11px' }}></span>
                    </Tooltip>
                  )}
                  {move.requires_daycab && (
                    <Tooltip content="Requires Daycab">
                      <span className="gw-icon gw-icon-daycab text-blue-600 dark:text-blue-400" style={{ fontSize: '11px' }}></span>
                    </Tooltip>
                  )}
                  {move.requires_ppe && (
                    <Tooltip content="Requires PPE">
                      <ShieldCheckIcon className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                    </Tooltip>
                  )}
                  {/* Score - show for any move with a score */}
                  {move.assignment_score !== undefined && (
                    <span
                      className={`px-1.5 py-0.5 text-[0.625rem] font-medium rounded ${getScoreColor(move.assignment_score)}`}
                    >
                      {formatScore(move.assignment_score)}
                    </span>
                  )}
                  {/* Why icon - show for any move with a score that's not existing */}
                  {move.assignment_score !== undefined && !move.is_existing && (
                    <Tooltip
                      content={<AssignmentReasoning move={move} />}
                      placement="top"
                    >
                      <QuestionMarkCircleIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
                    </Tooltip>
                  )}
                  {/* Debug button */}
                  {!move.is_existing && item.moves.length === 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDebugClick(move);
                      }}
                      className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                      title="View details"
                    >
                      <InformationCircleIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}

              {/* Location names - condensed */}
              <div
                className={`text-gray-700 dark:text-gray-300 ${item.moves.length > 1 ? 'text-[0.625rem]' : 'text-xs'} truncate`}
                title={`${move.origin_name}${move.fallback_origin_name ? ` (${move.fallback_origin_name})` : ''} → ${move.dest_name}${move.fallback_dest_name ? ` (${move.fallback_dest_name})` : ''}`}
              >
                {move.origin_name}
                {move.fallback_origin_name && (
                  <span className="text-orange-500 dark:text-orange-400 text-[0.5625rem] ml-0.5">({move.fallback_origin_name})</span>
                )}
                {' → '}
                {move.dest_name}
                {move.fallback_dest_name && (
                  <span className="text-orange-500 dark:text-orange-400 text-[0.5625rem] ml-0.5">({move.fallback_dest_name})</span>
                )}
              </div>

              {/* Route - condensed for grouped, with miles right-aligned */}
              <div
                className="flex items-center justify-between text-[0.5625rem] text-muted"
                title={`${move.origin_city}${move.origin_state ? `, ${move.origin_state}` : ''}${move.fallback_origin_display ? ` (${move.fallback_origin_display})` : ''} → ${move.dest_city}${move.dest_state ? `, ${move.dest_state}` : ''}${move.fallback_dest_display ? ` (${move.fallback_dest_display})` : ''}`}
              >
                <span className="truncate">
                  {move.origin_city}{move.origin_state ? `, ${move.origin_state}` : ''}
                  {move.fallback_origin_display && (
                    <span className="text-orange-500 dark:text-orange-400"> ({move.fallback_origin_display})</span>
                  )}
                  {' → '}
                  {move.dest_city}{move.dest_state ? `, ${move.dest_state}` : ''}
                  {move.fallback_dest_display && (
                    <span className="text-orange-500 dark:text-orange-400"> ({move.fallback_dest_display})</span>
                  )}
                </span>
                {(move.is_return_move && move.distance_from_delivery !== undefined) ? (
                  <span className="text-yellow-600 dark:text-yellow-400 ml-1 shrink-0">
                    {move.distance_from_delivery.toFixed(1)} mi
                  </span>
                ) : move.leg_miles !== undefined && move.leg_miles > 0 && (
                  <span className="text-gray-400 ml-1 shrink-0">{move.leg_miles.toFixed(1)} mi</span>
                )}
              </div>

              {/* Chassis info */}
              {(move.chassis_type || move.chassis_size) && (
                <div className="text-[0.5625rem] text-gray-400 dark:text-gray-500">
                  {move.chassis_size}{move.chassis_size && move.chassis_type && '/'}{move.chassis_type}
                </div>
              )}

              {/* Time info */}
              <div className="text-[0.625rem] text-muted mt-0.5">
                <div className="flex items-center justify-between">
                  <span>
                    {formatDate(move.dest_appt_date)}
                    {move.dest_appt_time1 && (
                      <span className="ml-1 text-gray-600 dark:text-gray-300">
                        @ {move.dest_appt_time1}{move.dest_appt_time2 && ` - ${move.dest_appt_time2}`}
                      </span>
                    )}
                  </span>
                  <div className="text-right">
                    {move.estimated_start && (
                      <span className="text-label">
                        <span className="text-muted">Drive </span>
                        {formatTime(move.estimated_start)}{move.estimated_end && ` - ${formatTime(move.estimated_end)}`}
                      </span>
                    )}
                    {move.travel_time_from_previous > 0 && (
                      <span className="ml-1 text-orange-500 dark:text-orange-400">
                        +{formatDuration(move.travel_time_from_previous)}
                      </span>
                  )}
                </div>
                </div>
                <DestinationStopLine move={move} />
                {move.origin_appt_time && (
                  <div className="text-[0.5625rem] text-gray-400 dark:text-gray-500">
                    Pickup: {move.origin_appt_time}{move.origin_appt_time2 && ` - ${move.origin_appt_time2}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Quick Assign button - bottom right */}
      {onQuickAssign && !isApplied && !hasExistingMove && (
        <div className="absolute -bottom-2 -right-2 z-10">
          <button
            ref={buttonRef}
            onClick={(e) => {
              e.stopPropagation();
              if (!showQuickAssign && buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setPopupPos({ top: rect.top, left: rect.right });
              }
              setShowQuickAssign(!showQuickAssign);
            }}
            className="w-6 h-6 rounded-full bg-gray-900 dark:bg-gray-700 text-white flex items-center justify-center shadow-sm opacity-25 hover:opacity-100 transition-opacity"
            title="Quick assign to driver"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
              <path d="M17.244,4A2,2,0,0,0,13.83,5.414V7H9a9.01,9.01,0,0,0-9,9v7a1,1,0,0,0,2,0,6.006,6.006,0,0,1,6-6h5.83v1.586A2,2,0,0,0,17.244,20l5.879-5.879a3,3,0,0,0,0-4.242Z"/>
            </svg>
          </button>
        </div>
      )}

      {/* Quick assign popup - rendered via portal to escape overflow/z-index */}
      {showQuickAssign && popupPos && createPortal(
        <div
          ref={quickAssignRef}
          className="fixed bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-2 w-52"
          style={{ top: popupPos.top, left: popupPos.left + 8, zIndex: 9999, transform: 'translateY(-100%)' }}
        >
          {/* Suggested drivers from alternatives */}
          {(() => {
            const alts = item.moves[0]?.alternatives;
            if (!alts || alts.length === 0) return null;
            return (
              <div className="mb-2">
                <label className="block text-xs text-muted mb-1">
                  Suggestions
                </label>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {alts.slice(0, 5).map((alt) => (
                    <button
                      key={alt.driver}
                      onClick={() => {
                        if (onQuickAssign) {
                          onQuickAssign(item, alt.driver);
                          setShowQuickAssign(false);
                          setQuickAssignInput('');
                        }
                      }}
                      className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-transparent hover:border-blue-200 dark:hover:border-blue-800 transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-heading truncate">
                          {alt.driver_name || alt.driver}<MuleBadge isMule={alt.isMule} />
                        </span>
                        <span className={`ml-1 px-1.5 py-0.5 rounded text-[0.625rem] font-medium shrink-0 ${getScoreColor(alt.score)}`}>
                          {formatScore(alt.score)}
                        </span>
                      </div>
                      <div className="text-[0.625rem] text-gray-400 dark:text-gray-500 mt-0.5">
                        {alt.driver} · {alt.distance_miles?.toFixed(1)} mi
                      </div>
                    </button>
                  ))}
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2" />
              </div>
            );
          })()}
          <label className="block text-xs text-muted mb-1">
            Unit Number
          </label>
          <input
            ref={inputRef}
            type="text"
            value={quickAssignInput}
            onChange={(e) => setQuickAssignInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleQuickAssignSubmit();
              } else if (e.key === 'Escape') {
                setShowQuickAssign(false);
                setQuickAssignInput('');
              }
            }}
            placeholder="e.g., 1234"
            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={handleQuickAssignSubmit}
            disabled={!quickAssignInput.trim()}
            className="mt-2 w-full px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded transition-colors"
          >
            Assign
          </button>
          {/* The staging drawer's drop zone is only as tall as its content, so a card dragged
              from a scrolled-down lane never lands in it. This button is the reliable path
              (John, 2026-10-28). */}
          {onSendToStaging && (
            <button
              onClick={() => {
                onSendToStaging(item);
                setShowQuickAssign(false);
                setQuickAssignInput('');
              }}
              className="mt-2 w-full px-2 py-1 text-xs font-medium text-heading bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded transition-colors"
            >
              Send To Staging
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

/** Check if a driver's plan has actionable changes (new moves or rearranged order). */
const driverHasChanges = (
  driver: LocalDriverPlan,
  originalPlan: DriverDayPlan | undefined,
  appliedMoves: Set<string>
): boolean => {
  // Has new/unapplied moves (exclude existing moves — those were already dispatched before the preplan)
  const hasUnapplied = driver.items.some(item =>
    item.moves.some(m =>
      !m.is_existing &&
      !appliedMoves.has(`${m.pronumber}-${m.legnum}`) &&
      !m.was_applied_by_preplan
    )
  );
  if (hasUnapplied) return true;

  // Check if moves were rearranged vs original plan
  if (!originalPlan) return true; // No original to compare, assume changed

  const originalMoveKeys = originalPlan.moves.map(m => `${m.pronumber}-${m.legnum}`);
  const currentMoveKeys = driver.items.flatMap(item => item.moves.map(m => `${m.pronumber}-${m.legnum}`));

  if (originalMoveKeys.length !== currentMoveKeys.length) return true;
  for (let i = 0; i < originalMoveKeys.length; i++) {
    if (originalMoveKeys[i] !== currentMoveKeys[i]) return true;
  }
  return false;
};

// ============================================================================
// Driver Row Component (Vertical stacked layout)
// ============================================================================

interface DriverRowProps {
  driver: LocalDriverPlan;
  items: DraggableItem[];
  index: number; // 1-based index for display
  onDragStart: (e: React.DragEvent, item: DraggableItem, sourceDriver: string | null) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetDriver: string, targetIndex: number) => void;
  onDebugClick: (move: PlannedMove) => void;
  onProClick?: (pronumber: number, legnum: number) => void;
  originalDriverPlan?: DriverDayPlan; // For stats
  isSelected?: boolean;
  selectionDisabled?: boolean; // Disable checkbox when driver has no unapplied moves
  onSelectionChange?: (unitnumber: string, selected: boolean) => void;
  appliedMoves?: Set<string>; // Track which moves have been applied
  onQuickAssign?: (item: DraggableItem, targetUnitnumber: string, sourceDriver: string | null) => void;
  onSendToStaging?: (item: DraggableItem, sourceDriver: string | null) => void;
  // Multi-select for individual moves
  selectedMoves?: Set<string>; // Key: "driverUnitnumber:pronumber-legnum"
  onMoveSelectionChange?: (driverUnitnumber: string, item: DraggableItem, selected: boolean) => void;
  loadFilterMatch?: (move: PlannedMove) => boolean;
}

const DriverRow: React.FC<DriverRowProps> = ({
  driver,
  items,
  index,
  onDragStart,
  onDragOver,
  onDrop,
  onDebugClick,
  onProClick,
  originalDriverPlan,
  isSelected = false,
  selectionDisabled = false,
  onSelectionChange,
  appliedMoves = new Set(),
  onQuickAssign,
  onSendToStaging,
  selectedMoves,
  onMoveSelectionChange,
  loadFilterMatch,
}) => {
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
    onDragOver(e);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    onDrop(e, driver.unitnumber, index);
  };

  const avgScore = originalDriverPlan?.average_assignment_score;

  // Check if local items match the original plan (no drag-drop changes)
  const hasLocalChanges = useMemo(() => {
    if (!originalDriverPlan) return true; // No original to compare, assume changed

    // Compare move counts
    const originalMoveIds = new Set(
      originalDriverPlan.moves.map(m => `${m.pronumber}-${m.legnum}`)
    );
    const currentMoveIds = new Set(
      items.flatMap(item => item.moves.map(m => `${m.pronumber}-${m.legnum}`))
    );

    if (originalMoveIds.size !== currentMoveIds.size) return true;
    for (const id of originalMoveIds) {
      if (!currentMoveIds.has(id)) return true;
    }
    return false;
  }, [items, originalDriverPlan]);

  // Calculate total estimated pay
  // Use backend's precomputed value when no local changes (includes existing moves pay)
  // Recalculate from items when user has made drag-drop changes
  const totalEstimatedPay = useMemo(() => {
    // If no local changes and we have the original plan with day pay, use it
    // This correctly includes existing moves that don't have driver_context
    if (!hasLocalChanges && originalDriverPlan?.estimated_day_pay !== undefined) {
      return originalDriverPlan.estimated_day_pay;
    }

    // Calculate from items (used when moves have been dragged around)
    let total = 0;
    const processedGroups = new Set<string>();

    for (const item of items) {
      for (const move of item.moves) {
        // For grouped moves, use group_total_pay only once per group
        if (move.dispatch_group_id && move.group_total_pay) {
          if (!processedGroups.has(move.dispatch_group_id)) {
            processedGroups.add(move.dispatch_group_id);
            total += move.group_total_pay;
          }
        } else if (move.driver_context?.estimated_leg_pay) {
          // Individual leg pay from new moves
          total += move.driver_context.estimated_leg_pay;
        }
        // Note: existing moves without driver_context won't be counted here
        // After drag-drop, pay estimates may be incomplete for existing moves
      }
    }
    return total;
  }, [items, hasLocalChanges, originalDriverPlan?.estimated_day_pay]);

  // HOW MUCH OF THIS DAY IS TAKEN OFF SOMEBODY ELSE. The per-move badge is only
  // readable with the row expanded, and a replan spreads its reassignments thinly —
  // test plan 65 put 79 of them across 63 drivers out of 306. Without a count on the
  // header a dispatcher has to open every driver to find the handful that cost
  // someone their work.
  const reassignedCount = useMemo(
    () => items.reduce(
      (n, item) => n + item.moves.filter(m => m.reassigned_from).length, 0),
    [items]);

  // THE OTHER THING AN APPLY DOES TO THIS DAY. A street-turn move looks like an
  // ordinary empty leg on the board — the only clue it reuses a container was in the
  // apply dialog, by which point the dispatcher has already chosen the drivers.
  const streetTurnCount = useMemo(
    () => items.reduce(
      (n, item) => n + item.moves.filter(m => m.street_turn_export).length, 0),
    [items]);

  return (
    <div className="bg-panel rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Row Header */}
      <div
        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-750 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Selection Checkbox */}
          {onSelectionChange && items.length > 0 && (
            <input
              type="checkbox"
              checked={isSelected}
              disabled={selectionDisabled}
              onChange={(e) => {
                e.stopPropagation();
                onSelectionChange(driver.unitnumber, e.target.checked);
              }}
              className={`h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700 ${selectionDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={(e) => e.stopPropagation()}
              title={selectionDisabled ? 'No unapplied changes to apply' : undefined}
            />
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex items-center gap-3"
          >
            {isCollapsed ? (
              <ChevronRightIcon className="h-4 w-4 text-muted" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-muted" />
            )}
            <div className="flex items-center gap-2">
              <div className="text-left">
                <span className="text-sm font-medium text-muted mr-2">
                  {index}.
                </span>
                <span className="font-medium text-heading">
                  {driver.driver_name}<MuleBadge isMule={driver.isMule} />
                </span>
                <span className="ml-2 text-sm text-muted">
                  ({driver.unitnumber})
                </span>
              </div>
            {/* Qualification Icons */}
            <div className="flex items-center gap-1 ml-2">
              {driver.has_93k && (
                <Tooltip content="93K Permit">
                  <span className="gw-icon text-purple-600 dark:text-purple-400" style={{ fontSize: '14px' }}>&#xE9E5;</span>
                </Tooltip>
              )}
              {driver.has_hazmat && (
                <Tooltip content="Hazmat">
                  <span className="gw-icon gw-icon-hazmat text-orange-500" style={{ fontSize: '14px' }}></span>
                </Tooltip>
              )}
              {driver.has_tank && (
                <Tooltip content="Tank">
                  <span className="gw-icon gw-icon-tank text-purple-600 dark:text-purple-400" style={{ fontSize: '14px' }}></span>
                </Tooltip>
              )}
              {driver.is_light_truck && (
                <Tooltip content="Light Truck (<15K lbs)">
                  <svg viewBox="0 0 32 20" width="18" height="11" fill="currentColor" className="text-green-600 dark:text-green-400">
                    <rect x="1" y="1" width="30" height="18" rx="3" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
                    <text x="16" y="14" textAnchor="middle" fontFamily="var(--app-font-family)" fontSize="11" fontWeight="bold">&lt;15K</text>
                  </svg>
                </Tooltip>
              )}
              {driver.is_daycab ? (
                <Tooltip content="Daycab">
                  <span className="gw-icon gw-icon-daycab text-blue-600 dark:text-blue-400" style={{ fontSize: '14px' }}></span>
                </Tooltip>
              ) : (
                <Tooltip content="Sleeper">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="text-indigo-600 dark:text-indigo-400">
                    <path d="M2,14H24v8H22V19H2v3H0V2H2Zm3.5-2A2.5,2.5,0,1,0,3,9.5,2.5,2.5,0,0,0,5.5,12ZM21,6H12A3,3,0,0,0,9,9v3H24V9A3,3,0,0,0,21,6Z"/>
                  </svg>
                </Tooltip>
              )}
              {driver.has_ppe && (
                <Tooltip content="PPE Equipped">
                  <ShieldCheckIcon className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
                </Tooltip>
              )}
            </div>
          </div>
          </button>
        </div>
        <div className="flex items-center gap-4">
          {totalEstimatedPay > 0 && (
            <div className="text-sm font-medium text-green-600 dark:text-green-400">
              ${totalEstimatedPay.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
          )}
          {streetTurnCount > 0 && (
            <Tooltip content={`${streetTurnCount} of this driver's moves ${streetTurnCount === 1 ? 'is a proposed street turn' : 'are proposed street turns'}: the import's container goes straight to an export shipper instead of back to the terminal. Each one must be ticked in the apply dialog or neither the reuse nor its leg is applied.`}>
              <span className="px-2 py-0.5 rounded text-xs font-semibold text-white bg-violet-600 ring-1 ring-violet-700 cursor-help">
                {streetTurnCount} street turn{streetTurnCount === 1 ? '' : 's'}
              </span>
            </Tooltip>
          )}
          {reassignedCount > 0 && (
            <Tooltip content={`${reassignedCount} ${reassignedCount === 1 ? 'leg is' : 'legs are'} currently dispatched to other drivers. Applying this plan moves ${reassignedCount === 1 ? 'it' : 'them'} off their days.`}>
              <span className="px-2 py-0.5 rounded text-xs font-semibold text-white bg-amber-500 ring-1 ring-amber-600 cursor-help">
                {reassignedCount} reassigned
              </span>
            </Tooltip>
          )}
          <div className="text-sm text-muted">
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </div>
          {avgScore !== null && avgScore !== undefined && (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getScoreColor(avgScore)}`}>
              Avg: {formatScore(avgScore)}
            </span>
          )}
        </div>
      </div>

      {/* Items - Horizontal scrolling list */}
      {!isCollapsed && (
        <div
          className="p-3 overflow-x-auto"
          onDragOver={(e) => handleDragOver(e, items.length)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, items.length)}
        >
          <div className="flex gap-3 flex-wrap">
            {items.map((item, idx) => {
              // Width: single cards = fixed 280px, grouped cards = auto (fit content)
              const isGrouped = item.moves.length > 1;
              return (
                <div key={item.id} className="flex items-stretch">
                  {/* Drop indicator - vertical line */}
                  {dragOverIndex === idx && (
                    <div className="w-1 bg-blue-400 dark:bg-blue-500 rounded-full mr-2" />
                  )}
                  <div
                    className={isGrouped ? 'flex-shrink-0' : 'flex-shrink-0 w-[280px]'}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOverIndex(idx);
                    }}
                    onDrop={(e) => {
                      e.stopPropagation();
                      handleDrop(e, idx);
                    }}
                  >
                    <DraggableMoveCard
                      item={item}
                      driverUnitnumber={driver.unitnumber}
                      priority={idx + 1}
                      onDragStart={onDragStart}
                      onDebugClick={onDebugClick}
                      onProClick={onProClick}
                      appliedMoves={appliedMoves}
                      onQuickAssign={onQuickAssign ? (itm, target) => onQuickAssign(itm, target, driver.unitnumber) : undefined}
                      onSendToStaging={onSendToStaging ? (itm) => onSendToStaging(itm, driver.unitnumber) : undefined}
                      preference={locationPreferenceTone(item.moves, driver)}
                      loadFilterMatch={loadFilterMatch}
                      isSelected={selectedMoves ? item.moves.every(m => selectedMoves.has(`${driver.unitnumber}:${m.pronumber}-${m.legnum}`)) : false}
                      onSelectionChange={onMoveSelectionChange ? (selected) => onMoveSelectionChange(driver.unitnumber, item, selected) : undefined}
                    />
                  </div>
                </div>
              );
            })}
            {/* Drop zone at the end */}
            {dragOverIndex === items.length && items.length > 0 && (
              <div className="w-1 bg-blue-400 dark:bg-blue-500 rounded-full" />
            )}
            {items.length === 0 && (
              <div className="flex-1 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg py-4 px-4">
                <div className="text-center text-gray-400 dark:text-gray-500 text-sm italic mb-2">
                  Drop moves here
                </div>
                {driver.near_misses && driver.near_misses.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-xs font-medium text-muted mb-1.5">
                      Considered for {driver.near_misses.length} move{driver.near_misses.length !== 1 ? 's' : ''} — not selected:
                    </div>
                    <div className="space-y-1">
                      {driver.near_misses.map((miss) => (
                        <div
                          key={miss.move_key}
                          className="text-xs text-muted bg-gray-50 dark:bg-gray-750 rounded px-2 py-1.5"
                        >
                          <span className="font-medium text-label">
                            PRO {miss.move_key}
                          </span>
                          <span className="mx-1">·</span>
                          <span>Scored {Math.round(miss.driver_score * 100)}%</span>
                          <span className="mx-1">·</span>
                          <span className="text-gray-400 dark:text-gray-500">{miss.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Unassigned Row Component
// ============================================================================

interface UnassignedRowProps {
  items: DraggableItem[];
  onDragStart: (e: React.DragEvent, item: DraggableItem, sourceDriver: string | null) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetDriver: string | null, targetIndex: number) => void;
  onDebugClick: (move: PlannedMove) => void;
  onProClick?: (pronumber: number, legnum: number) => void;
  onQuickAssign?: (item: DraggableItem, targetUnitnumber: string, sourceDriver: string | null) => void;
  onSendToStaging?: (item: DraggableItem, sourceDriver: string | null) => void;
}

const UnassignedRow: React.FC<UnassignedRowProps> = ({
  items,
  onDragStart,
  onDragOver,
  onDrop,
  onDebugClick,
  onProClick,
  onQuickAssign,
  onSendToStaging,
}) => {
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
    onDragOver(e);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    onDrop(e, null, index);
  };

  if (items.length === 0) return null;

  return (
    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg border-2 border-dashed border-orange-300 dark:border-orange-700 overflow-hidden">
      {/* Row Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full px-4 py-3 bg-orange-100 dark:bg-orange-900/30 flex items-center justify-between hover:bg-orange-200 dark:hover:bg-orange-900/40 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isCollapsed ? (
            <ChevronRightIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />
          )}
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            <span className="font-medium text-orange-900 dark:text-orange-100">
              Unassigned
            </span>
          </div>
        </div>
        <div className="text-sm text-orange-600 dark:text-orange-400">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </div>
      </button>

      {/* Items - Horizontal scrolling list */}
      {!isCollapsed && (
        <div
          className="p-3 overflow-x-auto"
          onDragOver={(e) => handleDragOver(e, items.length)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, items.length)}
        >
          <div className="flex gap-3 flex-wrap">
            {items.map((item, idx) => (
              <div key={item.id} className="flex items-stretch">
                {dragOverIndex === idx && (
                  <div className="w-1 bg-orange-400 dark:bg-orange-500 rounded-full mr-2" />
                )}
                <div
                  className="flex-shrink-0 w-96"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverIndex(idx);
                  }}
                  onDrop={(e) => {
                    e.stopPropagation();
                    handleDrop(e, idx);
                  }}
                >
                  <DraggableMoveCard
                    item={item}
                    driverUnitnumber={null}
                    priority={idx + 1}
                    onDragStart={onDragStart}
                    onDebugClick={onDebugClick}
                    onProClick={onProClick}
                    onQuickAssign={onQuickAssign ? (itm, target) => onQuickAssign(itm, target, null) : undefined}
                    onSendToStaging={onSendToStaging ? (itm) => onSendToStaging(itm, null) : undefined}
                  />
                </div>
              </div>
            ))}
            {dragOverIndex === items.length && items.length > 0 && (
              <div className="w-1 bg-orange-400 dark:bg-orange-500 rounded-full" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface DriverPlanCardProps {
  driverPlan: DriverDayPlan;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onMoveDebugClick: (move: PlannedMove) => void;
  onProClick?: (pronumber: number, legnum: number) => void;
}

const DriverPlanCard: React.FC<DriverPlanCardProps> = ({
  driverPlan,
  isCollapsed,
  onToggleCollapse,
  onMoveDebugClick,
  onProClick,
}) => {
  const avgScore = driverPlan.average_assignment_score;

  return (
    <div className="bg-panel rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggleCollapse}
        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-750 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isCollapsed ? (
            <ChevronRightIcon className="h-4 w-4 text-muted" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-muted" />
          )}
          <div className="text-left">
            <span className="font-medium text-heading">
              {driverPlan.driver_name}<MuleBadge isMule={driverPlan.isMule} />
            </span>
            <span className="ml-2 text-sm text-muted">
              ({driverPlan.unitnumber})
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Move Counts */}
          <div className="text-sm text-muted">
            {driverPlan.new_moves} new
            {driverPlan.existing_moves > 0 && (
              <span className="ml-1">+ {driverPlan.existing_moves} existing</span>
            )}
          </div>

          {/* Day Pay */}
          {driverPlan.estimated_day_pay !== undefined && driverPlan.estimated_day_pay > 0 && (
            <Tooltip
              content={
                <div className="text-xs space-y-1">
                  <div className="font-semibold border-b border-gray-600 pb-1 mb-1">Day Pay Breakdown</div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-400">Existing moves:</span>
                    <span>{formatCurrency(driverPlan.existing_moves_pay)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-400">New moves:</span>
                    <span>{formatCurrency(driverPlan.new_moves_pay)}</span>
                  </div>
                  <div className="flex justify-between gap-4 border-t border-gray-600 pt-1 mt-1 font-medium">
                    <span>Total:</span>
                    <span>{formatCurrency(driverPlan.estimated_day_pay)}</span>
                  </div>
                </div>
              }
              placement="left"
            >
              <span className="px-2 py-0.5 rounded text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 cursor-help">
                {formatCurrency(driverPlan.estimated_day_pay)}
              </span>
            </Tooltip>
          )}

          {/* Average Score */}
          {avgScore !== null && avgScore !== undefined && (
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium ${getScoreColor(avgScore)}`}
            >
              Avg: {formatScore(avgScore)}
            </span>
          )}
        </div>
      </button>

      {/* Moves List */}
      {!isCollapsed && (
        <div>
          {driverPlan.moves.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted italic">
              No moves assigned
            </div>
          ) : (
            driverPlan.moves.map((move) => (
              <MoveRow
                key={`${move.pronumber}-${move.legnum}`}
                move={move}
                onDebugClick={() => onMoveDebugClick(move)}
                onProClick={onProClick}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

interface UnassignedMoveSectionProps {
  unassignedMoves: UnassignedMove[];
  onProClick?: (pronumber: number, legnum: number) => void;
  /** The whole list's size, when a quick filter has narrowed `unassignedMoves`. */
  totalCount?: number;
}

const UnassignedMoveSection: React.FC<UnassignedMoveSectionProps> = ({ unassignedMoves, onProClick, totalCount }) => {
  if (unassignedMoves.length === 0) return null;

  return (
    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 overflow-hidden">
      <div className="px-4 py-3 bg-orange-100 dark:bg-orange-900/30 flex items-center gap-2">
        <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        <span className="font-medium text-orange-900 dark:text-orange-100">
          Unassigned Moves ({totalCount !== undefined ? `${unassignedMoves.length} of ${totalCount}` : unassignedMoves.length})
        </span>
        <span className="text-orange-700 dark:text-orange-300">
          - These will need to be manually assigned via the Dispatch Planner
        </span>
      </div>
      <div className="divide-y divide-orange-200 dark:divide-orange-800">
        {unassignedMoves.map((move) => (
          <div
            key={`${move.pronumber}-${move.legnum}`}
            className="px-4 py-3"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onProClick?.(move.pronumber, move.legnum)}
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                  >
                    PRO {move.pronumber}-{move.legnum}
                  </button>
                  {move.move_category === 'export-return' && (
                    <span className="px-1.5 py-0.5 text-[0.625rem] font-medium rounded text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30">
                      Term
                    </span>
                  )}
                  <a
                    href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${move.pronumber}&searchtype=pronumber`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400"
                    title="Open in Legacy CMS"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  </a>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {move.origin_name} → {move.dest_name}
                </div>
                <div className="text-xs text-muted">
                  {move.origin_city} → {move.dest_city}
                </div>
              </div>
              <div className="text-right text-sm">
                <div className="text-muted">
                  {formatDate(move.dest_appt_date)}
                  {move.dest_appt_time1 && (
                    <span className="ml-1">
                      @ {move.dest_appt_time1}{move.dest_appt_time2 && ` - ${move.dest_appt_time2}`}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {move.total_drivers_checked} drivers checked
                </div>
              </div>
            </div>

            {/* Reasons */}
            {move.reasons.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">
                  Reasons:
                </div>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                  {move.reasons.map((reason, idx) => (
                    <li key={idx}>• {reason.description}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Suggestions */}
            {move.suggestions.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                  Suggestions:
                </div>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                  {move.suggestions.map((suggestion, idx) => (
                    <li key={idx}>• {suggestion}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

interface MoveDebugDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  move: PlannedMove | null;
  driverPlan: DriverDayPlan | null;
}

const MoveDebugDrawer: React.FC<MoveDebugDrawerProps> = ({
  isOpen,
  onClose,
  move,
  driverPlan,
}) => {
  if (!move || !driverPlan) return null;

  // Calculate total rejected from either rejection_summary or rejected_drivers
  const totalRejected = move.rejected_drivers
    ? (move.rejected_drivers.not_qualified?.length || 0) +
      (move.rejected_drivers.schedule_conflict?.length || 0) +
      (move.rejected_drivers.below_threshold?.length || 0) +
      (move.rejected_drivers.at_capacity?.length || 0) +
      (move.rejected_drivers.insufficient_hos?.length || 0) +
      (move.rejected_drivers.start_time_conflict?.length || 0)
    : move.rejection_summary
    ? (move.rejection_summary.not_qualified || 0) +
      (move.rejection_summary.schedule_conflict || 0) +
      (move.rejection_summary.below_threshold || 0) +
      (move.rejection_summary.at_capacity || 0) +
      (move.rejection_summary.insufficient_hos || 0) +
      (move.rejection_summary.start_time_conflict || 0)
    : 0;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Move Details: PRO ${move.pronumber}-${move.legnum}`}
      position="right"
      maxWidth="lg"
    >
      <div className="space-y-6 p-4">
        {/* Why This Driver Section */}
        <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
          <h4 className="font-medium text-green-900 dark:text-green-100 mb-3 flex items-center gap-2">
            <CheckIcon className="h-5 w-5" />
            #1 - {driverPlan.driver_name} ({driverPlan.unitnumber})<MuleBadge isMule={driverPlan.isMule} />
          </h4>
          <div className="space-y-3">
            {/* Overall Score */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Overall Score:</span>
              <span
                className={`px-3 py-1 rounded text-sm font-medium ${
                  move.assignment_score ? getScoreColor(move.assignment_score) : ''
                }`}
              >
                {formatScore(move.assignment_score)}
              </span>
            </div>

            {/* Score Breakdown */}
            {move.score_components && (
              <div className="border-t border-green-200 dark:border-green-800 pt-3 space-y-2">
                {/* Score formula explanation */}
                <div className="text-xs text-muted mb-2 bg-green-100 dark:bg-green-900/50 p-2 rounded">
                  <strong>Score Formula:</strong>
                  {move.driver_context?.weights_used ? (
                    <span className="block mt-1">
                      (Dist × {(move.driver_context.weights_used.distance * 100).toFixed(0)}%) +
                      (Work × {(move.driver_context.weights_used.workload * 100).toFixed(0)}%) +
                      (Prof × {(move.driver_context.weights_used.profitability * 100).toFixed(0)}%) +
                      (Perf × {((move.driver_context.weights_used.driver_score || 0.20) * 100).toFixed(0)}%)
                    </span>
                  ) : (
                    <span className="block mt-1">
                      (Dist × 30%) + (Work × 25%) + (Prof × 25%) + (Perf × 20%)
                    </span>
                  )}
                </div>

                {/* Individual scores with weights */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-panel p-2 rounded border border-green-200 dark:border-green-700">
                    <div className="text-xs text-muted">Distance</div>
                    <div className="text-lg font-semibold text-heading">
                      {formatScore(move.score_components.distance)}
                    </div>
                    {move.driver_context?.distance_miles !== undefined && (
                      <div className="text-xs text-gray-400">{move.driver_context.distance_miles.toFixed(1)} mi away</div>
                    )}
                  </div>
                  <div className="bg-panel p-2 rounded border border-green-200 dark:border-green-700">
                    <div className="text-xs text-muted">Workload</div>
                    <div className="text-lg font-semibold text-heading">
                      {formatScore(move.score_components.workload)}
                    </div>
                    {move.driver_context?.remaining_dispatches !== undefined && (
                      <div className="text-xs text-gray-400">{move.driver_context.remaining_dispatches} slots left</div>
                    )}
                  </div>
                  <div className="bg-panel p-2 rounded border border-green-200 dark:border-green-700">
                    <div className="text-xs text-muted">Profitability</div>
                    <div className="text-lg font-semibold text-heading">
                      {formatScore(move.score_components.profitability)}
                    </div>
                    {move.driver_context?.current_pay !== undefined && move.driver_context?.daily_goal !== undefined && (
                      <div className="text-xs text-gray-400">
                        {formatCurrency(move.driver_context.current_pay)} / {formatCurrency(move.driver_context.daily_goal)}
                        {' '}({((move.driver_context.current_pay / move.driver_context.daily_goal) * 100).toFixed(0)}%)
                      </div>
                    )}
                  </div>
                  <div className="bg-panel p-2 rounded border border-green-200 dark:border-green-700">
                    <div className="text-xs text-muted">Driver Perf</div>
                    <div className="text-lg font-semibold text-heading">
                      {formatScore(move.score_components.driver_score ?? 0.5)}
                    </div>
                    <div className="text-xs text-gray-400">
                      {move.score_components.driver_score !== undefined ? 'from API' : 'default'}
                    </div>
                  </div>
                </div>

                {/* Calculated total */}
                {move.driver_context?.weights_used && (
                  <div className="border-t border-green-200 dark:border-green-800 pt-2 mt-2">
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      <strong>Calculation:</strong>
                      <div className="mt-1 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        ({formatScore(move.score_components.distance)} × {move.driver_context.weights_used.distance.toFixed(2)}) +
                        ({formatScore(move.score_components.workload)} × {move.driver_context.weights_used.workload.toFixed(2)}) +
                        ({formatScore(move.score_components.profitability)} × {move.driver_context.weights_used.profitability.toFixed(2)}) +
                        ({formatScore(move.score_components.driver_score ?? 0.5)} × {(move.driver_context.weights_used.driver_score || 0.20).toFixed(2)})
                        {scoreBoosts(move.score_components).map(boost => <span key={boost.label}> × {boost.factor} ({boost.label.toLowerCase()})</span>)}
                        <span className="block mt-1 text-green-700 dark:text-green-400 font-bold">
                          = {formatScore(weightedScore(move.score_components, move.driver_context.weights_used))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Driver Context */}
            {move.driver_context && (
              <div className="border-t border-green-200 dark:border-green-800 pt-3 space-y-2">
                <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                  Driver Context
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Distance to Pickup:</span>
                  <span className="text-heading">
                    {move.driver_context.distance_miles?.toFixed(1) || '-'} mi
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Remaining Dispatches:</span>
                  <span className="text-heading">
                    {move.driver_context.remaining_dispatches}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Current Pay:</span>
                  <span className="text-heading">
                    {formatCurrency(move.driver_context.current_pay)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Daily Goal:</span>
                  <span className="text-heading">
                    {formatCurrency(move.driver_context.daily_goal)}
                  </span>
                </div>
                {move.driver_context.estimated_leg_pay !== undefined && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">Est. Leg Pay:</span>
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      {formatCurrency(move.driver_context.estimated_leg_pay)}
                    </span>
                  </div>
                )}
                {move.driver_context.current_pay !== undefined &&
                 move.driver_context.daily_goal !== undefined &&
                 move.driver_context.estimated_leg_pay !== undefined && (
                  <div className="flex justify-between text-sm border-t border-green-200 dark:border-green-700 pt-2 mt-2">
                    <span className="text-muted">Projected Pay After:</span>
                    <span className="text-heading font-medium">
                      {formatCurrency(move.driver_context.current_pay + move.driver_context.estimated_leg_pay)}
                      <span className="text-xs text-gray-400 ml-1">
                        ({(((move.driver_context.current_pay + move.driver_context.estimated_leg_pay) / move.driver_context.daily_goal) * 100).toFixed(0)}% of goal)
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Alternatives Section - Enhanced */}
        {move.alternatives && move.alternatives.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
              <UsersIcon className="h-5 w-5" />
              Other Drivers Checked ({move.alternatives.length})
            </h4>
            <div className="space-y-3">
              {move.alternatives.map((alt, idx) => (
                <div
                  key={alt.driver}
                  className="bg-panel rounded-lg p-3 border border-blue-200 dark:border-blue-800"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        #{idx + 2}
                      </span>
                      <span className="ml-2 text-sm font-medium text-heading">
                        {alt.driver_name || alt.driver}<MuleBadge isMule={alt.isMule} />
                      </span>
                      {alt.driver_name && (
                        <span className="ml-1 text-xs text-gray-500">({alt.driver})</span>
                      )}
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${getScoreColor(alt.score)}`}
                    >
                      {formatScore(alt.score)}
                    </span>
                  </div>
                  {alt.not_selected_reason && (
                    <div className="mb-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-1.5 rounded">
                      {alt.not_selected_reason}
                    </div>
                  )}
                  {alt.score_components && (
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div className="text-center">
                        <div className="text-gray-400">Dist</div>
                        <div className="font-medium">{formatScore(alt.score_components.distance)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-400">Work</div>
                        <div className="font-medium">{formatScore(alt.score_components.workload)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-400">Prof</div>
                        <div className="font-medium">{formatScore(alt.score_components.profitability)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-400">Perf</div>
                        <div className="font-medium">{formatScore(alt.score_components.driver_score ?? 0.5)}</div>
                      </div>
                    </div>
                  )}
                  {(alt.remaining_dispatches !== undefined || alt.current_pay !== undefined) && (
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex gap-4 text-xs text-gray-500">
                      {alt.remaining_dispatches !== undefined && (
                        <span>Remaining: {alt.remaining_dispatches}</span>
                      )}
                      {alt.current_pay !== undefined && alt.daily_goal !== undefined && (
                        <span>Pay: {formatCurrency(alt.current_pay)} / {formatCurrency(alt.daily_goal)}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rejected Drivers Section */}
        {totalRejected > 0 && (
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
            <h4 className="font-medium text-orange-900 dark:text-orange-100 mb-3 flex items-center gap-2">
              <ExclamationTriangleIcon className="h-5 w-5" />
              Rejected Drivers ({totalRejected})
            </h4>
            <div className="space-y-4">
              <RejectionCategory title="Not Qualified" reasons={move.rejected_drivers?.not_qualified} />
              <RejectionCategory title="At Capacity" reasons={move.rejected_drivers?.at_capacity} />
              <RejectionCategory title="Schedule Conflicts" reasons={move.rejected_drivers?.schedule_conflict} />
              <RejectionCategory title="Below Score Threshold" reasons={move.rejected_drivers?.below_threshold} />
              <RejectionCategory title="Insufficient HOS" reasons={move.rejected_drivers?.insufficient_hos} />
              <RejectionCategory title="Start Time Conflicts" reasons={move.rejected_drivers?.start_time_conflict} />
            </div>
          </div>
        )}

        {/* Move Details Section */}
        <div className="bg-gray-50 dark:bg-gray-750 rounded-lg p-4">
          <h4 className="font-medium text-heading mb-3">Move Details</h4>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-muted">Origin</dt>
              <dd className="text-heading">{move.origin_name}</dd>
              <dd className="text-xs text-gray-500">{move.origin_city}, {move.origin_state}</dd>
            </div>
            <div>
              <dt className="text-muted">Destination</dt>
              <dd className="text-heading">{move.dest_name}</dd>
              <dd className="text-xs text-gray-500">{move.dest_city}, {move.dest_state}</dd>
            </div>
            <div>
              <dt className="text-muted">Delivery Appt</dt>
              <dd className="text-heading">{formatDate(move.dest_appt_date)}</dd>
              {move.dest_appt_time1 && (
                <dd className="text-xs text-gray-500">
                  {move.dest_appt_time1}{move.dest_appt_time2 && ` - ${move.dest_appt_time2}`}
                </dd>
              )}
            </div>
            {move.origin_appt_time && (
              <div>
                <dt className="text-muted">Pickup Appt</dt>
                <dd className="text-heading">{formatDate(move.origin_appt_date)}</dd>
                <dd className="text-xs text-gray-500">
                  {move.origin_appt_time}{move.origin_appt_time2 && ` - ${move.origin_appt_time2}`}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-muted">Type</dt>
              <dd className="text-heading">
                {move.move_category?.includes('prepull') ? 'Pre-Pull' : move.legdrop === 'Y' ? 'Drop' : 'Live'}
              </dd>
            </div>
            <div>
              <dt className="text-muted">Est. Duration</dt>
              <dd className="text-heading">{move.estimated_duration_minutes} min</dd>
            </div>
            <div>
              <dt className="text-muted">Travel From Prev</dt>
              <dd className="text-heading">{move.travel_time_from_previous} min</dd>
            </div>
          </dl>
        </div>

        {/* Debug: Raw Data */}
        <details className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
          <summary className="cursor-pointer text-sm font-medium text-label">
            Debug: Raw Data
          </summary>
          <div className="mt-2 space-y-2 text-xs">
            <div>
              <span className="text-gray-500">rejection_summary:</span>
              <pre className="mt-1 p-2 bg-white dark:bg-gray-900 rounded overflow-auto max-h-24">
                {JSON.stringify(move.rejection_summary, null, 2) || 'null'}
              </pre>
            </div>
            <div>
              <span className="text-gray-500">rejected_drivers:</span>
              <pre className="mt-1 p-2 bg-white dark:bg-gray-900 rounded overflow-auto max-h-32">
                {JSON.stringify(move.rejected_drivers, null, 2) || 'null'}
              </pre>
            </div>
            <div>
              <span className="text-gray-500">driver_context:</span>
              <pre className="mt-1 p-2 bg-white dark:bg-gray-900 rounded overflow-auto max-h-24">
                {JSON.stringify(move.driver_context, null, 2) || 'null'}
              </pre>
            </div>
            <div>
              <span className="text-gray-500">alternatives count:</span> {move.alternatives?.length || 0}
            </div>
          </div>
        </details>
      </div>
    </Drawer>
  );
};

// ============================================================================
// Staging Drawer Components
// ============================================================================

interface StagingHandleProps {
  count: number;
  onClick: () => void;
}

/** Fixed viewport handle that shows "Staging (X)" vertically on the right edge */
const StagingHandle: React.FC<StagingHandleProps> = ({ count, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-blue-600 hover:bg-blue-700 text-white px-2 py-4 rounded-l-lg shadow-lg transition-colors"
      style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
    >
      <span className="text-sm font-medium tracking-wide">
        Staging ({count})
      </span>
    </button>
  );
};

interface StagingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  items: DraggableItem[];
  width: number;
  minWidth: number;
  maxWidth: number;
  onWidthChange: (width: number) => void;
  onDragStart: (item: DraggableItem) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDebugClick?: (move: PlannedMove) => void;
  appliedMoves?: Set<string>;
}

/** Staging drawer that slides from right and supports drag-drop */
const StagingDrawer: React.FC<StagingDrawerProps> = ({
  isOpen,
  onClose,
  items,
  width,
  minWidth,
  maxWidth,
  onWidthChange,
  onDragStart,
  onDragOver,
  onDrop,
  onDebugClick,
  appliedMoves = new Set(),
}) => {
  const [isResizing, setIsResizing] = React.useState(false);
  const drawerRef = React.useRef<HTMLDivElement>(null);

  // Handle resize
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  React.useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      onWidthChange(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minWidth, maxWidth, onWidthChange]);

  if (!isOpen) return null;

  return (
    <div
      ref={drawerRef}
      className="flex-shrink-0 h-full bg-panel border-l border-gray-200 dark:border-gray-700 shadow-lg flex flex-col relative"
      style={{ width: `${width}px` }}
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-blue-400 transition-colors"
        onMouseDown={handleMouseDown}
        style={{ backgroundColor: isResizing ? '#60a5fa' : 'transparent' }}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Staging ({items.length})
        </h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <XMarkIcon className="h-5 w-5 text-muted" />
        </button>
      </div>

      {/* Drop zone content */}
      <div
        className="flex-1 overflow-auto p-3"
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        {items.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center p-4">
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Drag moves here to hold while scrolling
            </p>
          </div>
        ) : (
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(200px, 1fr))` }}>
            {items.map((item) => (
              <Tooltip
                key={item.id}
                placement="left"
                content={
                  <div className={`p-2 rounded-lg border min-w-[260px] ${
                    item.isGroup
                      ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                      : 'border-gray-300 bg-panel'
                  }`}>
                    <div className={item.moves.length > 1 ? 'flex gap-2' : ''}>
                      {item.moves.map((move, idx) => {
                        const isApplied = appliedMoves.has(`${move.pronumber}-${move.legnum}`) || move.was_applied_by_preplan;
                        return (<div
                          key={`${move.pronumber}-${move.legnum}`}
                          className={item.moves.length > 1 ? 'flex-1 p-1.5 bg-gray-50 dark:bg-gray-700/50 rounded border border-rule' : ''}
                        >
                          {/* PRO Number row */}
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              {move.pronumber}-{move.legnum}
                            </span>
                            <div className="flex items-center gap-0.5">
                              {/* Move Type Badge (Import/Export/Rail) */}
                              {move.movetype && (
                                <span className={`px-1 py-0.5 text-[0.5625rem] font-medium rounded ${
                                  move.movetype === 'IMPORT'
                                    ? 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
                                    : move.movetype === 'EXPORT'
                                    ? 'text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30'
                                    : move.movetype === 'RAIL'
                                    ? 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30'
                                    : 'text-label bg-gray-100 dark:bg-gray-700/30'
                                }`}>
                                  {move.movetype === 'IMPORT' ? 'Imp' : move.movetype === 'EXPORT' ? 'Exp' : move.movetype === 'RAIL' ? 'Rail' : move.movetype?.substring(0, 3)}
                                </span>
                              )}
                              {/* Delivery Type Badge */}
                              <span className={`px-1 py-0.5 text-[0.5625rem] font-medium rounded ${
                                move.move_category?.includes('prepull')
                                  ? 'text-cyan-700 dark:text-cyan-300 bg-cyan-100 dark:bg-cyan-900/30'
                                  : move.is_return_move
                                  ? (move.movetype === 'EXPORT'
                                    ? 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
                                    : 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30')
                                  : move.legdrop === 'Y'
                                  ? 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30'
                                  : 'text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30'
                              }`}>
                                {move.move_category?.includes('prepull') ? 'Pre' : move.is_return_move ? (move.movetype === 'EXPORT' ? 'Full' : 'Empty') : move.legdrop === 'Y' ? 'Drop' : 'Live'}
                              </span>
                            </div>
                          </div>

                          {/* Requirement Icons and Score row */}
                          {(move.requires_93k || move.requires_hazmat || move.requires_tank || move.requires_daycab || move.requires_ppe || move.assignment_score !== undefined) && (
                            <div className="flex items-center gap-1 mb-1">
                              {move.requires_93k && (
                                <span className="gw-icon text-purple-600 dark:text-purple-400" style={{ fontSize: '11px' }}>&#xE9E5;</span>
                              )}
                              {move.requires_hazmat && (
                                <span className="gw-icon gw-icon-hazmat text-orange-500" style={{ fontSize: '11px' }}></span>
                              )}
                              {move.requires_tank && (
                                <span className="gw-icon gw-icon-tank text-purple-600 dark:text-purple-400" style={{ fontSize: '11px' }}></span>
                              )}
                              {move.requires_daycab && (
                                <span className="gw-icon gw-icon-daycab text-blue-600 dark:text-blue-400" style={{ fontSize: '11px' }}></span>
                              )}
                              {move.requires_ppe && (
                                <ShieldCheckIcon className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                              )}
                              {move.assignment_score !== undefined && (
                                <span className={`px-1.5 py-0.5 text-[0.625rem] font-medium rounded ${getScoreColor(move.assignment_score)}`}>
                                  {formatScore(move.assignment_score)}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Location names */}
                          <div className="text-xs text-label">
                            {move.origin_name}
                            {move.fallback_origin_name && (
                              <span className="text-orange-500 dark:text-orange-400 text-[0.5625rem] ml-0.5">({move.fallback_origin_name})</span>
                            )}
                            {' → '}
                            {move.dest_name}
                            {move.fallback_dest_name && (
                              <span className="text-orange-500 dark:text-orange-400 text-[0.5625rem] ml-0.5">({move.fallback_dest_name})</span>
                            )}
                          </div>

                          {/* Route - city/state with miles right-aligned */}
                          <div className="flex items-center justify-between text-[0.5625rem] text-muted">
                            <span className="truncate">
                              {move.origin_city}{move.origin_state ? `, ${move.origin_state}` : ''}
                              {move.fallback_origin_display && (
                                <span className="text-orange-500 dark:text-orange-400"> ({move.fallback_origin_display})</span>
                              )}
                              {' → '}
                              {move.dest_city}{move.dest_state ? `, ${move.dest_state}` : ''}
                              {move.fallback_dest_display && (
                                <span className="text-orange-500 dark:text-orange-400"> ({move.fallback_dest_display})</span>
                              )}
                            </span>
                            {(move.is_return_move && move.distance_from_delivery !== undefined) ? (
                              <span className="text-yellow-600 dark:text-yellow-400 ml-1 shrink-0">
                                {move.distance_from_delivery.toFixed(1)} mi
                              </span>
                            ) : move.leg_miles !== undefined && move.leg_miles > 0 && (
                              <span className="text-gray-400 ml-1 shrink-0">{move.leg_miles.toFixed(1)} mi</span>
                            )}
                          </div>

                          {/* Chassis info */}
                          {(move.chassis_type || move.chassis_size) && (
                            <div className="text-[0.5625rem] text-gray-400 dark:text-gray-500">
                              {move.chassis_size}{move.chassis_size && move.chassis_type && '/'}{move.chassis_type}
                            </div>
                          )}

                          {/* Time info */}
                          <div className="text-[0.625rem] text-muted mt-0.5">
                            <div className="flex items-center justify-between">
                              <span>
                                {formatDate(move.dest_appt_date)}
                                {move.dest_appt_time1 && (
                                  <span className="ml-1 text-gray-600 dark:text-gray-300">
                                    @ {move.dest_appt_time1}{move.dest_appt_time2 && ` - ${move.dest_appt_time2}`}
                                  </span>
                                )}
                              </span>
                              <div className="text-right">
                                {move.estimated_start && (
                                  <span className="text-label">
                                    {formatTime(move.estimated_start)}{move.estimated_end && ` - ${formatTime(move.estimated_end)}`}
                                  </span>
                                )}
                                {move.travel_time_from_previous > 0 && (
                                  <span className="ml-1 text-orange-500 dark:text-orange-400">
                                    +{formatDuration(move.travel_time_from_previous)}
                                  </span>
                                )}
                              </div>
                            </div>
                            {move.origin_appt_time && (
                              <div className="text-[0.5625rem] text-gray-400 dark:text-gray-500">
                                Pickup: {move.origin_appt_time}{move.origin_appt_time2 && ` - ${move.origin_appt_time2}`}
                              </div>
                            )}
                          </div>
                        </div>
                      );})}
                    </div>
                  </div>
                }
              >
                <div
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    onDragStart(item);
                  }}
                  className={`p-2 rounded-lg border cursor-grab active:cursor-grabbing transition-colors ${
                    item.isGroup
                      ? 'border-purple-400 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
                  }`}
                >
                  {item.moves.map((move, idx) => {
                    const isApplied = appliedMoves.has(`${move.pronumber}-${move.legnum}`);
                    return (
                      <div
                        key={`${move.pronumber}-${move.legnum}`}
                        className={`text-xs ${idx > 0 ? 'mt-1 pt-1 border-t border-rule' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${isApplied ? 'text-green-600 dark:text-green-400' : 'text-heading'}`}>
                            {move.pronumber}-{move.legnum}
                          </span>
                          {onDebugClick && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDebugClick(move);
                              }}
                              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                            >
                              <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400" />
                            </button>
                          )}
                        </div>
                        <div className="text-muted truncate">
                          {move.origin_city} → {move.dest_city}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

function SmartPrePlanning({ initialPlan, initialRequest }: {
  initialPlan?: GeneratePrePlanResponse;
  initialRequest?: GeneratePrePlanRequest;
} = {}) {
  // Permission check
  const { hasAccess, permissions } = usePermissions();
  const canEditConfig = hasAccess('/administration/smart-dispatch');

  // Footer context for Apply Plan button
  const { setDispatchButton } = useDispatchFooter();

  // Icon legend context
  const { setLegendSections } = useIconLegend();

  // State
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    if (initialPlan) return new Date(`${initialPlan.target_date}T00:00:00`);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });
  const [selectedRegion, setSelectedRegion] = useState<string>(initialPlan?.region_name || '');
  const [regionOptions, setRegionOptions] = useState<{ value: string; label: string }[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [selectedPreference, setSelectedPreference] = useState<'All' | 'OTR' | 'Local' | 'Pre-Pulls'>(initialRequest?.preference_filter || 'All');
  const [includeAssignedMoves, setIncludeAssignedMoves] = useState(initialRequest?.include_assigned_moves ?? false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prePlan, setPrePlan] = useState<GeneratePrePlanResponse | null>(null);

  // Refs
  const homeRegionInitialized = React.useRef(!!initialPlan);

  // Collapsed drivers
  const [collapsedDrivers, setCollapsedDrivers] = useState<Set<string>>(new Set());

  // Debug drawer
  const [debugDrawerMove, setDebugDrawerMove] = useState<PlannedMove | null>(null);
  const [debugDriverPlan, setDebugDriverPlan] = useState<DriverDayPlan | null>(null);
  const [isDebugDrawerOpen, setIsDebugDrawerOpen] = useState(false);

  // Order details drawer state
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  // Config modal
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

  // ============================================================================
  // Local editable state for Kanban drag-drop
  // ============================================================================
  const [localDrivers, setLocalDrivers] = useState<LocalDriverPlan[]>([]);
  const [localUnassigned, setLocalUnassigned] = useState<DraggableItem[]>([]);
  const [driverSort, setDriverSort] = useState<'driver' | 'loads_asc' | 'loads_desc' | 'reassigned_desc'>('driver');
  const [searchQuery, setSearchQuery] = useState('');
  const [qualificationFilters, setQualificationFilters] = useState<Set<string>>(new Set());
  const [loadFilters, setLoadFilters] = useState<Set<LoadFilter>>(new Set());
  const [draggedItem, setDraggedItem] = useState<{
    item: DraggableItem;
    sourceDriver: string | null;
  } | null>(null);

  // Header collapse state
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  // Staging drawer state
  const [isStagingOpen, setIsStagingOpen] = useState(false);
  const [stagedMoves, setStagedMoves] = useState<DraggableItem[]>([]);
  const [stagingWidth, setStagingWidth] = useState(300);
  const stagingMinWidth = 250;
  const stagingMaxWidth = typeof window !== 'undefined' ? window.innerWidth * 0.5 : 600;

  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState<{
    analysis: string;
    suggestions: string[];
    warnings: string[];
    optimization_score?: number;
    input_tokens?: number;
    output_tokens?: number;
    model_used?: string;
  } | null>(null);
  const [analyzingPlan, setAnalyzingPlan] = useState(false);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [dataCopied, setDataCopied] = useState(false);
  const [selectedAiModel, setSelectedAiModel] = useState<string>('sonnet');

  // AI Optimization suggestions state
  const [aiOptimizations, setAiOptimizations] = useState<{
    suggestions: Array<{
      type: 'assign' | 'swap' | 'remove' | 'warning';
      priority: 'high' | 'medium' | 'low';
      description: string;
      move_pro?: string;
      move_legnum?: number;
      suggested_driver?: string;
      suggested_driver_name?: string;
      from_driver?: string;
      from_driver_name?: string;
      to_driver?: string;
      to_driver_name?: string;
      reasoning?: string;
    }>;
    summary: string;
    input_tokens?: number;
    output_tokens?: number;
    model_used?: string;
  } | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  const [showOptimizations, setShowOptimizations] = useState(false);

  // Schedule validation state (for drag-and-drop)
  const [validatingSchedule, setValidatingSchedule] = useState(false);

  // Apply Plan state
  const [selectedDrivers, setSelectedDrivers] = useState<Set<string>>(new Set());
  const [applyingPlan, setApplyingPlan] = useState(false);
  // Moves sent so far in a running apply; null when none is running.
  const [applyProgress, setApplyProgress] = useState<ApplyProgress | null>(null);
  const [showApplyConfirmDialog, setShowApplyConfirmDialog] = useState(false);
  // Street turns the user has ticked, keyed "pronumber-legnum". Empty by default:
  // a turn is not a dispatch, it rewrites two orders and asks a steamship line to
  // approve the reuse, so it is opted into one pairing at a time.
  const [turnsToPerform, setTurnsToPerform] = useState<Set<string>>(new Set());
  const [applyResults, setApplyResults] = useState<{
    success: AppliedMove[];
    // Held back by a hard stop; nothing on the PRO changed. Not offered a retry.
    blocked: HeldMove[];
    failed: HeldMove[];
    cmsResponse?: unknown;  // Raw CMS response for debugging
  } | null>(null);
  // Track applied moves (key: "pronumber-legnum")
  const [appliedMoves, setAppliedMoves] = useState<Set<string>>(new Set());

  // Header-drag for the apply-results modal.
  const applyResultsDrag = useModalDrag(!!applyResults);

  // Multi-select state for bulk removal
  const [selectedMoves, setSelectedMoves] = useState<Set<string>>(new Set()); // Key: "driverUnitnumber:pronumber-legnum"
  const [showBulkRemoveConfirm, setShowBulkRemoveConfirm] = useState(false);

  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [bulkRemoveResults, setBulkRemoveResults] = useState<{ success: number; failed: number; errors?: string[] } | null>(null);

  // Bulk undo preplanned state
  const [showUndoPreplannedModal, setShowUndoPreplannedModal] = useState(false);
  const [appliedPlans, setAppliedPlans] = useState<Array<{
    plan_id: string;
    applied_at: string | null;
    applied_by: string | null;
    created_at: string | null;
    status: string;
    region_name: string;
    applied_count: number;
    total_assignments: number;
  }>>([]);
  const [selectedPlansToUndo, setSelectedPlansToUndo] = useState<Set<string>>(new Set());
  const [loadingAppliedPlans, setLoadingAppliedPlans] = useState(false);
  const [undoingPreplanned, setUndoingPreplanned] = useState(false);
  const { checkAndPrompt, showNoteModal, handleNoteSubmit: onNoteSubmit, handleNoteCancel } = useDeadlineCheck();
  // Undo reverses a street turn with the order screen's Undo Reuse, so it is gated the
  // same way: `reuse-container`, hidden until granted.
  const mayUndoTurns = useResolvedAccessLevel('reuse-container', true) >= 2;
  const [undoPreplannedResults, setUndoPreplannedResults] = useState<{ success: number; failed: number; staleCleaned?: number; restored?: number; kept?: number; turnsUndone?: number; errors?: string[] } | null>(null);

  // AI Model options with pricing (per million tokens)
  const aiModelOptions = [
    { value: 'sonnet', label: 'Claude Sonnet 4', description: 'Fast & capable (default)', inputCost: 3, outputCost: 15 },
    { value: 'opus', label: 'Claude Opus 4', description: 'Most capable, slower', inputCost: 15, outputCost: 75 },
    { value: 'haiku', label: 'Claude 3.5 Haiku', description: 'Fastest, basic analysis', inputCost: 0.8, outputCost: 4 },
  ];

  // Estimate tokens and cost for AI analysis
  const aiCostEstimate = useMemo(() => {
    if (!prePlan) return null;

    // Token estimation: JSON is ~3-4 chars per token on average
    // But we send a summarized version, not raw JSON - the backend formats it as text
    // Based on actual usage: ~25-35k tokens for 300 drivers, 275 moves
    const driverCount = prePlan.driver_plans?.length || 0;
    const unassignedCount = prePlan.unassigned_moves?.length || 0;

    // Estimate based on actual observed token usage
    // Base prompt: ~2000 tokens
    // Per driver with moves: ~80 tokens (name, moves, timing)
    // Per unassigned move: ~50 tokens
    // Config/stats: ~500 tokens
    const baseTokens = 2500;
    const driverTokens = driverCount * 80;
    const unassignedTokens = unassignedCount * 50;
    const estimatedInputTokens = baseTokens + driverTokens + unassignedTokens;

    // Output is typically 1500-2500 tokens for the analysis
    const estimatedOutputTokens = 2000;

    const selectedModel = aiModelOptions.find(m => m.value === selectedAiModel) || aiModelOptions[0];
    const inputCost = (estimatedInputTokens / 1_000_000) * selectedModel.inputCost;
    const outputCost = (estimatedOutputTokens / 1_000_000) * selectedModel.outputCost;
    const totalCost = inputCost + outputCost;

    return {
      inputTokens: estimatedInputTokens,
      outputTokens: estimatedOutputTokens,
      totalCost,
      modelLabel: selectedModel.label,
    };
  }, [prePlan, selectedAiModel, aiModelOptions]);

  // Calculate actual cost from AI response
  const aiActualCost = useMemo(() => {
    if (!aiAnalysis?.input_tokens || !aiAnalysis?.output_tokens) return null;

    // Find the model pricing based on model_used
    let inputCostPerM = 3; // Default to Sonnet
    let outputCostPerM = 15;

    if (aiAnalysis.model_used?.includes('opus')) {
      inputCostPerM = 15;
      outputCostPerM = 75;
    } else if (aiAnalysis.model_used?.includes('haiku')) {
      inputCostPerM = 0.8;
      outputCostPerM = 4;
    }

    const inputCost = (aiAnalysis.input_tokens / 1_000_000) * inputCostPerM;
    const outputCost = (aiAnalysis.output_tokens / 1_000_000) * outputCostPerM;

    return {
      inputTokens: aiAnalysis.input_tokens,
      outputTokens: aiAnalysis.output_tokens,
      totalCost: inputCost + outputCost,
      modelUsed: aiAnalysis.model_used,
    };
  }, [aiAnalysis]);

  // HOW MANY DRIVERS EACH ACTION TOUCHES. Drives the two board-action chips: a chip
  // that cannot say "12" is not worth clicking, and one reading 0 says the answer
  // without a click at all.
  const actionCounts = useMemo(() => {
    let reassigned = 0, streetTurn = 0;
    for (const driver of localDrivers) {
      if (driver.items.some(i => i.moves.some(m => m.reassigned_from))) reassigned += 1;
      if (driver.items.some(i => i.moves.some(m => m.street_turn_export))) streetTurn += 1;
    }
    return { reassigned, street_turn: streetTurn };
  }, [localDrivers]);

  // Live / Drop / Early appt ask about MOVES, not trucks: a driver stays on the board
  // when any move in their day matches, and the matching moves are ringed so they can
  // be found in it. Each chip counts the drivers it would leave on its own.
  const loadFilterMatch = useMemo(
    () => (loadFilters.size > 0 ? (move: PlannedMove) => matchesLoadFilters(move, loadFilters) : undefined),
    [loadFilters]);
  const loadFilterCounts = useMemo(() => {
    const counts = {} as Record<LoadFilter, number>;
    for (const { key } of LOAD_FILTERS) {
      const only = new Set([key]);
      counts[key] = localDrivers.filter(d => d.items.some(i => i.moves.some(m => matchesLoadFilters(m, only)))).length;
    }
    return counts;
  }, [localDrivers]);

  // Filter and sort drivers based on search query and sort option
  const filteredSortedDrivers = useMemo(() => {
    let filtered = localDrivers;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(driver => {
        // Match driver name or unit number
        if (driver.driver_name.toLowerCase().includes(query)) return true;
        if (driver.unitnumber.toLowerCase().includes(query)) return true;

        // Match any move's PRO number or location names
        return driver.items.some(item =>
          item.moves.some(move => {
            if (String(move.pronumber).includes(query)) return true;
            if (move.origin_name?.toLowerCase().includes(query)) return true;
            if (move.origin_city?.toLowerCase().includes(query)) return true;
            if (move.dest_name?.toLowerCase().includes(query)) return true;
            if (move.dest_city?.toLowerCase().includes(query)) return true;
            return false;
          })
        );
      });
    }

    // Apply qualification filters (AND logic - driver must have ALL selected qualifications)
    if (qualificationFilters.size > 0) {
      filtered = filtered.filter(driver => {
        for (const qual of qualificationFilters) {
          switch (qual) {
            case 'sleeper': if (driver.is_daycab) return false; break;
            case 'daycab': if (!driver.is_daycab) return false; break;
            case 'hazmat': if (!driver.has_hazmat) return false; break;
            case 'tank': if (!driver.has_tank) return false; break;
            case '93k': if (!driver.has_93k) return false; break;
            case 'light_truck': if (!driver.is_light_truck) return false; break;
            // NOT QUALIFICATIONS — what this apply is about to DO. Both are thin on a
            // real board (63 reassignments and 12 street turns across 306 drivers on
            // test plan 65/66), so without a way to ask for them they are findable
            // only by scrolling the whole roster.
            case 'reassigned':
              if (!driver.items.some(i => i.moves.some(m => m.reassigned_from))) return false;
              break;
            case 'street_turn':
              if (!driver.items.some(i => i.moves.some(m => m.street_turn_export))) return false;
              break;
          }
        }
        return true;
      });
    }

    if (loadFilterMatch) {
      filtered = filtered.filter(driver => driver.items.some(item => item.moves.some(loadFilterMatch)));
    }

    // Apply sort
    const sorted = [...filtered];
    switch (driverSort) {
      case 'driver':
        sorted.sort((a, b) => a.unitnumber.localeCompare(b.unitnumber));
        break;
      case 'loads_asc':
        sorted.sort((a, b) => a.items.length - b.items.length);
        break;
      case 'loads_desc':
        sorted.sort((a, b) => b.items.length - a.items.length);
        break;
      // THE ONLY WAY TO FIND THEM. A replan spreads reassignments thinly — 79 across
      // 63 of 306 drivers on test plan 65 — so the ones that cost somebody their work
      // are invisible without scrolling the whole board. This floats them to the top.
      case 'reassigned_desc': {
        const taken = (d: LocalDriverPlan) => d.items.reduce(
          (n, item) => n + item.moves.filter(m => m.reassigned_from).length, 0);
        sorted.sort((a, b) => taken(b) - taken(a) || a.unitnumber.localeCompare(b.unitnumber));
        break;
      }
    }

    return sorted;
  }, [localDrivers, searchQuery, driverSort, qualificationFilters, loadFilterMatch]);

  // Function to copy driver data for AI comparison
  const handleCopyDriverData = useCallback(() => {
    if (!prePlan) return;

    // Build a comprehensive data export for comparison
    const lines: string[] = [];

    lines.push('=== PRE-PLAN ANALYSIS DATA ===');
    lines.push(`Generated: ${new Date().toISOString()}`);
    lines.push(`Region: ${prePlan.region_name}`);
    lines.push(`Date: ${prePlan.target_date}`);
    lines.push(`Plan ID: ${prePlan.plan_id}`);
    lines.push('');

    // ============ CONFIG USED ============
    lines.push('--- CONFIG USED ---');
    const cfg = prePlan.config_used as Record<string, number | string>;
    lines.push(`Weights: distance=${cfg.weight_distance || '?'}, workload=${cfg.weight_workload || '?'}, profitability=${cfg.weight_profitability || '?'}, driver_score=${cfg.weight_driver_score || '?'}`);
    lines.push(`Max Dispatches: ${cfg.max_dispatches || '?'}`);
    lines.push(`Distance Max: ${cfg.distance_max_miles || '?'} miles`);
    lines.push(`Return Max: ${cfg.return_max_miles || '?'} miles`);
    lines.push(`Min Score Threshold: ${cfg.min_score_threshold || '?'}`);
    lines.push(`Buffer Minutes: ${prePlan.buffer_minutes}`);
    lines.push('');

    // ============ STATISTICS ============
    lines.push('--- STATISTICS ---');
    const stats = prePlan.statistics;
    lines.push(`Total Delivery Moves: ${stats.total_moves}`);
    const prepullsStr = (stats.assigned_prepulls ?? 0) > 0 ? ` + ${stats.assigned_prepulls} pre-pulls` : '';
    const localPrepullsStr = (stats.assigned_local_prepulls ?? 0) > 0 ? ` + ${stats.assigned_local_prepulls} local pre-pulls` : '';
    const exportReturnsStr = (stats.assigned_export_returns ?? 0) > 0 ? ` + ${stats.assigned_export_returns} export terminates` : '';
    lines.push(`Assigned: ${stats.assigned_moves} (${stats.assigned_deliveries} deliveries${prepullsStr}${localPrepullsStr}${exportReturnsStr} + ${stats.slotted_returns} returns)`);
    lines.push(`Unassigned: ${stats.unassigned_moves}`);
    lines.push(`Drivers: ${stats.drivers_with_assignments} with work / ${stats.total_drivers_in_region} total (${stats.drivers_at_capacity} at capacity)`);
    lines.push(`Avg Moves/Driver: ${stats.average_moves_per_driver?.toFixed(1) || '?'}`);
    lines.push(`Avg Assignment Score: ${((stats.average_assignment_score || 0) * 100).toFixed(0)}%`);
    lines.push(`Generation Time: ${stats.generation_time_ms}ms (${stats.moves_per_second?.toFixed(1) || '?'} moves/sec)`);
    lines.push('');

    // ============ UNASSIGNED MOVES ============
    if (prePlan.unassigned_moves && prePlan.unassigned_moves.length > 0) {
      lines.push('--- ❌ UNASSIGNED MOVES (CRITICAL) ---');
      lines.push(`${prePlan.unassigned_moves.length} moves could not be assigned:\n`);

      // Group by rejection reason for summary
      const reasonCounts: Record<string, number> = {};
      for (const um of prePlan.unassigned_moves) {
        for (const r of um.reasons || []) {
          reasonCounts[r.reason_code] = (reasonCounts[r.reason_code] || 0) + 1;
        }
      }
      lines.push('Rejection Reasons Summary:');
      for (const [reason, count] of Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])) {
        lines.push(`  ${reason}: ${count} moves affected`);
      }
      lines.push('');

      // Detail each unassigned move
      for (const um of prePlan.unassigned_moves) {
        const apptTime = um.dest_appt_time1 ? ` @ ${um.dest_appt_time1}` : '';
        lines.push(`PRO ${um.pronumber}-${um.legnum}: ${um.origin_city} → ${um.dest_city}${apptTime}`);
        lines.push(`  Location: ${um.origin_name} → ${um.dest_name}`);
        lines.push(`  Drivers Checked: ${um.total_drivers_checked}`);
        for (const r of um.reasons || []) {
          lines.push(`  ❌ ${r.reason_code}: ${r.description} (${r.affected_drivers} drivers)`);
        }
        if (um.suggestions && um.suggestions.length > 0) {
          lines.push(`  💡 Suggestions: ${um.suggestions.join(', ')}`);
        }
        lines.push('');
      }
    } else {
      lines.push('--- ✅ ALL MOVES ASSIGNED ---');
      lines.push('No unassigned moves.');
      lines.push('');
    }

    // ============ AVAILABLE DRIVERS WITHOUT WORK ============
    const driversWithWork = new Set(prePlan.driver_plans.filter(dp => dp.total_moves > 0).map(dp => dp.unitnumber));
    const idleDrivers = (prePlan.available_drivers || []).filter(ad => !driversWithWork.has(ad.unitnumber));

    if (idleDrivers.length > 0) {
      lines.push('--- AVAILABLE DRIVERS WITHOUT ASSIGNMENTS ---');
      lines.push(`${idleDrivers.length} drivers have no work assigned:\n`);

      // Count qualifications
      const qualCounts = {
        hazmat: idleDrivers.filter(d => d.has_hazmat).length,
        tank: idleDrivers.filter(d => d.has_tank).length,
        '93k': idleDrivers.filter(d => d.has_93k).length,
        daycab: idleDrivers.filter(d => d.is_daycab).length,
        light_truck: idleDrivers.filter(d => d.is_light_truck).length,
      };
      lines.push('Qualification Summary of Idle Drivers:');
      lines.push(`  Hazmat: ${qualCounts.hazmat}, Tank: ${qualCounts.tank}, 93K: ${qualCounts['93k']}, Daycab: ${qualCounts.daycab}, Light Truck: ${qualCounts.light_truck}`);
      lines.push('');

      // List first 15 with their qualifications
      for (const ad of idleDrivers.slice(0, 15)) {
        const quals: string[] = [];
        if (ad.has_hazmat) quals.push('HAZMAT');
        if (ad.has_tank) quals.push('TANK');
        if (ad.has_93k) quals.push('93K');
        if (ad.is_daycab) quals.push('DAYCAB');
        if (ad.is_light_truck) quals.push('LIGHT');
        const qualStr = quals.length > 0 ? ` [${quals.join(', ')}]` : '';
        lines.push(`${ad.driver_name} (${ad.unitnumber}): ${ad.available_hours}h available, ${ad.current_capacity_used.toFixed(1)} capacity used${qualStr}`);
      }
      if (idleDrivers.length > 15) {
        lines.push(`... and ${idleDrivers.length - 15} more idle drivers`);
      }
      lines.push('');
    }

    // ============ WARNING SUMMARY ============
    const driversWithWarnings = prePlan.driver_plans.filter(dp => dp.schedule_warnings && dp.schedule_warnings.length > 0);
    const warningsWithNewMoves = driversWithWarnings.filter(dp => (dp.new_moves || 0) > 0);
    const warningsExistingOnly = driversWithWarnings.filter(dp => (dp.new_moves || 0) === 0);

    lines.push('--- WARNING SUMMARY ---');
    lines.push(`Total drivers with warnings: ${driversWithWarnings.length}`);
    lines.push(`  - Warnings from NEW assignments (ACTION NEEDED): ${warningsWithNewMoves.length}`);
    lines.push(`  - Warnings from EXISTING dispatches only (pre-existing): ${warningsExistingOnly.length}`);
    lines.push('');

    // Warnings from new assignments
    if (warningsWithNewMoves.length > 0) {
      lines.push('--- ⚠️ WARNINGS FROM NEW ASSIGNMENTS ---');
      for (const dp of warningsWithNewMoves.sort((a, b) => (b.new_moves || 0) - (a.new_moves || 0))) {
        lines.push(`${dp.driver_name} (${dp.unitnumber}): ${dp.total_moves} moves (${dp.existing_moves} existing, ${dp.new_moves} NEW)`);
        lines.push(`  Hours: ${dp.estimated_total_hours?.toFixed(1) || '?'}h${dp.raw_span_hours ? ` (raw: ${dp.raw_span_hours.toFixed(1)}h)` : ''}, Multi-day: ${dp.is_multi_day ? 'YES' : 'no'}`);
        for (const w of dp.schedule_warnings || []) {
          lines.push(`  ⚠️ ${w}`);
        }
      }
      lines.push('');
    }

    // Existing-only warnings (condensed)
    if (warningsExistingOnly.length > 0) {
      lines.push('--- EXISTING DISPATCH WARNINGS (not from pre-plan) ---');
      lines.push(`${warningsExistingOnly.length} drivers with pre-existing schedule issues (0 new moves):`);
      for (const dp of warningsExistingOnly.slice(0, 10)) {
        lines.push(`  ${dp.driver_name} (${dp.unitnumber}): ${dp.total_moves} moves, ${dp.estimated_total_hours?.toFixed(1) || '?'}h`);
      }
      if (warningsExistingOnly.length > 10) {
        lines.push(`  ... and ${warningsExistingOnly.length - 10} more`);
      }
      lines.push('');
    }

    // ============ HIGH-LOAD DRIVERS ============
    const highLoadWithNew = prePlan.driver_plans.filter(dp => dp.total_moves >= 5 && (dp.new_moves || 0) > 0);
    if (highLoadWithNew.length > 0) {
      lines.push('--- HIGH-LOAD DRIVERS (5+ moves with new assignments) ---');
      for (const dp of highLoadWithNew.sort((a, b) => b.total_moves - a.total_moves)) {
        const warningFlag = (dp.schedule_warnings && dp.schedule_warnings.length > 0) ? ' ⚠️' : '';
        lines.push(`${dp.driver_name} (${dp.unitnumber}): ${dp.total_moves} moves (${dp.existing_moves} existing, ${dp.new_moves} NEW), ${dp.estimated_total_hours?.toFixed(1) || '?'}h${warningFlag}`);
      }
      lines.push('');
    }

    // ============ REJECTION ANALYSIS (from assigned moves) ============
    // Aggregate rejection reasons from all assigned moves
    const rejectionTotals: Record<string, number> = {};
    for (const dp of prePlan.driver_plans) {
      for (const move of dp.moves || []) {
        if (move.rejection_summary) {
          for (const [reason, count] of Object.entries(move.rejection_summary)) {
            if (count && count > 0) {
              rejectionTotals[reason] = (rejectionTotals[reason] || 0) + count;
            }
          }
        }
      }
    }
    if (Object.keys(rejectionTotals).length > 0) {
      lines.push('--- REJECTION ANALYSIS (why drivers were skipped) ---');
      lines.push('Aggregate rejection reasons across all assigned moves:');
      for (const [reason, count] of Object.entries(rejectionTotals).sort((a, b) => b[1] - a[1])) {
        lines.push(`  ${reason}: ${count} driver-move combinations rejected`);
      }
      lines.push('');
    }

    // ============ MOVE REQUIREMENTS SUMMARY ============
    // Check what qualifications moves require
    const moveReqs = { hazmat: 0, tank: 0, '93k': 0, daycab: 0, overweight: 0 };
    for (const dp of prePlan.driver_plans) {
      for (const move of dp.moves || []) {
        if (move.requires_hazmat) moveReqs.hazmat++;
        if (move.requires_tank) moveReqs.tank++;
        if (move.requires_93k) moveReqs['93k']++;
        if (move.requires_daycab) moveReqs.daycab++;
        if (move.requires_overweight_permit) moveReqs.overweight++;
      }
    }
    const hasReqs = Object.values(moveReqs).some(v => v > 0);
    if (hasReqs) {
      lines.push('--- MOVE REQUIREMENTS SUMMARY ---');
      lines.push('Assigned moves requiring special qualifications:');
      if (moveReqs.hazmat > 0) lines.push(`  Hazmat: ${moveReqs.hazmat} moves`);
      if (moveReqs.tank > 0) lines.push(`  Tank: ${moveReqs.tank} moves`);
      if (moveReqs['93k'] > 0) lines.push(`  93K: ${moveReqs['93k']} moves`);
      if (moveReqs.daycab > 0) lines.push(`  Daycab: ${moveReqs.daycab} moves`);
      if (moveReqs.overweight > 0) lines.push(`  Overweight Permit: ${moveReqs.overweight} moves`);
      lines.push('');
    }

    const text = lines.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setDataCopied(true);
      setTimeout(() => setDataCopied(false), 2000);
    });
  }, [prePlan]);

  // Drop validation error state
  const [dropValidationError, setDropValidationError] = useState<string | null>(null);

  // Current config display
  const [currentConfig, setCurrentConfig] = useState<{
    weight_distance: number;
    weight_workload: number;
    weight_profitability: number;
    weight_driver_score: number;
    max_dispatches: number;
    default_start_time: string;
    distance_max_miles: number;
    return_max_miles: number;
    round_trip_live_count: number;
    round_trip_drop_count: number;
  } | null>(null);

  // Function to reload config (used by useEffect and after saving)
  const reloadCurrentConfig = async (region?: string) => {
    try {
      const config = await preplanningService.getEffectiveConfig(region || undefined, 'preplanning');
      setCurrentConfig({
        weight_distance: config.weight_distance,
        weight_workload: config.weight_workload,
        weight_profitability: config.weight_profitability,
        weight_driver_score: config.weight_driver_score,
        max_dispatches: config.max_dispatches,
        default_start_time: config.default_start_time,
        distance_max_miles: config.distance_max_miles,
        return_max_miles: config.return_max_miles,
        round_trip_live_count: config.round_trip_live_count,
        round_trip_drop_count: config.round_trip_drop_count
      });
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  // Load config when region changes
  useEffect(() => {
    reloadCurrentConfig(selectedRegion);
  }, [selectedRegion]);

  // Load regions on mount
  useEffect(() => {
    const loadRegions = async () => {
      setRegionsLoading(true);
      try {
        const regions = await preplanningService.getRegions();
        setRegionOptions(
          regions.map((r) => ({
            value: r.name,
            label: r.name,
          }))
        );
      } catch (err) {
        console.error('Failed to load regions:', err);
      } finally {
        setRegionsLoading(false);
      }
    };
    loadRegions();
  }, []);

  // Auto-select user's home region when available
  useEffect(() => {
    if (
      !homeRegionInitialized.current &&
      permissions?.home_region &&
      permissions?.home_region_as_default !== false &&
      regionOptions.length > 0 &&
      regionOptions.some(opt => opt.value === permissions.home_region)
    ) {
      setSelectedRegion(permissions.home_region);
      homeRegionInitialized.current = true;
    }
  }, [permissions?.home_region, permissions?.home_region_as_default, regionOptions]);

  // Shared hydration keeps v2 review on the same editing, validation and apply flow.
  const initializePlan = useCallback((result: GeneratePrePlanResponse) => {
    setPrePlan(result);
    // Expand all drivers by default
    setCollapsedDrivers(new Set());

    // Initialize local editable state from the generated plan
    const { drivers, unassigned } = convertToLocalState(
      result.driver_plans,
      result.unassigned_moves,
      result.available_drivers
    );

    // Add empty drivers (those without assignments) to local state
    const driversWithLoads = drivers.filter(d => d.items.length > 0);
    const driversWithLoadsSet = new Set(driversWithLoads.map(d => d.unitnumber));

    // HAVING WORK IS THE ONLY TEST. This also asked `current_capacity_used === 0`,
    // which is NOT "this driver has nothing to do": it is derived from the DELIVERY
    // cap alone, and returns are counted against a separate one
    // (`effective_remaining_returns`). So a driver whose whole plan day is returns
    // reads as zero capacity used and was listed as unassigned despite being busy.
    //
    // On plan 64 that was 195 of 311 drivers — every one of them holding only
    // return work — reported to the user as "200 drivers not assigned" on a board
    // where the planner had in fact given all 311 something to do.
    const emptyDrivers: LocalDriverPlan[] = (result.available_drivers || [])
      .filter(ad => !driversWithLoadsSet.has(ad.unitnumber))
      .map(ad => ({
        unitnumber: ad.unitnumber,
        driver_name: ad.driver_name,
        isMule: ad.isMule,
        items: [],
        has_hazmat: ad.has_hazmat,
        has_tank: ad.has_tank,
        has_93k: ad.has_93k,
        is_daycab: ad.is_daycab,
        is_light_truck: ad.is_light_truck,
        preferred_location_ids: ad.preferred_location_ids,
        not_preferred_location_ids: ad.not_preferred_location_ids,
        near_misses: ad.near_misses,
      }));

    setLocalDrivers([...driversWithLoads, ...emptyDrivers]);
    setLocalUnassigned(unassigned);
    // Clear staging when generating new plan
    setStagedMoves([]);
  }, []);

  useEffect(() => {
    if (initialPlan) initializePlan(initialPlan);
  }, [initialPlan, initializePlan]);

  // Generate pre-plan handler
  const handleGenerate = useCallback(async () => {
    if (!selectedRegion) {
      setError('Please select a region');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await preplanningService.generatePrePlan({
        target_date: formatLocalDate(selectedDate),
        region_name: selectedRegion,
        preference_filter: selectedPreference,
        include_alternatives: true,
        max_alternatives: 5,
        include_timeline: true,
        include_assigned_moves: includeAssignedMoves,
        strategy: initialRequest?.strategy,
        buffer_minutes: initialRequest?.buffer_minutes,
      });
      initializePlan(result);
    } catch (err: unknown) {
      console.error('Failed to generate pre-plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate pre-plan');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedRegion, selectedPreference, includeAssignedMoves, initialRequest, initializePlan]);

  // Toggle driver collapse
  const toggleDriverCollapse = useCallback((unitnumber: string) => {
    setCollapsedDrivers((prev) => {
      const next = new Set(prev);
      if (next.has(unitnumber)) {
        next.delete(unitnumber);
      } else {
        next.add(unitnumber);
      }
      return next;
    });
  }, []);

  // Debug drawer handler
  const handleMoveDebugClick = useCallback((move: PlannedMove, driverPlan: DriverDayPlan) => {
    setDebugDrawerMove(move);
    setDebugDriverPlan(driverPlan);
    setIsDebugDrawerOpen(true);
  }, []);

  // Open order details drawer
  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  // Close order details drawer
  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // ============================================================================
  // Drag-and-Drop Handlers
  // ============================================================================

  /**
   * Check if a driver can handle a move based on qualification requirements.
   * Returns an error message if the driver cannot handle the move, null otherwise.
   */
  const validateMoveForDriver = useCallback((
    item: DraggableItem,
    targetDriver: LocalDriverPlan | null
  ): string | null => {
    // If moving to unassigned, always allow
    if (!targetDriver) return null;

    // Check each move in the item (handles grouped moves)
    for (const move of item.moves) {
      const missingQualifications: string[] = [];

      // Check 93K requirement
      if (move.requires_93k && !targetDriver.has_93k) {
        missingQualifications.push('93K Permit');
      }

      // Check Hazmat requirement
      if (move.requires_hazmat && !targetDriver.has_hazmat) {
        missingQualifications.push('Hazmat');
      }

      // Check Tank requirement
      if (move.requires_tank && !targetDriver.has_tank) {
        missingQualifications.push('Tank');
      }

      // Check Daycab requirement (requires_daycab=true means only daycab drivers can do it)
      if (move.requires_daycab && !targetDriver.is_daycab) {
        missingQualifications.push('Daycab');
      }

      // Check PPE requirement
      if (move.requires_ppe && !targetDriver.has_ppe) {
        missingQualifications.push('PPE');
      }

      if (missingQualifications.length > 0) {
        return `Driver ${targetDriver.unitnumber} missing: ${missingQualifications.join(', ')}`;
      }
    }

    return null;
  }, []);

  const handleDragStart = useCallback((
    e: React.DragEvent,
    item: DraggableItem,
    sourceDriver: string | null
  ) => {
    setDraggedItem({ item, sourceDriver });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // Special source value for staging
  const STAGING_SOURCE = '__staging__';

  // Take a move off its lane (or the Unassigned row) and into the staging drawer.
  // Shared by the drag-drop path and the card's "Send To Staging" button.
  const stageItem = useCallback((item: DraggableItem, sourceDriver: string | null): boolean => {
    // Don't allow staging existing moves (they can only be reordered within same driver)
    const hasExistingMove = item.moves.some(m => m.is_existing);
    if (hasExistingMove) {
      setDropValidationError('Existing moves cannot be staged. They can only be reordered within the same driver.');
      setTimeout(() => setDropValidationError(null), 3000);
      return false;
    }

    // Remove from source
    if (sourceDriver) {
      setLocalDrivers(prev => prev.map(d => {
        if (d.unitnumber !== sourceDriver) return d;
        return {
          ...d,
          items: d.items.filter(i => i.id !== item.id),
        };
      }));
    } else {
      setLocalUnassigned(prev => prev.filter(i => i.id !== item.id));
    }

    // Add to staging
    setStagedMoves(prev => [...prev, item]);
    return true;
  }, []);

  // Handle dropping a move to staging
  const handleDropToStaging = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    if (!draggedItem) return;

    const { item, sourceDriver } = draggedItem;

    // Don't allow dropping from staging back to staging
    if (sourceDriver !== STAGING_SOURCE) {
      stageItem(item, sourceDriver);
    }
    setDraggedItem(null);
  }, [draggedItem, stageItem]);

  // "Send To Staging" from the card's move-arrow popover: the drawer's drop zone is only as
  // tall as its content, so a drag from a scrolled-down lane never lands in it (John, 2026-10-28).
  const handleSendToStaging = useCallback((item: DraggableItem, sourceDriver: string | null) => {
    if (stageItem(item, sourceDriver)) {
      setIsStagingOpen(true);
    }
  }, [stageItem]);

  // Handle starting a drag from staging
  const handleDragStartFromStaging = useCallback((item: DraggableItem) => {
    setDraggedItem({ item, sourceDriver: STAGING_SOURCE });
  }, []);

  const handleDrop = useCallback(async (
    e: React.DragEvent,
    targetDriver: string | null,
    targetIndex: number
  ) => {
    e.preventDefault();

    if (!draggedItem) return;

    const { item, sourceDriver } = draggedItem;

    // Check if this is an existing move (already assigned before plan generation)
    const hasExistingMove = item.moves.some(m => m.is_existing);

    // Existing moves can only be reordered within the same driver, not moved to other drivers
    if (hasExistingMove && sourceDriver !== targetDriver) {
      setDropValidationError('Existing moves cannot be reassigned to other drivers. They can only be reordered within the same driver.');
      setTimeout(() => setDropValidationError(null), 3000);
      setDraggedItem(null);
      return;
    }

    // Validate move for target driver (check qualification requirements)
    const targetDriverPlan = targetDriver
      ? localDrivers.find(d => d.unitnumber === targetDriver) || null
      : null;
    const validationError = validateMoveForDriver(item, targetDriverPlan);
    if (validationError) {
      setDropValidationError(validationError);
      // Auto-clear the error after 3 seconds
      setTimeout(() => setDropValidationError(null), 3000);
      setDraggedItem(null);
      return;
    }

    // Same driver, same position - no change
    if (sourceDriver === targetDriver) {
      const sourceItems = sourceDriver
        ? localDrivers.find(d => d.unitnumber === sourceDriver)?.items || []
        : localUnassigned;
      const currentIndex = sourceItems.findIndex(i => i.id === item.id);
      if (currentIndex === targetIndex || currentIndex === targetIndex - 1) {
        setDraggedItem(null);
        return;
      }
    }

    // Clear any previous validation error
    setDropValidationError(null);

    // Build proposed assignments for schedule validation
    // We need to validate both the source driver (after removal) and target driver (after addition)
    const driversToValidate: Array<{ unitnumber: string; moves: Array<{ pronumber: number; legnum: number; sequence: number }> }> = [];

    // Helper to build move list from items with flat sequence numbers (1, 2, 3...)
    const buildMoveList = (items: DraggableItem[]): Array<{ pronumber: number; legnum: number; sequence: number }> => {
      const moves: Array<{ pronumber: number; legnum: number; sequence: number }> = [];
      let sequence = 1;
      items.forEach((itm) => {
        itm.moves.forEach((m) => {
          moves.push({
            pronumber: m.pronumber,
            legnum: m.legnum,
            sequence: sequence++,
          });
        });
      });
      return moves;
    };

    // Calculate what the source driver's items would look like after removal
    // Skip this if same driver - we'll handle it in the target section
    if (sourceDriver && sourceDriver !== targetDriver) {
      const sourceDriverData = localDrivers.find(d => d.unitnumber === sourceDriver);
      if (sourceDriverData) {
        const itemsAfterRemoval = sourceDriverData.items.filter(i => i.id !== item.id);
        // Only validate if they still have moves
        if (itemsAfterRemoval.length > 0) {
          driversToValidate.push({
            unitnumber: sourceDriver,
            moves: buildMoveList(itemsAfterRemoval),
          });
        }
      }
    }

    // Calculate what the target driver's items would look like after addition (or reordering)
    if (targetDriver) {
      const targetDriverData = localDrivers.find(d => d.unitnumber === targetDriver);
      if (targetDriverData) {
        const currentItems = targetDriverData.items.filter(i => i.id !== item.id); // Remove if same driver
        let insertIndex = targetIndex;
        if (sourceDriver === targetDriver) {
          const oldIndex = targetDriverData.items.findIndex(i => i.id === item.id);
          if (oldIndex < targetIndex) {
            insertIndex = targetIndex - 1;
          }
        }
        const itemsAfterAddition = [...currentItems];
        itemsAfterAddition.splice(insertIndex, 0, item);
        driversToValidate.push({
          unitnumber: targetDriver,
          moves: buildMoveList(itemsAfterAddition),
        });
      }
    }

    // Call backend to validate schedules if we have drivers to validate
    if (driversToValidate.length > 0 && selectedRegion) {
      setValidatingSchedule(true);
      try {
        const validationResult = await preplanningService.validateSchedules({
          target_date: formatLocalDate(selectedDate),
          region_name: selectedRegion,
          driver_assignments: driversToValidate,
          buffer_minutes: prePlan?.buffer_minutes ?? 15,
        });

        if (!validationResult.valid) {
          // Show error and abort the drop
          setDropValidationError(validationResult.error || 'Schedule conflict: move cannot be placed at this position');
          setTimeout(() => setDropValidationError(null), 5000);
          setDraggedItem(null);
          setValidatingSchedule(false);
          return;
        }

        // Validation passed - update move timing from the response
        // We'll apply the timing updates after we complete the state changes below
        const timingUpdates = new Map<string, { estimated_start: string | null; estimated_end: string | null; travel_time_from_previous: number }>();
        for (const driverResult of validationResult.driver_results) {
          for (const moveResult of driverResult.moves) {
            const key = `${moveResult.pronumber}-${moveResult.legnum}`;
            timingUpdates.set(key, {
              estimated_start: moveResult.estimated_start,
              estimated_end: moveResult.estimated_end,
              travel_time_from_previous: moveResult.travel_time_from_previous,
            });
          }
        }

        // Apply timing updates to the item being moved
        const updatedItem: DraggableItem = {
          ...item,
          moves: item.moves.map(m => {
            const key = `${m.pronumber}-${m.legnum}`;
            const timing = timingUpdates.get(key);
            if (timing) {
              return {
                ...m,
                estimated_start: timing.estimated_start || undefined,
                estimated_end: timing.estimated_end || undefined,
                travel_time_from_previous: timing.travel_time_from_previous,
              };
            }
            return m;
          }),
        };

        // Helper to apply timing updates to items
        const applyTimingUpdates = (items: DraggableItem[]): DraggableItem[] => {
          return items.map(itm => ({
            ...itm,
            moves: itm.moves.map(m => {
              const key = `${m.pronumber}-${m.legnum}`;
              const timing = timingUpdates.get(key);
              if (timing) {
                return {
                  ...m,
                  estimated_start: timing.estimated_start || undefined,
                  estimated_end: timing.estimated_end || undefined,
                  travel_time_from_previous: timing.travel_time_from_previous,
                };
              }
              return m;
            }),
          }));
        };

        // Handle state updates based on source/target
        if (sourceDriver === targetDriver && sourceDriver) {
          // Same driver reorder - handle in a single state update
          setLocalDrivers(prev => prev.map(d => {
            if (d.unitnumber !== sourceDriver) return d;

            const oldIndex = d.items.findIndex(i => i.id === item.id);
            const currentItems = d.items.filter(i => i.id !== item.id);
            let insertIndex = targetIndex;
            if (oldIndex < targetIndex) {
              insertIndex = targetIndex - 1;
            }
            const newItems = [...currentItems];
            newItems.splice(insertIndex, 0, updatedItem);

            return {
              ...d,
              items: applyTimingUpdates(newItems),
            };
          }));
        } else {
          // Different drivers - remove from source, add to target
          if (sourceDriver === STAGING_SOURCE) {
            // Remove from staging
            setStagedMoves(prev => prev.filter(i => i.id !== item.id));
          } else if (sourceDriver) {
            setLocalDrivers(prev => prev.map(d => {
              if (d.unitnumber !== sourceDriver) return d;
              const newItems = d.items.filter(i => i.id !== item.id);
              return {
                ...d,
                items: applyTimingUpdates(newItems),
              };
            }));
          } else {
            setLocalUnassigned(prev => prev.filter(i => i.id !== item.id));
          }

          if (targetDriver) {
            setLocalDrivers(prev => prev.map(d => {
              if (d.unitnumber !== targetDriver) return d;

              const newItems = [...d.items];
              newItems.splice(targetIndex, 0, updatedItem);

              return {
                ...d,
                items: applyTimingUpdates(newItems),
              };
            }));
          } else {
            setLocalUnassigned(prev => {
              const newItems = [...prev];
              newItems.splice(targetIndex, 0, updatedItem);
              return newItems;
            });
          }
        }

        setValidatingSchedule(false);

      } catch (err) {
        console.error('Schedule validation failed:', err);
        setDropValidationError('Failed to validate schedule. Please try again.');
        setTimeout(() => setDropValidationError(null), 3000);
        setDraggedItem(null);
        setValidatingSchedule(false);
        return;
      }
    } else {
      // No drivers to validate (e.g., moving to/from unassigned, or no region selected)
      // Show warning if we skipped validation due to missing region
      if (!selectedRegion && (sourceDriver || targetDriver)) {
        console.warn('Skipping schedule validation: no region selected');
      }

      // Handle state updates
      if (sourceDriver === targetDriver && sourceDriver) {
        // Same driver reorder without validation
        setLocalDrivers(prev => prev.map(d => {
          if (d.unitnumber !== sourceDriver) return d;

          const oldIndex = d.items.findIndex(i => i.id === item.id);
          const currentItems = d.items.filter(i => i.id !== item.id);
          let insertIndex = targetIndex;
          if (oldIndex < targetIndex) {
            insertIndex = targetIndex - 1;
          }
          const newItems = [...currentItems];
          newItems.splice(insertIndex, 0, item);

          return {
            ...d,
            items: newItems,
          };
        }));
      } else {
        // Different source/target
        if (sourceDriver === STAGING_SOURCE) {
          // Remove from staging
          setStagedMoves(prev => prev.filter(i => i.id !== item.id));
        } else if (sourceDriver) {
          setLocalDrivers(prev => prev.map(d => {
            if (d.unitnumber !== sourceDriver) return d;
            return {
              ...d,
              items: d.items.filter(i => i.id !== item.id),
            };
          }));
        } else {
          setLocalUnassigned(prev => prev.filter(i => i.id !== item.id));
        }

        if (targetDriver) {
          setLocalDrivers(prev => prev.map(d => {
            if (d.unitnumber !== targetDriver) return d;

            const newItems = [...d.items];
            newItems.splice(targetIndex, 0, item);
            return {
              ...d,
              items: newItems,
            };
          }));
        } else {
          setLocalUnassigned(prev => {
            const newItems = [...prev];
            let insertIndex = targetIndex;
            // Adjust index if moving within unassigned list
            if (!sourceDriver) {
              const oldIndex = prev.findIndex(i => i.id === item.id);
              if (oldIndex >= 0 && oldIndex < targetIndex) {
                insertIndex = targetIndex - 1;
              }
            }
            newItems.splice(insertIndex, 0, item);
            return newItems;
          });
        }
      }
    }

    setDraggedItem(null);
  }, [draggedItem, localDrivers, localUnassigned, validateMoveForDriver, selectedDate, selectedRegion, prePlan]);

  // Debug click handler for Kanban view
  const handleKanbanDebugClick = useCallback((move: PlannedMove) => {
    // Find the driver plan for this move
    const driverPlan = prePlan?.driver_plans.find(dp =>
      dp.moves.some(m => m.pronumber === move.pronumber && m.legnum === move.legnum)
    );
    setDebugDrawerMove(move);
    setDebugDriverPlan(driverPlan || null);
    setIsDebugDrawerOpen(true);
  }, [prePlan]);

  // Quick assign handler - assign item directly to a driver by unit number
  const handleQuickAssign = useCallback((item: DraggableItem, targetUnitnumber: string, sourceDriver: string | null) => {
    // Find target driver
    const targetDriverData = localDrivers.find(d => d.unitnumber === targetUnitnumber);
    if (!targetDriverData) {
      setDropValidationError(`Driver ${targetUnitnumber} not found`);
      setTimeout(() => setDropValidationError(null), 3000);
      return;
    }

    // Don't allow moving to same driver
    if (sourceDriver === targetUnitnumber) {
      return;
    }

    // Validate move for target driver (qualification requirements)
    const validationError = validateMoveForDriver(item, targetDriverData);
    if (validationError) {
      setDropValidationError(validationError);
      setTimeout(() => setDropValidationError(null), 3000);
      return;
    }

    // Remove from source
    if (sourceDriver === STAGING_SOURCE) {
      setStagedMoves(prev => prev.filter(i => i.id !== item.id));
    } else if (sourceDriver) {
      setLocalDrivers(prev => prev.map(d => {
        if (d.unitnumber !== sourceDriver) return d;
        return {
          ...d,
          items: d.items.filter(i => i.id !== item.id),
        };
      }));
    } else {
      setLocalUnassigned(prev => prev.filter(i => i.id !== item.id));
    }

    // Add to target driver at the end
    setLocalDrivers(prev => prev.map(d => {
      if (d.unitnumber !== targetUnitnumber) return d;
      return {
        ...d,
        items: [...d.items, item],
      };
    }));
  }, [localDrivers, validateMoveForDriver]);

  // ============================================================================
  // Apply Plan Handler
  // ============================================================================

  const handleApplyPlan = useCallback(async () => {
    if (selectedDrivers.size === 0 || !prePlan) return;

    setApplyingPlan(true);
    setApplyResults(null);

    // Gather all moves from selected drivers with full details
    const moves: Array<{
      pronumber: number;
      legnum: number;
      driver: string;
      origcode?: number;
      destcode?: number;
      priority?: number;
    }> = [];

    for (const driver of localDrivers) {
      if (!selectedDrivers.has(driver.unitnumber)) continue;
      let priority = 1;
      for (const item of driver.items) {
        // Track if any move from this item was added (not already applied)
        let itemHasMoves = false;
        for (const move of item.moves) {
          // Skip already-applied moves (either in this session or by a previous preplan)
          if (appliedMoves.has(`${move.pronumber}-${move.legnum}`) || move.was_applied_by_preplan) continue;
          itemHasMoves = true;
          // All moves in the same item (dispatch group) share the same priority
          // THE PAIRING RIDES ALONG WHETHER OR NOT IT WAS TICKED. It is what tells
          // the server this is a proposed reuse rather than an ordinary leg, and the
          // server refuses to dispatch either way — the planned destination is the
          // export's shipper, so dispatching it without performing the reuse would
          // send a driver there with the import still expecting its empty back.
          const turnKey = `${move.pronumber}-${move.legnum}`;
          moves.push({
            pronumber: move.pronumber,
            legnum: move.legnum,
            driver: driver.unitnumber,
            origcode: move.origcode,
            destcode: move.destcode,
            priority,
            ...(move.street_turn_export
              ? {
                  street_turn_export: move.street_turn_export,
                  street_turn_export_legnum: move.street_turn_export_legnum,
                  perform_street_turn: turnsToPerform.has(turnKey),
                }
              : {}),
          });
        }
        // Only increment priority after processing an item that had moves
        if (itemHasMoves) {
          priority++;
        }
      }
    }

    // In batches, so the board fills in as it goes and the user can see how long is
    // left — see applyBatches for why moves sharing a PRO stay together.
    const rows: ApplyResultRow[] = [];
    let cmsResponse: unknown;
    setApplyProgress({ done: 0, total: moves.length, startedAt: Date.now() });
    try {
      for (const batch of applyBatches(moves)) {
        try {
          const result = await preplanningService.applyPreplan(batch, prePlan.target_date, prePlan.plan_id);
          rows.push(...result.results);
          cmsResponse = result.cms_response;
          const applied = result.results.filter(r => r.success);
          if (applied.length > 0) {
            setAppliedMoves(prev => {
              const next = new Set(prev);
              applied.forEach(m => next.add(`${m.pronumber}-${m.legnum}`));
              return next;
            });
          }
        } catch (err) {
          // One request failing outright is that batch's moves failing, not the apply:
          // the batches before it are written and the ones after it still go.
          console.error('Failed to apply plan batch:', err);
          const reason = applyError(err);
          rows.push(...batch.map(m => ({ pronumber: m.pronumber, legnum: m.legnum, driver: m.driver, success: false, error: reason })));
        }
        setApplyProgress(p => p && { ...p, done: p.done + batch.length });
      }

      const { success, blocked, failed } = splitApplyResults(rows);
      setApplyResults({ success, blocked, failed, cmsResponse });

      // Nothing left a retry could get past, so clear selection. Blocked moves stay on
      // the board unapplied until their stop is cleared.
      if (failed.length === 0) {
        setSelectedDrivers(new Set());
      }
    } finally {
      setApplyingPlan(false);
      setApplyProgress(null);
    }
  }, [selectedDrivers, localDrivers, prePlan, appliedMoves, turnsToPerform]);

  // ============================================================================
  // Remove From Driver Handler (for undoing preplan-applied moves)
  // ============================================================================

  // ============================================================================
  // Multi-Select Handlers (for bulk removal of applied moves)
  // ============================================================================

  // Toggle selection of a move
  const handleToggleMoveSelection = useCallback((driverUnitnumber: string, item: DraggableItem, selected: boolean) => {
    setSelectedMoves(prev => {
      const next = new Set(prev);
      // Each move in the item gets its own key
      for (const move of item.moves) {
        const key = `${driverUnitnumber}:${move.pronumber}-${move.legnum}`;
        if (selected) {
          next.add(key);
        } else {
          next.delete(key);
        }
      }
      return next;
    });
  }, []);

  // Moves in this apply that are currently somebody else's. A replan is allowed to take
  // work off a worse-placed driver — that is the mode doing its job — but it is the one
  // change that costs a person their day, so it is said out loud before anything writes.
  const reassignments = useMemo(() => {
    const byLoser = new Map<string, number>();
    for (const driver of localDrivers) {
      if (!selectedDrivers.has(driver.unitnumber)) continue;
      for (const item of driver.items) {
        for (const move of item.moves) {
          if (!move.reassigned_from) continue;
          if (appliedMoves.has(`${move.pronumber}-${move.legnum}`) || move.was_applied_by_preplan) continue;
          byLoser.set(move.reassigned_from, (byLoser.get(move.reassigned_from) || 0) + 1);
        }
      }
    }
    const moves = [...byLoser.values()].reduce((a, b) => a + b, 0);
    return { moves, drivers: byLoser.size };
  }, [localDrivers, selectedDrivers, appliedMoves]);

  // The street turns this apply could perform: proposed on the selected drivers, not
  // already applied. Derived rather than stored, so regenerating a plan or changing the
  // driver selection cannot leave a tick behind pointing at a pairing that is gone.
  const proposedTurns = useMemo(() => {
    const rows: Array<{ key: string; move: PlannedMove; driver: string }> = [];
    for (const driver of localDrivers) {
      if (!selectedDrivers.has(driver.unitnumber)) continue;
      for (const item of driver.items) {
        for (const move of item.moves) {
          if (!move.street_turn_export) continue;
          if (appliedMoves.has(`${move.pronumber}-${move.legnum}`) || move.was_applied_by_preplan) continue;
          rows.push({ key: `${move.pronumber}-${move.legnum}`, move, driver: driver.unitnumber });
        }
      }
    }
    return rows;
  }, [localDrivers, selectedDrivers, appliedMoves]);

  // A tick for a pairing that is no longer on offer must not survive to the next apply.
  useEffect(() => {
    setTurnsToPerform(prev => {
      const live = new Set(proposedTurns.map(t => t.key));
      const next = new Set([...prev].filter(k => live.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [proposedTurns]);

  // Clear all selections
  const handleClearSelections = useCallback(() => {
    setSelectedMoves(new Set());
  }, []);

  // Get count of selected moves
  const selectedMoveCount = selectedMoves.size;

  // Get selected items grouped by driver for processing
  const getSelectedItemsByDriver = useCallback(() => {
    const byDriver = new Map<string, Array<{ item: DraggableItem; moves: PlannedMove[] }>>();

    for (const key of selectedMoves) {
      const [driverUnitnumber, moveKey] = key.split(':');
      const [pronumberStr, legnumStr] = moveKey.split('-');
      const pronumber = parseInt(pronumberStr, 10);
      const legnum = parseInt(legnumStr, 10);

      // Find the driver and item
      const driver = localDrivers.find(d => d.unitnumber === driverUnitnumber);
      if (!driver) continue;

      const item = driver.items.find(it =>
        it.moves.some(m => m.pronumber === pronumber && m.legnum === legnum)
      );
      if (!item) continue;

      const move = item.moves.find(m => m.pronumber === pronumber && m.legnum === legnum);
      if (!move) continue;

      if (!byDriver.has(driverUnitnumber)) {
        byDriver.set(driverUnitnumber, []);
      }

      // Check if we already have this item in the list
      const existingEntry = byDriver.get(driverUnitnumber)!.find(e => e.item.id === item.id);
      if (existingEntry) {
        if (!existingEntry.moves.some(m => m.pronumber === pronumber && m.legnum === legnum)) {
          existingEntry.moves.push(move);
        }
      } else {
        byDriver.get(driverUnitnumber)!.push({ item, moves: [move] });
      }
    }

    return byDriver;
  }, [selectedMoves, localDrivers]);

  // Handler for bulk remove confirmation
  const handleConfirmBulkRemove = useCallback(async () => {
    if (!prePlan || selectedMoves.size === 0) return;

    setBulkRemoving(true);
    setBulkRemoveResults(null);

    let totalSuccess = 0;
    let totalFailed = 0;
    const errors: string[] = [];

    try {
      const byDriver = getSelectedItemsByDriver();

      // Process each driver's selected moves
      for (const [driverUnitnumber, entries] of byDriver) {
        // Collect all moves for this driver
        const allMoves: PlannedMove[] = [];
        for (const entry of entries) {
          allMoves.push(...entry.moves);
        }

        if (allMoves.length === 0) continue;

        // Check which moves need appointment clearing
        let movesToClearAppt: Set<string> = new Set();
        try {
          const unapplyResult = await preplanningService.markAssignmentsUnapplied(
            prePlan.plan_id,
            allMoves.map(m => ({ pronumber: m.pronumber, legnum: m.legnum }))
          );
          if (unapplyResult.moves_to_clear_appointment) {
            movesToClearAppt = new Set(
              unapplyResult.moves_to_clear_appointment.map(m => `${m.pronumber}-${m.legnum}`)
            );
          }
        } catch (err) {
          console.error('Failed to query/update preplan assignments:', err);
        }

        // Build items for removeFromDriver
        const items = allMoves.map(move => {
          const needsClearAppt = movesToClearAppt.has(`${move.pronumber}-${move.legnum}`);
          return {
            proNumber: move.pronumber,
            legnum: move.legnum,
            origcode: move.origcode || 0,
            destcode: move.destcode || 0,
            origApptDate: move.origin_appt_date || '',
            destApptDate: needsClearAppt ? '' : (move.dest_appt_date || ''),
            driver: '',  // Empty to remove driver
          };
        });

        // Call removeFromDriver
        const response = await ordersService.removeFromDriver(items);
        totalSuccess += response.success;
        totalFailed += response.failed;
        if (response.errors) {
          errors.push(...response.errors);
        }
      }

      setBulkRemoveResults({
        success: totalSuccess,
        failed: totalFailed,
        errors: errors.length > 0 ? errors : undefined,
      });

      // Remove successful moves from local state and add to staging
      if (totalSuccess > 0) {
        // First, collect the items that will be removed (before state update)
        const removedKeys = new Set<string>();
        const removedItems: DraggableItem[] = [];

        // Use getSelectedItemsByDriver which we already called
        for (const [driverUnitnumber, entries] of byDriver) {
          for (const entry of entries) {
            // Add all move keys from this item
            entry.item.moves.forEach(m => removedKeys.add(`${m.pronumber}-${m.legnum}`));
            // Collect the item for staging (avoid duplicates)
            if (!removedItems.some(ri => ri.id === entry.item.id)) {
              removedItems.push(entry.item);
            }
          }
        }

        // Now update local drivers to remove these items
        setLocalDrivers(prev => prev.map(driver => ({
          ...driver,
          items: driver.items.filter(item => {
            // Check if any move in this item was selected for removal
            const hasSelectedMove = item.moves.some(m =>
              selectedMoves.has(`${driver.unitnumber}:${m.pronumber}-${m.legnum}`)
            );
            return !hasSelectedMove; // Keep if NOT selected
          }),
        })));

        // Add removed items to staging drawer (with updated move status)
        if (removedItems.length > 0) {
          const stagedItems: DraggableItem[] = removedItems.map(removedItem => ({
            ...removedItem,
            moves: removedItem.moves.map(m => ({
              ...m,
              is_existing: false,
              was_applied_by_preplan: false,
              applied_plan_id: undefined,
            })),
          }));
          setStagedMoves(prev => [...prev, ...stagedItems]);
          setIsStagingOpen(true);
        }

        // Clear from appliedMoves Set
        setAppliedMoves(prev => {
          const next = new Set(prev);
          removedKeys.forEach(key => next.delete(key));
          return next;
        });

        // Clear selections
        setSelectedMoves(new Set());

        // Close modal after success
        setTimeout(() => {
          setShowBulkRemoveConfirm(false);
          setBulkRemoveResults(null);
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to bulk remove from drivers:', err);
      setBulkRemoveResults({
        success: 0,
        failed: selectedMoves.size,
        errors: [(err as Error).message],
      });
    } finally {
      setBulkRemoving(false);
    }
  }, [prePlan, selectedMoves, getSelectedItemsByDriver]);

  // Handler to open the Undo Preplanned modal
  const handleOpenUndoPreplannedModal = useCallback(async () => {
    if (!prePlan) return;

    setShowUndoPreplannedModal(true);
    setLoadingAppliedPlans(true);
    setSelectedPlansToUndo(new Set());
    setUndoPreplannedResults(null);

    try {
      const result = await preplanningService.getAppliedPlans(prePlan.target_date, prePlan.region_name);
      setAppliedPlans(result.plans);
    } catch (err) {
      console.error('Failed to load applied plans:', err);
      setAppliedPlans([]);
    } finally {
      setLoadingAppliedPlans(false);
    }
  }, [prePlan]);

  // Handler to execute bulk undo of preplanned moves
  const handleConfirmUndoPreplanned = useCallback(async () => {
    if (!prePlan || selectedPlansToUndo.size === 0) return;

    setUndoingPreplanned(true);
    setUndoPreplannedResults(null);

    let totalSuccess = 0;
    let totalFailed = 0;
    let totalStaleCleaned = 0;
    let totalRestored = 0;
    let totalKept = 0;
    let totalTurnsUndone = 0;
    const errors: string[] = [];
    // Collect all successfully removed moves for staging
    const successfullyRemovedMoves: Array<{
      pronumber: number;
      legnum: number;
      driver: string;
      origin_name: string;
      origin_city: string;
      dest_name: string;
      dest_city: string;
      dest_appt_date: string | null;
      dest_appt_time1: string;
      dest_appt_time2: string;
      origcode: number;
      destcode: number;
      dispatch_group_id: string | null;
    }> = [];

    try {
      // Pre-fetch all plan moves and check deadline criteria before processing
      const allPlanMoves: Map<string, Awaited<ReturnType<typeof preplanningService.getAppliedPlanMoves>>> = new Map();
      for (const planId of selectedPlansToUndo) {
        try {
          const planMoves = await preplanningService.getAppliedPlanMoves(planId);
          allPlanMoves.set(planId, planMoves);
        } catch (err) {
          console.error(`Failed to fetch moves for plan ${planId}:`, err);
          errors.push(`Plan ${planId}: ${(err as Error).message}`);
        }
      }

      // Undo puts a move back the way it was before the apply, which is not always
      // "no driver" (planner feedback, 2026-10-28): a leg the plan took from another
      // driver goes back to them, and a street turn the plan performed is reversed.
      const undoByPlan = new Map(Array.from(allPlanMoves, ([planId, planMoves]) =>
        [planId, planUndo(planMoves.moves || [], mayUndoTurns)] as const));

      // Only a move left with nobody on it can miss a deadline.
      const allMoves = Array.from(undoByPlan.values()).flatMap(undo => [...undo.clear, ...undo.turn]);
      let deadlineNote: string | null = null;
      let qualifyingProSet = new Set<number>();
      let deadlineResult: Awaited<ReturnType<typeof checkAndPrompt>> = null;

      if (allMoves.length > 0) {
        deadlineResult = await checkAndPrompt(
          allMoves.map(move => ({
            proNumber: move.pronumber,
            legnum: move.legnum,
            oldDriver: move.driver || '',
            newDriver: '',
          }))
        );

        if (deadlineResult === null) {
          // User cancelled the note modal
          setUndoingPreplanned(false);
          return;
        }

        // Build enriched note: "Category - Reason - Notes" or "Category - Notes"
        deadlineNote = deadlineResult.note
          ? [deadlineResult.category_name, deadlineResult.reason_name, deadlineResult.note].filter(Boolean).join(' - ')
          : null;
        qualifyingProSet = new Set(deadlineResult.qualifyingPros);
      }

      // Process each selected plan
      for (const planId of selectedPlansToUndo) {
        try {
          const planMoves = allPlanMoves.get(planId);
          const undo = undoByPlan.get(planId);
          if (!planMoves || !undo) continue;

          // Track stale moves that were auto-cleaned
          if (planMoves.stale_cleaned) {
            totalStaleCleaned += planMoves.stale_cleaned;
          }

          // If no active moves to remove, continue to next plan
          if (planMoves.moves.length === 0) continue;

          // Already this driver's before the apply: nothing to put back.
          const undone: UndoableMove[] = [...undo.keep];
          totalKept += undo.keep.length;

          // The driver comes off the moves nobody held, and off each turn's export leg.
          const offTheirDriver = [...undo.clear, ...undo.turn];
          if (offTheirDriver.length > 0) {
            // Build items for removeFromDriver using current move data from CMS
            const items = offTheirDriver.map(move => ({
              proNumber: move.pronumber,
              legnum: move.legnum,
              origcode: move.origcode,
              destcode: move.destcode,
              origApptDate: move.orig_appt_date || '',
              destApptDate: move.clear_appointment ? '' : (move.dest_appt_date || ''),
              driver: '',  // Empty to remove driver
              oldDriver: move.driver || '',
              ...(deadlineNote && qualifyingProSet.has(move.pronumber) ? { notes: deadlineNote } : {}),
            }));

            // Call removeFromDriver
            const response = await ordersService.removeFromDriver(items);
            totalSuccess += response.success;
            totalFailed += response.failed;
            if (response.errors) {
              errors.push(...response.errors);
            }

            const removed = new Set(driverRemoved(offTheirDriver, response).map(m => m.assignment_id));
            for (const move of undo.clear) {
              if (!removed.has(move.assignment_id)) continue;
              undone.push(move);
              successfullyRemovedMoves.push({
                pronumber: move.pronumber,
                legnum: move.legnum,
                driver: move.driver,
                origin_name: move.origin_name,
                origin_city: move.origin_city,
                dest_name: move.dest_name,
                dest_city: move.dest_city,
                dest_appt_date: move.dest_appt_date,
                dest_appt_time1: move.dest_appt_time1,
                dest_appt_time2: move.dest_appt_time2,
                origcode: move.origcode,
                destcode: move.destcode,
                dispatch_group_id: move.dispatch_group_id,
              });
            }

            // Reverse each turn whose driver came off, through the same undo as the
            // order screen's Undo Reuse. Never under a driver still on the leg: the
            // undo moves the export's pickup back to where the box was.
            for (const move of undo.turn) {
              if (!removed.has(move.assignment_id)) continue;
              // The plan's driver is off it whether or not the reuse comes back.
              undone.push(move);
              try {
                await ordersService.undoReuseContainer(move.pronumber);
                totalTurnsUndone += 1;
              } catch (err) {
                errors.push(
                  `Street turn ${move.street_turn_import} → ${move.pronumber}: the driver was removed but the ` +
                  `reuse was not undone (${reuseError(err)}). Use Undo Reuse on PRO ${move.pronumber}.`
                );
              }
            }
          }

          // A leg taken from another driver goes back to them through the apply's own
          // dispatch. Handing a leg to a different driver needs a new dispatch minted,
          // which removeFromDriver cannot do.
          if (undo.restore.length > 0) {
            try {
              const result = await preplanningService.applyPreplan(undo.restore.map(move => ({
                pronumber: move.pronumber,
                legnum: move.legnum,
                driver: (move.original_driver ?? '').trim(),
                // 0 is "unknown" here, and the apply writes any code it is sent.
                origcode: move.origcode || undefined,
                destcode: move.destcode || undefined,
              })));
              const back = new Set(result.results.filter(r => r.success).map(r => `${r.pronumber}-${r.legnum}`));
              for (const move of undo.restore) {
                if (!back.has(`${move.pronumber}-${move.legnum}`)) continue;
                undone.push(move);
                totalRestored += 1;
              }
              totalFailed += result.failed;
              for (const r of result.results.filter(r => !r.success)) {
                errors.push(`PRO ${r.pronumber} leg ${r.legnum} did not go back to driver ${r.driver}${r.error ? `: ${r.error}` : ''}`);
              }
            } catch (err) {
              totalFailed += undo.restore.length;
              errors.push(`${undo.restore.length} move${undo.restore.length !== 1 ? 's' : ''} did not go back to their original drivers: ${(err as Error).message}`);
            }
          }

          for (const move of undo.turn_not_permitted) {
            errors.push(`Street turn ${move.street_turn_import} → ${move.pronumber} was left in place: you do not have permission to undo container reuses.`);
          }

          // Mark assignments as unapplied in the preplan table
          if (undone.length > 0) {
            try {
              await preplanningService.markAssignmentsUnapplied(
                planId,
                undone.map(m => ({ pronumber: m.pronumber, legnum: m.legnum, assignment_id: m.assignment_id }))
              );
            } catch (err) {
              console.error(`Failed to mark assignments as unapplied for plan ${planId}:`, err);
            }
          }
        } catch (err) {
          console.error(`Failed to process plan ${planId}:`, err);
          errors.push(`Plan ${planId}: ${(err as Error).message}`);
        }
      }

      // Record dispatch removal audit entries
      if (totalSuccess > 0 && deadlineResult?.note && deadlineResult.qualifyingDetails.length > 0) {
        dispatchRemovalService.recordRemovals(
          deadlineResult.qualifyingDetails.map(d => ({
            category_id: deadlineResult!.category_id!,
            reason_id: deadlineResult!.reason_id,
            notes: deadlineResult!.note!,
            pronumber: d.pronumber,
            driver: d.driver,
            dispatch_id: d.dispatch_id,
            appt_date: d.appt_date,
            deadline_type: d.deadline_type,
            deadline_date: d.deadline_date,
          }))
        ).catch(err => console.error('Failed to record dispatch removals:', err));
      }

      setUndoPreplannedResults({
        success: totalSuccess,
        failed: totalFailed,
        staleCleaned: totalStaleCleaned > 0 ? totalStaleCleaned : undefined,
        restored: totalRestored > 0 ? totalRestored : undefined,
        kept: totalKept > 0 ? totalKept : undefined,
        turnsUndone: totalTurnsUndone > 0 ? totalTurnsUndone : undefined,
        errors: errors.length > 0 ? errors : undefined,
      });

      // Remove the undone moves from local state and appliedMoves Set
      // Also handle case where only stale moves were cleaned (nothing to remove from CMS)
      if (totalSuccess > 0 || totalRestored > 0 || totalKept > 0 || totalStaleCleaned > 0) {
        // Build a set of move keys that were successfully removed
        const removedMoveKeys = new Set(
          successfullyRemovedMoves.map(m => `${m.pronumber}-${m.legnum}`)
        );

        setLocalDrivers(prev => prev.map(driver => ({
          ...driver,
          items: driver.items.filter(item => {
            // Check if this item was applied by a selected plan (previous preplans)
            const appliedBySelectedPlan = item.moves.some(m =>
              m.was_applied_by_preplan && m.applied_plan_id && selectedPlansToUndo.has(m.applied_plan_id)
            );

            // Check if this item was applied in the current session and current plan is selected
            const appliedInCurrentSession = prePlan && selectedPlansToUndo.has(prePlan.plan_id) &&
              item.moves.some(m => appliedMoves.has(`${m.pronumber}-${m.legnum}`));

            // Remove if it was from a selected plan
            if (appliedBySelectedPlan || appliedInCurrentSession) {
              return false;
            }

            return true;
          }),
        })));

        // Clear undone moves from the appliedMoves Set
        if (removedMoveKeys.size > 0) {
          setAppliedMoves(prev => {
            const next = new Set(prev);
            removedMoveKeys.forEach(key => next.delete(key));
            return next;
          });
        }

        // Add removed moves to staging drawer, grouping by dispatch_group_id
        if (successfullyRemovedMoves.length > 0) {
          const stagedItems: DraggableItem[] = [];
          const processedGroups = new Set<string>();

          for (const move of successfullyRemovedMoves) {
            // Check if this move is part of a dispatch group with other moves
            if (move.dispatch_group_id) {
              // Skip if we've already processed this group
              if (processedGroups.has(move.dispatch_group_id)) continue;
              processedGroups.add(move.dispatch_group_id);

              // Find all moves in this group
              const groupMoves = successfullyRemovedMoves.filter(
                m => m.dispatch_group_id === move.dispatch_group_id
              );

              // If only one move in the "group", treat it as a single move
              if (groupMoves.length === 1) {
                stagedItems.push({
                  id: `${move.pronumber}-${move.legnum}`,
                  isGroup: false,
                  moves: [{
                    pronumber: move.pronumber,
                    legnum: move.legnum,
                    sequence: 1,
                    origin_name: move.origin_name,
                    origin_city: move.origin_city,
                    dest_name: move.dest_name,
                    dest_city: move.dest_city,
                    dest_appt_date: move.dest_appt_date || undefined,
                    dest_appt_time1: move.dest_appt_time1 || undefined,
                    dest_appt_time2: move.dest_appt_time2 || undefined,
                    origcode: move.origcode,
                    destcode: move.destcode,
                    dispatch_group_id: move.dispatch_group_id,
                    estimated_duration_minutes: 0,
                    travel_time_from_previous: 0,
                    is_existing: false,
                    was_applied_by_preplan: false,
                    has_hard_appointment: false,
                    legdrop: 'N',
                  }],
                });
              } else {
                // Multiple moves in the group - create a grouped item
                // Sort by legnum to maintain order
                groupMoves.sort((a, b) => a.legnum - b.legnum);

                stagedItems.push({
                  id: `group_${move.dispatch_group_id}`,
                  isGroup: true,
                  dispatch_group_id: move.dispatch_group_id,
                  moves: groupMoves.map((m, idx) => ({
                    pronumber: m.pronumber,
                    legnum: m.legnum,
                    sequence: idx + 1,
                    origin_name: m.origin_name,
                    origin_city: m.origin_city,
                    dest_name: m.dest_name,
                    dest_city: m.dest_city,
                    dest_appt_date: m.dest_appt_date || undefined,
                    dest_appt_time1: m.dest_appt_time1 || undefined,
                    dest_appt_time2: m.dest_appt_time2 || undefined,
                    origcode: m.origcode,
                    destcode: m.destcode,
                    dispatch_group_id: m.dispatch_group_id || undefined,
                    estimated_duration_minutes: 0,
                    travel_time_from_previous: 0,
                    is_existing: false,
                    was_applied_by_preplan: false,
                    has_hard_appointment: false,
                    legdrop: 'N',
                  })),
                });
              }
            } else {
              // No dispatch group - single move
              stagedItems.push({
                id: `${move.pronumber}-${move.legnum}`,
                isGroup: false,
                moves: [{
                  pronumber: move.pronumber,
                  legnum: move.legnum,
                  sequence: 1,
                  origin_name: move.origin_name,
                  origin_city: move.origin_city,
                  dest_name: move.dest_name,
                  dest_city: move.dest_city,
                  dest_appt_date: move.dest_appt_date || undefined,
                  dest_appt_time1: move.dest_appt_time1 || undefined,
                  dest_appt_time2: move.dest_appt_time2 || undefined,
                  origcode: move.origcode,
                  destcode: move.destcode,
                  estimated_duration_minutes: 0,
                  travel_time_from_previous: 0,
                  is_existing: false,
                  was_applied_by_preplan: false,
                  has_hard_appointment: false,
                  legdrop: 'N',
                }],
              });
            }
          }

          setStagedMoves(prev => [...prev, ...stagedItems]);
          setIsStagingOpen(true);
        }

        // Close modal after success
        setTimeout(() => {
          setShowUndoPreplannedModal(false);
          setUndoPreplannedResults(null);
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to undo preplanned moves:', err);
      setUndoPreplannedResults({
        success: 0,
        failed: totalFailed,
        errors: [(err as Error).message],
      });
    } finally {
      setUndoingPreplanned(false);
    }
  }, [prePlan, selectedPlansToUndo, appliedMoves, mayUndoTurns]);

  // Set icon legend sections for this page
  useEffect(() => {
    setLegendSections(dispatchLegendSections);
    return () => {
      setLegendSections([]);
    };
  }, [setLegendSections]);

  // Set up footer button for Apply Plan
  useEffect(() => {
    // Calculate unapplied move count for selected drivers
    const getUnappliedMoveCount = () => {
      return localDrivers
        .filter(d => selectedDrivers.has(d.unitnumber))
        .reduce((sum, d) => sum + d.items.reduce((itemSum, item) =>
          itemSum + item.moves.filter(m => !appliedMoves.has(`${m.pronumber}-${m.legnum}`) && !m.was_applied_by_preplan).length, 0), 0);
    };

    // Count moves that are applied (either in current session or by previous preplans)
    const getAppliedMoveCount = () => {
      return localDrivers.reduce((sum, d) => sum + d.items.reduce((itemSum, item) =>
        itemSum + item.moves.filter(m => appliedMoves.has(`${m.pronumber}-${m.legnum}`) || m.was_applied_by_preplan).length, 0), 0);
    };

    const unappliedCount = getUnappliedMoveCount();
    const appliedCount = getAppliedMoveCount();
    // Drivers with changes = have unapplied moves or rearranged order (selectable for Apply)
    const driversWithChanges = localDrivers.filter(d => {
      if (d.items.length === 0) return false;
      const originalPlan = prePlan?.driver_plans.find(dp => dp.unitnumber === d.unitnumber);
      return driverHasChanges(d, originalPlan, appliedMoves);
    });
    const allSelected = selectedDrivers.size > 0 && selectedDrivers.size === driversWithChanges.length;

    if (prePlan) {
      setDispatchButton(
        <div className="flex items-center justify-between w-full">
          {/* Left side - Select All and Undo Preplanned */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDrivers(new Set(driversWithChanges.map(d => d.unitnumber)));
                  } else {
                    setSelectedDrivers(new Set());
                  }
                }}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700"
              />
              <span>
                Select All
                <span className="ml-1 text-green-600 dark:text-green-400 font-medium">
                  ({selectedDrivers.size}/{driversWithChanges.length})
                </span>
              </span>
            </label>

            {/* Remove Selected button - show if moves are selected */}
            {selectedMoves.size > 0 && (
              <button
                onClick={() => setShowBulkRemoveConfirm(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
                Remove Selected ({selectedMoves.size})
              </button>
            )}

            {/* Undo Applied button - show if there are applied moves (current session or previous preplans) */}
            {appliedCount > 0 && (
              <button
                onClick={handleOpenUndoPreplannedModal}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Undo Applied ({appliedCount})
              </button>
            )}
          </div>

          {/* Right side - Selection info and Apply button */}
          {selectedDrivers.size > 0 && (
            <div className="flex items-center gap-4">
              {applyProgress ? (
                <div className="flex items-center gap-2 text-sm text-label" role="status" aria-live="polite">
                  <div className="w-40 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div
                      className="h-full bg-green-600 transition-all duration-500"
                      style={{ width: `${progressPercent(applyProgress)}%` }}
                    />
                  </div>
                  <span>
                    Applying <span className="font-medium text-heading">{applyProgress.done}</span> of {applyProgress.total}
                    {' · '}{timeLeft(applyProgress) ?? 'estimating time left…'}
                  </span>
                </div>
              ) : (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium text-heading">{selectedDrivers.size}</span>
                  {' '}driver{selectedDrivers.size !== 1 ? 's' : ''} selected
                  {' '}({unappliedCount} moves to apply)
                </div>
              )}
              <button
                onClick={() => setSelectedDrivers(new Set())}
                // Clearing mid-apply would hide the progress with the selection.
                disabled={applyingPlan}
                className="px-3 py-1.5 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Clear
              </button>
              <button
                onClick={() => setShowApplyConfirmDialog(true)}
                disabled={applyingPlan || unappliedCount === 0}
                className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                {applyingPlan ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <DocumentCheckIcon className="h-4 w-4" />
                )}
                Apply Plan
              </button>
            </div>
          )}
        </div>
      );
    } else {
      setDispatchButton(null);
    }

    return () => setDispatchButton(null);
  }, [prePlan, selectedDrivers, localDrivers, appliedMoves, applyingPlan, applyProgress, handleApplyPlan, handleOpenUndoPreplannedModal, setDispatchButton, selectedMoves]);

  // ============================================================================
  // Export Plan Handler
  // ============================================================================

  const handleExportPlan = useCallback(() => {
    if (!prePlan) return;

    const lines: string[] = [];

    // Header Section
    lines.push('SMART PREPLAN EXPORT');
    lines.push('');
    lines.push(`Generated At,${prePlan.generated_at}`);
    lines.push(`Target Date,${prePlan.target_date}`);
    lines.push(`Region,${prePlan.region_name}`);
    lines.push('');

    // Statistics Section
    lines.push('PLAN STATISTICS');
    lines.push(`Total Moves,${prePlan.statistics.total_moves}`);
    lines.push(`Assigned Moves,${prePlan.statistics.assigned_moves}`);
    lines.push(`Unassigned Moves,${prePlan.statistics.unassigned_moves}`);
    lines.push(`Drivers with Assignments,${prePlan.statistics.drivers_with_assignments}`);
    lines.push(`Average Moves per Driver,${prePlan.statistics.average_moves_per_driver.toFixed(2)}`);
    lines.push(`Average Assignment Score,${(prePlan.statistics.average_assignment_score * 100).toFixed(1)}%`);
    lines.push('');

    // Driver Assignments Section
    lines.push('DRIVER ASSIGNMENTS');
    lines.push('Driver Unit,Driver Name,Driver Type,PRO Number,Leg,Assignment Status,Sequence,Score,Origin,Destination,Appt Date,Appt Time,Move Type,Requires Hazmat,Requires Tank,Requires 93K,Requires Daycab');

    // Use localDrivers if available (reflects user modifications), otherwise use prePlan
    const driversToExport = localDrivers.length > 0
      ? localDrivers.map(ld => {
          // Find original driver plan for additional info
          const origDriver = prePlan.driver_plans.find(d => d.unitnumber === ld.unitnumber);
          return {
            unitnumber: ld.unitnumber,
            driver_name: ld.driver_name,
            isMule: ld.isMule,
            moves: ld.items.flatMap(item => item.moves)
          };
        })
      : prePlan.driver_plans.map(dp => ({
          unitnumber: dp.unitnumber,
          driver_name: dp.driver_name,
          isMule: dp.isMule,
          moves: dp.moves
        }));

    for (const driver of driversToExport) {
      for (const move of driver.moves) {
        const assignmentStatus = move.is_existing ? 'Already Assigned' : 'Smart PrePlan Added';
        const score = move.assignment_score !== undefined ? `${(move.assignment_score * 100).toFixed(1)}%` : 'N/A';
        const apptDate = move.dest_appt_date || '';
        const apptTime = move.dest_appt_time1 ? `${move.dest_appt_time1}${move.dest_appt_time2 ? '-' + move.dest_appt_time2 : ''}` : '';

        // Escape any commas in text fields
        const escapeCsv = (val: string) => val.includes(',') ? `"${val}"` : val;

        lines.push([
          driver.unitnumber,
          escapeCsv(driver.driver_name),
        driver.isMule ? 'Mule' : 'Road',
          move.pronumber,
          move.legnum,
          assignmentStatus,
          move.sequence,
          score,
          escapeCsv(`${move.origin_name} - ${move.origin_city}`),
          escapeCsv(`${move.dest_name} - ${move.dest_city}`),
          apptDate,
          apptTime,
          move.movetype || '',
          move.requires_hazmat ? 'Yes' : 'No',
          move.requires_tank ? 'Yes' : 'No',
          move.requires_93k ? 'Yes' : 'No',
          move.requires_daycab ? 'Yes' : 'No',
        ].join(','));
      }
    }

    lines.push('');

    // Driver Summary Section
    lines.push('DRIVER SUMMARY');
    lines.push('Driver Unit,Driver Name,Driver Type,Total Moves,Already Assigned,Smart PrePlan Added,Estimated Day Pay,Existing Moves Pay,New Moves Pay,HOS Available Hours,HOS Exceeds Available,Schedule Warnings');

    for (const driver of prePlan.driver_plans) {
      const warnings = driver.schedule_warnings?.join('; ') || '';
      const escapeCsv = (val: string) => val.includes(',') ? `"${val}"` : val;

      lines.push([
        driver.unitnumber,
        escapeCsv(driver.driver_name),
        driver.isMule ? 'Mule' : 'Road',
        driver.total_moves,
        driver.existing_moves,
        driver.new_moves,
        driver.estimated_day_pay?.toFixed(2) || '',
        driver.existing_moves_pay?.toFixed(2) || '',
        driver.new_moves_pay?.toFixed(2) || '',
        driver.hos_available_hours?.toFixed(2) || '',
        driver.hos_exceeds_available ? 'Yes' : 'No',
        escapeCsv(warnings),
      ].join(','));
    }

    lines.push('');

    // Unassigned Moves Section
    lines.push('UNASSIGNED MOVES');
    lines.push('PRO Number,Leg,Origin,Destination,Appt Date,Appt Time,Drivers Checked,Reasons,Suggestions');

    // Use localUnassigned if available (reflects user modifications), otherwise use prePlan
    const unassignedToExport = localUnassigned.length > 0
      ? localUnassigned.flatMap(item => item.moves.map(move => ({
          pronumber: move.pronumber,
          legnum: move.legnum,
          origin_name: move.origin_name,
          origin_city: move.origin_city,
          dest_name: move.dest_name,
          dest_city: move.dest_city,
          dest_appt_date: move.dest_appt_date || '',
          dest_appt_time1: move.dest_appt_time1,
          dest_appt_time2: move.dest_appt_time2,
          // Find original unassigned move for reasons
          ...prePlan.unassigned_moves.find(u => u.pronumber === move.pronumber && u.legnum === move.legnum)
        })))
      : prePlan.unassigned_moves;

    for (const move of unassignedToExport) {
      const apptTime = move.dest_appt_time1 ? `${move.dest_appt_time1}${move.dest_appt_time2 ? '-' + move.dest_appt_time2 : ''}` : '';
      const reasons = (move as { reasons?: Array<{ description: string }> }).reasons?.map(r => r.description).join('; ') || '';
      const suggestions = (move as { suggestions?: string[] }).suggestions?.join('; ') || '';
      const driversChecked = (move as { total_drivers_checked?: number }).total_drivers_checked || 0;

      const escapeCsv = (val: string) => val.includes(',') || val.includes(';') ? `"${val}"` : val;

      lines.push([
        move.pronumber,
        move.legnum,
        escapeCsv(`${move.origin_name} - ${move.origin_city}`),
        escapeCsv(`${move.dest_name} - ${move.dest_city}`),
        move.dest_appt_date,
        apptTime,
        driversChecked,
        escapeCsv(reasons),
        escapeCsv(suggestions),
      ].join(','));
    }

    // Download the CSV
    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    void saveBlob(blob, `smart_preplan_${prePlan.region_name}_${prePlan.target_date}.csv`);
  }, [prePlan, localDrivers, localUnassigned]);

  // ============================================================================
  // AI Analysis Handler
  // ============================================================================

  const handleAnalyzeWithAI = useCallback(async () => {
    if (!prePlan) return;

    setAnalyzingPlan(true);
    setAiAnalysis(null);
    try {
      // Build current state from local editable data (reflects user modifications)
      const currentDriverPlans = buildCurrentDriverPlans(localDrivers);
      const currentUnassignedMoves = buildCurrentUnassignedMoves(localUnassigned);

      const result = await preplanningService.analyzePrePlan({
        plan_id: prePlan.plan_id,
        target_date: prePlan.target_date,
        region_name: prePlan.region_name,
        config_used: prePlan.config_used as unknown as Record<string, unknown>,
        statistics: prePlan.statistics as unknown as Record<string, unknown>,
        driver_plans: currentDriverPlans,
        unassigned_moves: currentUnassignedMoves,
        available_drivers: prePlan.available_drivers as unknown as Record<string, unknown>[],
        model: selectedAiModel,
      });
      setAiAnalysis(result);
      setShowAiAnalysis(true);
    } catch (err: unknown) {
      console.error('Failed to analyze pre-plan:', err);
      // Extract error message from API response if available
      const errorMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || (err instanceof Error ? err.message : 'Unknown error');
      setError(`Failed to analyze pre-plan: ${errorMessage}`);
    } finally {
      setAnalyzingPlan(false);
    }
  }, [prePlan, selectedAiModel, localDrivers, localUnassigned]);

  // AI Optimization Handler
  const handleOptimizeWithAI = useCallback(async () => {
    if (!prePlan) return;

    setOptimizing(true);
    setAiOptimizations(null);
    try {
      // Build current state from local editable data (reflects user modifications)
      const currentDriverPlans = buildCurrentDriverPlans(localDrivers);
      const currentUnassignedMoves = buildCurrentUnassignedMoves(localUnassigned);

      const result = await preplanningService.getAIOptimizations({
        plan_id: prePlan.plan_id,
        target_date: prePlan.target_date,
        region_name: prePlan.region_name,
        config_used: prePlan.config_used as unknown as Record<string, unknown>,
        statistics: prePlan.statistics as unknown as Record<string, unknown>,
        driver_plans: currentDriverPlans,
        unassigned_moves: currentUnassignedMoves,
        available_drivers: prePlan.available_drivers as unknown as Record<string, unknown>[],
        model: selectedAiModel,
      });
      setAiOptimizations(result);
      setShowOptimizations(true);
    } catch (err: unknown) {
      console.error('Failed to get AI optimizations:', err);
      const errorMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || (err instanceof Error ? err.message : 'Unknown error');
      setError(`Failed to get AI optimizations: ${errorMessage}`);
    } finally {
      setOptimizing(false);
    }
  }, [prePlan, selectedAiModel, localDrivers, localUnassigned]);

  // Apply AI Optimization suggestion
  const handleApplyOptimization = useCallback((suggestion: {
    type: 'assign' | 'swap' | 'remove' | 'warning';
    move_pro?: string;
    move_legnum?: number;
    suggested_driver?: string;
    from_driver?: string;
    to_driver?: string;
  }) => {
    if (!suggestion.move_pro) return;

    // Build the item ID from PRO number
    const itemId = suggestion.move_legnum
      ? `${suggestion.move_pro}-${suggestion.move_legnum}`
      : suggestion.move_pro;

    // Find the item and its source driver
    let sourceDriver: string | null = null;
    let item: DraggableItem | null = null;

    // Check in localDrivers
    for (const driver of localDrivers) {
      const foundItem = driver.items.find(i =>
        i.id === itemId || i.moves.some(m =>
          String(m.pronumber) === suggestion.move_pro &&
          (!suggestion.move_legnum || m.legnum === suggestion.move_legnum)
        )
      );
      if (foundItem) {
        sourceDriver = driver.unitnumber;
        item = foundItem;
        break;
      }
    }

    // Check in unassigned if not found
    if (!item) {
      const foundItem = localUnassigned.find(i =>
        i.id === itemId || i.moves.some(m =>
          String(m.pronumber) === suggestion.move_pro &&
          (!suggestion.move_legnum || m.legnum === suggestion.move_legnum)
        )
      );
      if (foundItem) {
        sourceDriver = null;
        item = foundItem;
      }
    }

    if (!item) {
      console.warn('Could not find item for suggestion:', suggestion);
      setError(`Could not find move PRO ${suggestion.move_pro}`);
      return;
    }

    // Determine target driver
    const targetDriver = suggestion.type === 'assign'
      ? suggestion.suggested_driver || null
      : suggestion.type === 'swap'
      ? suggestion.to_driver || null
      : null; // 'remove' goes to unassigned

    // Validate move for target driver
    if (targetDriver) {
      const targetDriverPlan = localDrivers.find(d => d.unitnumber === targetDriver) || null;
      const validationError = validateMoveForDriver(item, targetDriverPlan);
      if (validationError) {
        setDropValidationError(validationError);
        setTimeout(() => setDropValidationError(null), 3000);
        return;
      }
    }

    // Remove from source
    if (sourceDriver) {
      setLocalDrivers(prev => prev.map(d => {
        if (d.unitnumber !== sourceDriver) return d;
        return {
          ...d,
          items: d.items.filter(i => i.id !== item!.id),
        };
      }));
    } else {
      setLocalUnassigned(prev => prev.filter(i => i.id !== item!.id));
    }

    // Add to target
    if (targetDriver) {
      setLocalDrivers(prev => prev.map(d => {
        if (d.unitnumber !== targetDriver) return d;
        return {
          ...d,
          items: [...d.items, item!],
        };
      }));
    } else {
      // Move to unassigned
      setLocalUnassigned(prev => [...prev, item!]);
    }

    // Remove this suggestion from the list (mark as applied)
    setAiOptimizations(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        suggestions: prev.suggestions.filter(s =>
          !(s.move_pro === suggestion.move_pro && s.move_legnum === suggestion.move_legnum && s.type === suggestion.type)
        ),
      };
    });
  }, [localDrivers, localUnassigned, validateMoveForDriver]);

  // Statistics
  const stats = prePlan?.statistics;

  return (
    <div className="flex flex-col h-full">
      {/* Header Section */}
      <PermissionsContainer componentId="smart-preplanning-header" componentName="Pre-Planning Controls">
      <div className="flex flex-wrap items-center gap-4 px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <DateTimePicker
          mode="date"
          selectionType="single"
          value={selectedDate}
          onChange={(val) => val instanceof Date && setSelectedDate(val)}
          minDate={new Date(new Date().setHours(0, 0, 0, 0))}
          labelType="none"
          autoApply={true}
          openDirection="down"
          usePortal={true}
        />
        <div className="relative w-44 z-10">
          <PermissionAwareSelect
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            disabled={regionsLoading}
            className="w-full h-[38px] px-3 pr-8 text-sm bg-panel border border-gray-300 dark:border-gray-600 rounded-lg text-heading focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed appearance-none"
          >
            <option value="">{regionsLoading ? 'Loading...' : 'Select Region'}</option>
            {regionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </PermissionAwareSelect>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            {regionsLoading ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            )}
          </div>
        </div>
        <div className="relative w-36 z-10">
          <PermissionAwareSelect
            value={selectedPreference}
            onChange={(e) => setSelectedPreference(e.target.value as 'All' | 'OTR' | 'Local' | 'Pre-Pulls')}
            className="w-full h-[38px] px-3 pr-8 text-sm bg-panel border border-gray-300 dark:border-gray-600 rounded-lg text-heading focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none"
          >
            <option value="All">All</option>
            <option value="OTR">OTR</option>
            <option value="Local">Local</option>
            <option value="Pre-Pulls">Pre-Pulls</option>
          </PermissionAwareSelect>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-heading" title="Include already assigned moves and reconsider drivers within the selected date, region, and filters. Existing dispatches change only when you apply the plan.">
          <input
            type="checkbox"
            role="switch"
            checked={includeAssignedMoves}
            onChange={(event) => setIncludeAssignedMoves(event.target.checked)}
            disabled={loading}
            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          Replan assigned moves
        </label>
        <PermissionAwareButton
          onClick={handleGenerate}
          disabled={loading || !selectedRegion}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
          ) : (
            <SparklesIcon className="h-4 w-4" />
          )}
          Generate Plan
        </PermissionAwareButton>

        {/* Export Plan Button - shows when plan is generated */}
        {prePlan && (
          <PermissionAwareButton
            onClick={handleExportPlan}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            title="Export plan to CSV for comparison"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export Plan
          </PermissionAwareButton>
        )}

        {/* Analyze with AI Button + Model Selector - shows when plan is generated */}
        {prePlan && (
          <div className="flex items-center gap-3">
            {/* Analyze button group */}
            <div className="flex">
              <PermissionAwareButton
                onClick={handleAnalyzeWithAI}
                disabled={analyzingPlan || optimizing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-l-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-r border-blue-500"
              >
                {analyzingPlan ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <LightBulbIcon className="h-4 w-4" />
                )}
                Analyze
              </PermissionAwareButton>
              <PermissionAwareSelect
                value={selectedAiModel}
                onChange={(e) => setSelectedAiModel(e.target.value)}
                disabled={analyzingPlan || optimizing}
                className="px-3 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer text-sm appearance-none pr-8 bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3d%22http%3a%2f%2fwww.w3.org%2f2000%2fsvg%22%20width%3d%2212%22%20height%3d%2212%22%20viewBox%3d%220%200%2012%2012%22%3e%3cpath%20fill%3d%22%23ffffff%22%20d%3d%22M2%204l4%204%204-4%22%2f%3e%3c%2fsvg%3e')] bg-no-repeat bg-[right_0.5rem_center]"
                title="Select AI Model"
              >
                {aiModelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-white text-gray-900">
                    {opt.label}
                  </option>
                ))}
              </PermissionAwareSelect>
            </div>
            {/* AI Optimize button */}
            <PermissionAwareButton
              onClick={handleOptimizeWithAI}
              disabled={analyzingPlan || optimizing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Get specific suggestions to optimize the plan"
            >
              {optimizing ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <SparklesIcon className="h-4 w-4" />
              )}
              AI Optimize
            </PermissionAwareButton>
            {/* Cost Estimate */}
            {aiCostEstimate && (
              <Tooltip content={`~${aiCostEstimate.inputTokens.toLocaleString()} input + ~${aiCostEstimate.outputTokens.toLocaleString()} output tokens`}>
                <span className="text-xs text-muted cursor-help">
                  ~${aiCostEstimate.totalCost.toFixed(3)}
                </span>
              </Tooltip>
            )}
          </div>
        )}


        {error && (
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        )}
      </div>
      </PermissionsContainer>

      {/* Drop Validation Error Toast */}
      {dropValidationError && (
        <div className="fixed top-16 right-4 z-50 bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg max-w-md">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm font-medium">{dropValidationError}</span>
          </div>
        </div>
      )}

      {/* AI Analysis Panel - Collapsible */}
      {aiAnalysis && (
        <div className="border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowAiAnalysis(!showAiAnalysis)}
            className="w-full px-6 py-3 bg-blue-50 dark:bg-blue-900/20 flex items-center justify-between hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              {showAiAnalysis ? (
                <ChevronDownIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              )}
              <LightBulbIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="font-medium text-blue-900 dark:text-blue-100">
                AI Analysis
              </span>
              {aiAnalysis.optimization_score !== undefined && (
                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                  aiAnalysis.optimization_score >= 0.7
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : aiAnalysis.optimization_score >= 0.5
                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                    : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                }`}>
                  Score: {(aiAnalysis.optimization_score * 100).toFixed(0)}%
                </span>
              )}
              {/* Actual Cost */}
              {aiActualCost && (
                <Tooltip content={`${aiActualCost.inputTokens.toLocaleString()} input + ${aiActualCost.outputTokens.toLocaleString()} output tokens`}>
                  <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 cursor-help">
                    ${aiActualCost.totalCost.toFixed(3)}
                  </span>
                </Tooltip>
              )}
            </div>
            <span className="text-sm text-blue-600 dark:text-blue-400">
              {showAiAnalysis ? 'Hide' : 'Show'} Analysis
            </span>
          </button>

          {showAiAnalysis && (
            <div className="px-6 py-4 bg-panel space-y-4">
              {/* Warnings - show prominently if any */}
              {aiAnalysis.warnings.length > 0 && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <h4 className="font-medium text-orange-900 dark:text-orange-100 flex items-center gap-2 mb-2">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    Warnings
                  </h4>
                  <ul className="space-y-1 text-sm text-orange-800 dark:text-orange-200">
                    {aiAnalysis.warnings.map((warning, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-orange-500">•</span>
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Main Analysis */}
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <div className="text-label whitespace-pre-wrap leading-relaxed">
                  {aiAnalysis.analysis}
                </div>
              </div>

              {/* Quick Suggestions - if extracted separately */}
              {aiAnalysis.suggestions.length > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 dark:text-green-100 flex items-center gap-2 mb-2">
                    <CheckIcon className="h-5 w-5" />
                    Quick Suggestions
                  </h4>
                  <ul className="space-y-1 text-sm text-green-800 dark:text-green-200">
                    {aiAnalysis.suggestions.map((suggestion, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-green-500">•</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Copy Data for Comparison */}
              <div className="bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-body flex items-center gap-2">
                      <ClipboardDocumentIcon className="h-5 w-5 text-gray-500" />
                      Copy Data for Comparison
                    </h4>
                    <p className="text-sm text-muted mt-1">
                      Copy driver assignments and warnings to compare against AI findings
                    </p>
                  </div>
                  <button
                    onClick={handleCopyDriverData}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      dataCopied
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50'
                    }`}
                  >
                    {dataCopied ? (
                      <span className="flex items-center gap-2">
                        <CheckIcon className="h-4 w-4" />
                        Copied!
                      </span>
                    ) : (
                      'Copy Data'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI Optimization Suggestions Panel - Collapsible */}
      {aiOptimizations && (
        <div className="border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowOptimizations(!showOptimizations)}
            className="w-full px-6 py-3 bg-violet-50 dark:bg-violet-900/20 flex items-center justify-between hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              {showOptimizations ? (
                <ChevronDownIcon className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              )}
              <SparklesIcon className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              <span className="font-medium text-violet-900 dark:text-violet-100">
                AI Optimization Suggestions
              </span>
              <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                {aiOptimizations.suggestions.length} suggestions
              </span>
              {aiOptimizations.input_tokens && aiOptimizations.output_tokens && (
                <Tooltip content={`${aiOptimizations.input_tokens.toLocaleString()} input + ${aiOptimizations.output_tokens.toLocaleString()} output tokens`}>
                  <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 cursor-help">
                    ${(
                      (aiOptimizations.input_tokens / 1_000_000) * (aiOptimizations.model_used?.includes('opus') ? 15 : aiOptimizations.model_used?.includes('haiku') ? 0.8 : 3) +
                      (aiOptimizations.output_tokens / 1_000_000) * (aiOptimizations.model_used?.includes('opus') ? 75 : aiOptimizations.model_used?.includes('haiku') ? 4 : 15)
                    ).toFixed(3)}
                  </span>
                </Tooltip>
              )}
            </div>
            <span className="text-sm text-violet-600 dark:text-violet-400">
              {showOptimizations ? 'Hide' : 'Show'} Suggestions
            </span>
          </button>

          {showOptimizations && (
            <div className="px-6 py-4 bg-panel space-y-3">
              {/* Summary */}
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {aiOptimizations.summary}
              </p>

              {/* Suggestions List */}
              {aiOptimizations.suggestions.length === 0 ? (
                <div className="text-center py-8 text-muted">
                  <CheckIcon className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p>No optimization suggestions - the plan looks well balanced!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {aiOptimizations.suggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border ${
                        suggestion.priority === 'high'
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                          : suggestion.priority === 'medium'
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                          : 'bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Priority Badge */}
                        <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium uppercase ${
                          suggestion.priority === 'high'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                            : suggestion.priority === 'medium'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {suggestion.priority}
                        </span>
                        {/* Type Badge */}
                        <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
                          suggestion.type === 'swap'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                            : suggestion.type === 'assign'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                            : suggestion.type === 'remove'
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {suggestion.type}
                        </span>
                        {/* Content */}
                        <div className="flex-1">
                          <p className="font-medium text-body">
                            {suggestion.description}
                          </p>
                          {/* Details */}
                          <div className="mt-1 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                            {suggestion.move_pro && (
                              <p>
                                <span className="font-medium">Move:</span> PRO {suggestion.move_pro}
                                {suggestion.move_legnum && `-${suggestion.move_legnum}`}
                              </p>
                            )}
                            {suggestion.type === 'swap' && suggestion.from_driver && (
                              <p>
                                <span className="font-medium">From:</span> {suggestion.from_driver_name || suggestion.from_driver} ({suggestion.from_driver})
                                <span className="mx-2">→</span>
                                <span className="font-medium">To:</span> {suggestion.to_driver_name || suggestion.to_driver} ({suggestion.to_driver})
                              </p>
                            )}
                            {suggestion.type === 'assign' && suggestion.suggested_driver && (
                              <p>
                                <span className="font-medium">Assign to:</span> {suggestion.suggested_driver_name || suggestion.suggested_driver} ({suggestion.suggested_driver})
                              </p>
                            )}
                            {suggestion.reasoning && (
                              <p className="italic text-gray-500 dark:text-gray-500">
                                {suggestion.reasoning}
                              </p>
                            )}
                          </div>
                          {/* Apply button for actionable suggestions */}
                          {suggestion.type !== 'warning' && suggestion.move_pro && (
                            <button
                              onClick={() => handleApplyOptimization(suggestion)}
                              className="mt-2 px-3 py-1.5 bg-violet-600 text-white text-sm font-medium rounded hover:bg-violet-700 transition-colors"
                            >
                              Apply
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Collapsible Config + Stats Section */}
      <PermissionsContainer componentId="smart-preplanning-config" componentName="Planning Config">
      {(currentConfig || stats) && (
        <div className="border-b border-gray-200 dark:border-gray-700">
          {/* Toggle bar */}
          <button
            onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
            className="w-full px-6 py-1.5 bg-gray-50 dark:bg-gray-900 flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-xs text-muted"
          >
            {isHeaderCollapsed ? (
              <ChevronRightIcon className="h-3 w-3" />
            ) : (
              <ChevronDownIcon className="h-3 w-3" />
            )}
            {isHeaderCollapsed ? 'Show' : 'Hide'} Details
            {isHeaderCollapsed && stats && (
              <span className="ml-2 text-gray-400 dark:text-gray-500">
                — {stats.assigned_deliveries ?? (stats.assigned_moves - (stats.slotted_returns ?? 0) - (stats.assigned_prepulls ?? 0))}/{stats.total_deliveries ?? stats.total_moves} deliveries
                {(stats.total_export_returns ?? 0) > 0 && (
                  <span> {'\u00B7'} <span className="text-red-500">{stats.assigned_export_returns ?? 0}/{stats.total_export_returns} export terms</span></span>
                )}
                {' '}{'\u00B7'}{' '}{stats.drivers_with_assignments}/{stats.total_drivers_in_region} drivers
                {stats.unassigned_moves > 0 && (
                  <span className="text-orange-500"> {'\u00B7'} {stats.unassigned_moves} unassigned</span>
                )}
              </span>
            )}
          </button>

          {!isHeaderCollapsed && (
            <>
              {/* Config Display Bar */}
              {currentConfig && (
                <div className="relative px-6 py-2 bg-gray-50 dark:bg-gray-850 border-t border-gray-200 dark:border-gray-700 text-sm">
                  {canEditConfig && selectedRegion && (
                    <button
                      onClick={() => setIsConfigModalOpen(true)}
                      className="absolute top-2 right-6 inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                      Edit
                    </button>
                  )}
                  <div className="flex items-center gap-4">
                    <span className="text-muted font-medium">Scoring:</span>
                    <span className="text-label">
                      Distance {(currentConfig.weight_distance * 100).toFixed(0)}%
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="text-label">
                      Workload {(currentConfig.weight_workload * 100).toFixed(0)}%
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="text-label">
                      Profitability {(currentConfig.weight_profitability * 100).toFixed(0)}%
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="text-label">
                      Performance {(currentConfig.weight_driver_score * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-muted font-medium">Limits:</span>
                    <span className="text-label">
                      Max {currentConfig.max_dispatches} Loads
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="text-label">
                      Start {currentConfig.default_start_time}
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="text-label">
                      Max {currentConfig.distance_max_miles} mi
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="text-label">
                      Max Return {currentConfig.return_max_miles} mi
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="text-label">
                      Live Round Trips {currentConfig.round_trip_live_count}
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="text-label">
                      Drop Round Trips {currentConfig.round_trip_drop_count}
                    </span>
                  </div>
                </div>
              )}

              {/* Statistics Bar */}
              {stats && (
                <div className="flex items-center gap-6 px-6 py-3 bg-panel border-t border-gray-200 dark:border-gray-700">
                  <div className="text-sm">
                    <span className="text-muted">Deliveries:</span>{' '}
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {stats.assigned_deliveries ?? (stats.assigned_moves - (stats.slotted_returns ?? 0) - (stats.assigned_prepulls ?? 0))}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500">/{stats.total_deliveries ?? stats.total_moves}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted">Unassigned:</span>{' '}
                    <span
                      className={`font-medium ${
                        stats.unassigned_moves > 0
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-heading'
                      }`}
                    >
                      {stats.unassigned_moves}
                    </span>
                  </div>
                  {(stats.total_prepulls ?? 0) > 0 && (
                    <div className="text-sm">
                      <span className="text-muted">Pre-Pulls:</span>{' '}
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {stats.assigned_prepulls ?? 0}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">/{stats.total_prepulls}</span>
                    </div>
                  )}
                  {(stats.total_local_prepulls ?? 0) > 0 && (
                    <div className="text-sm">
                      <span className="text-muted">Local Pre-Pulls:</span>{' '}
                      <span className="font-medium text-cyan-600 dark:text-cyan-400">
                        {stats.assigned_local_prepulls ?? 0}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">/{stats.total_local_prepulls}</span>
                    </div>
                  )}
                  {(stats.total_export_returns ?? 0) > 0 && (
                    <div className="text-sm">
                      <span className="text-muted">Export Terms:</span>{' '}
                      <span className="font-medium text-red-600 dark:text-red-400">
                        {stats.assigned_export_returns ?? 0}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">/{stats.total_export_returns}</span>
                    </div>
                  )}
                  {(stats.slotted_returns ?? 0) > 0 && (
                    <div className="text-sm">
                      <span className="text-muted">Returns:</span>{' '}
                      <span className="font-medium text-yellow-600 dark:text-yellow-400">
                        +{stats.slotted_returns}
                      </span>
                    </div>
                  )}
                  <div className="text-sm">
                    <span className="text-muted">Total Moves:</span>{' '}
                    <span className="font-medium text-heading">{stats.assigned_moves}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted">Drivers:</span>{' '}
                    <span className="font-medium text-heading">
                      {stats.drivers_with_assignments}/{stats.total_drivers_in_region}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted">Avg Score:</span>{' '}
                    <span
                      className={`font-medium ${
                        stats.average_assignment_score >= 0.7
                          ? 'text-green-600 dark:text-green-400'
                          : stats.average_assignment_score >= 0.5
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-orange-600 dark:text-orange-400'
                      }`}
                    >
                      {formatScore(stats.average_assignment_score)}
                    </span>
                  </div>
                  <div className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                    Generated in {stats.generation_time_ms}ms
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      </PermissionsContainer>

      {/* Staging Handle - only visible when drawer is closed */}
      {!isStagingOpen && prePlan && (
        <StagingHandle
          count={stagedMoves.length}
          onClick={() => setIsStagingOpen(true)}
        />
      )}

      {/* Main Content - Kanban Board with Staging Drawer */}
      <PermissionsContainer componentId="smart-preplanning-board" componentName="Planning Board">
      <div className="flex-1 overflow-hidden flex flex-row">
        {/* Main content area */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
          <div className="flex items-center justify-center h-64">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : !prePlan ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted">
            <SparklesIcon className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg">Select a date and region to generate a pre-plan</p>
            <p className="text-sm mt-2">
              The system will optimize driver assignments based on distance, workload, and profitability
            </p>
          </div>
        ) : (
          <div className="h-full flex flex-col overflow-auto px-4 pb-4">
            {/* Search and Sort Controls - Sticky */}
            <div className="sticky top-0 z-20 -mx-4 px-6 pt-2 pb-2 mb-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm">
              <h2 className="text-xs font-medium text-muted mb-1.5">
                Drag and drop to reassign moves between drivers. Grouped moves (purple border) move together.
              </h2>
              <div className="flex items-center gap-4">
                {/* Search Input */}
                <div className="flex-1 max-w-md">
                  <PermissionAwareInput
                    type="text"
                    placeholder="Search by driver, unit #, PRO #, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-body placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                {/* Sort Dropdown */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted">Sort:</label>
                  <PermissionAwareSelect
                    value={driverSort}
                    onChange={(e) => setDriverSort(e.target.value as 'driver' | 'loads_asc' | 'loads_desc' | 'reassigned_desc')}
                    className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-body focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="driver">Driver Number</option>
                    <option value="loads_asc">Loads (Low to High)</option>
                    <option value="loads_desc">Loads (High to Low)</option>
                    <option value="reassigned_desc">Reassigned Work First</option>
                  </PermissionAwareSelect>
                </div>
                {/* Qualification Filter Chips */}
                <div className="flex items-center gap-1">
                  <label className="text-xs text-muted mr-0.5">Filter:</label>
                  {[
                    { key: 'sleeper', label: 'Sleeper' },
                    { key: 'daycab', label: 'Daycab' },
                    { key: 'hazmat', label: 'Hazmat' },
                    { key: 'tank', label: 'Tank' },
                    { key: '93k', label: '93K' },
                    { key: 'light_truck', label: 'Light' },
                  ].map(({ key, label }) => {
                    const isActive = qualificationFilters.has(key);
                    return (
                      <button
                        key={key}
                        onClick={() => {
                          setQualificationFilters(prev => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key);
                            else next.add(key);
                            return next;
                          });
                        }}
                        className={`px-2 py-0.5 text-[0.6875rem] font-medium rounded-full border transition-colors ${
                          isActive
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600'
                            : 'bg-panel text-muted border-gray-300 dark:border-gray-600 hover:bg-highlight'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {/* WHAT THIS APPLY WILL DO, as two chips you can click. Separate from
                    the qualification row above on purpose: those describe the truck,
                    these describe a change the apply makes to somebody's day. Each is
                    dropped when the board has none, rather than shown reading 0. */}
                {(['reassigned', 'street_turn'] as const)
                  .filter(key => actionCounts[key] > 0)
                  .map(key => {
                    const isActive = qualificationFilters.has(key);
                    const isTurn = key === 'street_turn';
                    const label = isTurn ? 'Street turns' : 'Reassigned';
                    return (
                      <button
                        key={key}
                        title={isTurn
                          ? `${actionCounts[key]} drivers carry a proposed street turn. Show only those.`
                          : `${actionCounts[key]} drivers are given work that is somebody else's today. Show only those.`}
                        onClick={() => {
                          setQualificationFilters(prev => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key);
                            else next.add(key);
                            return next;
                          });
                        }}
                        className={`px-2 py-0.5 text-[0.6875rem] font-semibold rounded-full border transition-colors ${
                          isActive
                            ? (isTurn
                              ? 'bg-violet-600 text-white border-violet-700'
                              : 'bg-amber-500 text-white border-amber-600')
                            : (isTurn
                              ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700 hover:bg-violet-100'
                              : 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700 hover:bg-amber-100')
                        }`}
                      >
                        {label} {actionCounts[key]}
                      </button>
                    );
                  })}
                {/* WHAT KIND OF WORK, a third group: these ask about the moves, not
                    the truck or the apply. Live and Drop take either when both are
                    on; Early appt narrows them (preplan-v2/loadFilters). */}
                <div className="flex items-center gap-1 pl-2 border-l border-gray-300 dark:border-gray-600">
                  {LOAD_FILTERS.map(({ key, label, noun }) => {
                    const isActive = loadFilters.has(key);
                    return (
                      <button
                        key={key}
                        aria-pressed={isActive}
                        title={`${loadFilterCounts[key]} drivers carry ${noun}. Show only those, and highlight the moves.`}
                        onClick={() => {
                          setLoadFilters(prev => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key);
                            else next.add(key);
                            return next;
                          });
                        }}
                        className={`px-2 py-0.5 text-[0.6875rem] font-semibold rounded-full border transition-colors ${
                          isActive
                            ? 'bg-sky-600 text-white border-sky-700'
                            : 'bg-sky-50 dark:bg-sky-900/30 text-sky-800 dark:text-sky-200 border-sky-300 dark:border-sky-700 hover:bg-sky-100'
                        }`}
                      >
                        {label} {loadFilterCounts[key]}
                      </button>
                    );
                  })}
                </div>
                {/* Results count when filtering */}
                {(searchQuery.trim() || qualificationFilters.size > 0 || loadFilters.size > 0) && (
                  <div className="text-xs text-muted">
                    {filteredSortedDrivers.length}/{localDrivers.length}
                  </div>
                )}
                {/* Driver summary - right aligned */}
                <div className="ml-auto text-xs text-muted">
                  {localDrivers.filter(d => d.items.length > 0).length} with loads {'\u00B7'} {localDrivers.filter(d => d.items.length === 0).length} without {'\u00B7'} {prePlan.unassigned_moves.length} unassigned
                </div>
              </div>
            </div>

            {/* Driver Rows - Stacked Vertically */}
            <div className="space-y-4">
              {/* Drivers with assignments (filtered and sorted) */}
              {filteredSortedDrivers
                .filter(driver => driver.items.length > 0)
                .map((driver, idx) => {
                  const originalDriverPlan = prePlan.driver_plans.find(
                    dp => dp.unitnumber === driver.unitnumber
                  );
                  const hasChanges = driverHasChanges(driver, originalDriverPlan, appliedMoves);
                  return (
                    <DriverRow
                      key={driver.unitnumber}
                      driver={driver}
                      items={driver.items}
                      index={idx + 1}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDebugClick={handleKanbanDebugClick}
                      onProClick={openOrderDrawer}
                      originalDriverPlan={originalDriverPlan}
                      isSelected={selectedDrivers.has(driver.unitnumber)}
                      selectionDisabled={!hasChanges}
                      onSelectionChange={(unitnumber, selected) => {
                        setSelectedDrivers(prev => {
                          const next = new Set(prev);
                          if (selected) {
                            next.add(unitnumber);
                          } else {
                            next.delete(unitnumber);
                          }
                          return next;
                        });
                      }}
                      appliedMoves={appliedMoves}
                      onQuickAssign={handleQuickAssign}
                      onSendToStaging={handleSendToStaging}
                      selectedMoves={selectedMoves}
                      onMoveSelectionChange={handleToggleMoveSelection}
                      loadFilterMatch={loadFilterMatch}
                    />
                  );
                })}

              {/* Drivers without assignments (filtered and sorted) */}
              {(() => {
                const driversWithLoads = filteredSortedDrivers.filter(d => d.items.length > 0);
                const driversWithoutLoads = filteredSortedDrivers.filter(d => d.items.length === 0);
                if (driversWithoutLoads.length === 0) return null;
                const startIndex = driversWithLoads.length + 1;
                return (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-medium text-muted mb-3">
                      Drivers Without Loads ({driversWithoutLoads.length})
                    </h3>
                    <div className="space-y-4">
                      {driversWithoutLoads.map((driver, idx) => (
                        <DriverRow
                          key={driver.unitnumber}
                          driver={driver}
                          items={driver.items}
                          index={startIndex + idx}
                          onDragStart={handleDragStart}
                          onDragOver={handleDragOver}
                          onDrop={handleDrop}
                          onDebugClick={handleKanbanDebugClick}
                          onProClick={openOrderDrawer}
                          onQuickAssign={handleQuickAssign}
                          onSendToStaging={handleSendToStaging}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Unassigned Section - Original detailed view */}
              <UnassignedMoveSection
                unassignedMoves={loadFilterMatch
                  ? prePlan.unassigned_moves.filter(m => matchesLoadFilters(m, loadFilters))
                  : prePlan.unassigned_moves}
                totalCount={loadFilterMatch ? prePlan.unassigned_moves.length : undefined}
                onProClick={openOrderDrawer}
              />
            </div>
          </div>
        )}
        </div>

        {/* Staging Drawer */}
        <StagingDrawer
          isOpen={isStagingOpen}
          onClose={() => setIsStagingOpen(false)}
          items={stagedMoves}
          width={stagingWidth}
          minWidth={stagingMinWidth}
          maxWidth={stagingMaxWidth}
          onWidthChange={setStagingWidth}
          onDragStart={handleDragStartFromStaging}
          onDragOver={handleDragOver}
          onDrop={handleDropToStaging}
          onDebugClick={(move) => {
            const driverPlan = prePlan?.driver_plans.find(dp =>
              dp.moves.some(m => m.pronumber === move.pronumber && m.legnum === move.legnum)
            );
            setDebugDrawerMove(move);
            setDebugDriverPlan(driverPlan || null);
            setIsDebugDrawerOpen(true);
          }}
          appliedMoves={appliedMoves}
        />
      </div>
      </PermissionsContainer>

      {/* Apply Results Modal */}
      <PermissionsContainer componentId="smart-preplanning-apply" componentName="Apply Plan Actions">
      {applyResults && (
        <div className="fixed inset-0 bg-scrim/50 flex items-center justify-center z-50">
          <div
            ref={applyResultsDrag.panelRef}
            style={applyResultsDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden"
          >
            <ModalHeader
              title="Apply Plan Results"
              onClose={() => {
                setApplyResults(null);
                setSelectedDrivers(new Set());
              }}
              dragHandleProps={applyResultsDrag.dragHandleProps}
            />
            <div className="px-6 py-4 max-h-96 overflow-y-auto">
              {/* Success Summary */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <CheckIcon className="h-5 w-5" />
                  <span className="font-medium">{applyResults.success.length} succeeded</span>
                </div>
                {applyResults.blocked.length > 0 && (
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span className="font-medium">{applyResults.blocked.length} blocked</span>
                  </div>
                )}
                {applyResults.failed.length > 0 && (
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                    <XMarkIcon className="h-5 w-5" />
                    <span className="font-medium">{applyResults.failed.length} failed</span>
                  </div>
                )}
              </div>

              {/* Blocked Moves List — held back before anything wrote; the leg keeps
                  the driver it had. A retry is refused until the stop is cleared. */}
              {applyResults.blocked.length > 0 && (
                <div className="space-y-2 mb-4">
                  <h4 className="text-sm font-medium text-label">Not applied — clear these first:</h4>
                  <div className="space-y-2">
                    {applyResults.blocked.map((held, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-amber-900 dark:text-amber-200">
                            PRO {held.pronumber}-{held.legnum}
                          </span>
                          <span className="text-sm text-amber-800 dark:text-amber-300">
                            Driver: {held.driver}
                          </span>
                        </div>
                        <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                          {held.error}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Failed Moves List */}
              {applyResults.failed.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-label">Failed Moves:</h4>
                  <div className="space-y-2">
                    {applyResults.failed.map((fail, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-red-900 dark:text-red-100">
                            PRO {fail.pronumber}-{fail.legnum}
                          </span>
                          <span className="text-sm text-red-700 dark:text-red-300">
                            Driver: {fail.driver}
                          </span>
                        </div>
                        <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                          {fail.error}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {applyResults.success.length > 0 && applyResults.failed.length === 0 && applyResults.blocked.length === 0 && (
                <div className="text-center py-4">
                  <CheckIcon className="h-12 w-12 mx-auto text-green-500 mb-2" />
                  <p className="text-gray-600 dark:text-gray-400">
                    All {applyResults.success.length} moves were applied successfully!
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              {applyResults.failed.length > 0 && (
                <button
                  onClick={() => {
                    // Keep only drivers with failed moves selected
                    const failedDrivers = new Set(applyResults.failed.map(f => f.driver));
                    setSelectedDrivers(failedDrivers);
                    setApplyResults(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
                >
                  Retry Failed
                </button>
              )}
              <button
                onClick={() => {
                  setApplyResults(null);
                  setSelectedDrivers(new Set());
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Drawer */}
      <MoveDebugDrawer
        isOpen={isDebugDrawerOpen}
        onClose={() => setIsDebugDrawerOpen(false)}
        move={debugDrawerMove}
        driverPlan={debugDriverPlan}
      />

      {/* Config Modal */}
      <SmartDispatchConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        selectedRegion={selectedRegion}
        title="Pre-Planning Configuration"
        mode="smart-preplanning"
        onSave={() => {
          // Reload config to update the display bar
          reloadCurrentConfig(selectedRegion);
          // Regenerate with new config if we have a plan
          if (prePlan && selectedRegion) {
            handleGenerate();
          }
        }}
      />

      {/* Apply Plan Confirmation Dialog */}
      <InfoModal
        isOpen={showApplyConfirmDialog}
        onClose={() => setShowApplyConfirmDialog(false)}
        title="Confirm Apply Plan"
        body={
          <div className="space-y-4">
            <p className="text-label">
              You are about to apply preplanning assignments for{' '}
              <span className="font-semibold">{selectedDrivers.size}</span> driver{selectedDrivers.size !== 1 ? 's' : ''} with{' '}
              <span className="font-semibold">
                {localDrivers
                  .filter(d => selectedDrivers.has(d.unitnumber))
                  .reduce((sum, d) => sum + d.items.reduce((itemSum, item) =>
                    itemSum + item.moves.filter(m => !appliedMoves.has(`${m.pronumber}-${m.legnum}`) && !m.was_applied_by_preplan).length, 0), 0)}
              </span>{' '}
              moves to CMS.
            </p>
            <p className="text-sm text-muted">
              This action will assign drivers to the selected moves. Are you sure you want to continue?
            </p>

            {/* STREET TURNS ARE OPTED INTO ONE AT A TIME, and never by default.
                Applying one is not a dispatch: it rewrites both orders, deletes the
                import's empty return, and asks the steamship line to approve the
                reuse. So the consequence is spelled out and nothing is pre-ticked. */}
            {reassignments.moves > 0 && (
              <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  {reassignments.moves} move{reassignments.moves !== 1 ? 's' : ''} will be taken
                  from {reassignments.drivers} other driver{reassignments.drivers !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                  You asked to replan, so the planner moved work it thought was better placed
                  elsewhere. Those legs are dispatched to someone today and applying this takes
                  them off that driver&rsquo;s day. They are marked &ldquo;from&nbsp;&lt;driver&gt;&rdquo; on the board.
                </p>
              </div>
            )}

            {proposedTurns.length > 0 && (
              <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                      {proposedTurns.length} street turn{proposedTurns.length !== 1 ? 's' : ''} proposed
                    </p>
                    <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                      Performing one reuses the import&rsquo;s container on the export, removes the
                      empty return, and e-mails the steamship line for approval. Leave a pairing
                      un-ticked and neither the reuse nor its leg is applied.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTurnsToPerform(prev =>
                      prev.size === proposedTurns.length ? new Set() : new Set(proposedTurns.map(t => t.key)))}
                    className="shrink-0 px-2 py-1 text-xs font-medium rounded border border-amber-400 dark:border-amber-600 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                  >
                    {turnsToPerform.size === proposedTurns.length ? 'Clear all' : 'Select all'}
                  </button>
                </div>
                <ul className="space-y-1 max-h-48 overflow-y-auto">
                  {proposedTurns.map(({ key, move, driver }) => (
                    <li key={key}>
                      <label className="flex items-start gap-2 text-xs text-amber-900 dark:text-amber-200 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={turnsToPerform.has(key)}
                          onChange={e => setTurnsToPerform(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(key); else next.delete(key);
                            return next;
                          })}
                          className="mt-0.5 h-3.5 w-3.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                        />
                        <span>
                          <span className="font-medium">{move.pronumber}</span>
                          {' \u2192 export '}
                          <span className="font-medium">{move.street_turn_export}</span>
                          {move.dest_name ? ` at ${move.dest_name}` : ''}
                          {' \u00b7 driver '}{driver}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                {turnsToPerform.size === 0 && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    None selected &mdash; no reuses will be performed and these legs stay unplanned.
                  </p>
                )}
              </div>
            )}
          </div>
        }
        buttons={[
          {
            label: 'Apply Plan',
            onClick: () => {
              setShowApplyConfirmDialog(false);
              handleApplyPlan();
            },
            variant: 'primary',
          },
          {
            label: 'Cancel',
            onClick: () => setShowApplyConfirmDialog(false),
            variant: 'secondary',
          },
        ]}
      />

      {/* Bulk Remove Selected Moves Confirmation Modal */}
      <InfoModal
        isOpen={showBulkRemoveConfirm}
        onClose={() => {
          if (!bulkRemoving) {
            setShowBulkRemoveConfirm(false);
            setBulkRemoveResults(null);
          }
        }}
        title="Remove Selected Moves"
        maxWidth="md"
        body={
          <div className="space-y-4">
            {!bulkRemoveResults ? (
              <>
                <p className="text-label">
                  Are you sure you want to remove <span className="font-semibold text-red-600 dark:text-red-400">{selectedMoves.size}</span> selected move{selectedMoves.size !== 1 ? 's' : ''} from their assigned drivers?
                </p>
                <p className="text-sm text-muted">
                  This will clear the driver assignment in CMS and mark the moves as unapplied in the preplan.
                </p>
              </>
            ) : (
              <div className={`p-3 rounded-lg ${
                bulkRemoveResults.failed > 0
                  ? 'bg-red-50 dark:bg-red-900/20'
                  : 'bg-green-50 dark:bg-green-900/20'
              }`}>
                {bulkRemoveResults.success > 0 && (
                  <p className="text-green-700 dark:text-green-300">
                    Successfully removed {bulkRemoveResults.success} move{bulkRemoveResults.success !== 1 ? 's' : ''} from drivers.
                  </p>
                )}
                {bulkRemoveResults.failed > 0 && (
                  <p className="text-red-700 dark:text-red-300">
                    Failed to remove {bulkRemoveResults.failed} move{bulkRemoveResults.failed !== 1 ? 's' : ''}.
                  </p>
                )}
                {bulkRemoveResults.errors && bulkRemoveResults.errors.length > 0 && (
                  <ul className="mt-2 text-sm text-red-600 dark:text-red-400 list-disc list-inside max-h-32 overflow-y-auto">
                    {bulkRemoveResults.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                    {bulkRemoveResults.errors.length > 5 && (
                      <li>...and {bulkRemoveResults.errors.length - 5} more errors</li>
                    )}
                  </ul>
                )}
              </div>
            )}

            {bulkRemoving && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Removing moves from drivers...</span>
              </div>
            )}
          </div>
        }
        buttons={[
          {
            label: bulkRemoving ? 'Removing...' : bulkRemoveResults ? 'Done' : `Remove ${selectedMoves.size} Move${selectedMoves.size !== 1 ? 's' : ''}`,
            onClick: bulkRemoveResults ? () => {
              setShowBulkRemoveConfirm(false);
              setBulkRemoveResults(null);
            } : handleConfirmBulkRemove,
            variant: bulkRemoveResults ? 'primary' : 'danger',
            disabled: bulkRemoving,
          },
          {
            label: 'Cancel',
            onClick: () => {
              setShowBulkRemoveConfirm(false);
              setBulkRemoveResults(null);
            },
            variant: 'secondary',
            disabled: bulkRemoving,
          },
        ]}
      />
      </PermissionsContainer>

      {/* Undo Preplanned Moves Modal */}
      <PermissionsContainer componentId="smart-preplanning-admin-actions" componentName="Admin Actions">
      <InfoModal
        isOpen={showUndoPreplannedModal}
        onClose={() => {
          if (!undoingPreplanned) {
            setShowUndoPreplannedModal(false);
            setSelectedPlansToUndo(new Set());
            setUndoPreplannedResults(null);
          }
        }}
        title="Undo Preplanned Moves"
        maxWidth="xl"
        body={
          <div className="space-y-4">
            {loadingAppliedPlans ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading applied plans...</span>
              </div>
            ) : appliedPlans.length === 0 ? (
              <p className="text-muted text-center py-4">
                No applied preplans found for this date and region.
              </p>
            ) : (
              <>
                <p className="text-label">
                  Select which preplans to undo. This will remove the driver assignments from CMS.
                </p>

                {/* Plan selection */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {appliedPlans.map((plan) => {
                    const isSelected = selectedPlansToUndo.has(plan.plan_id);
                    const createdTime = plan.created_at
                      ? new Date(plan.created_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })
                      : 'Unknown time';
                    const isCurrentPlan = prePlan && plan.plan_id === prePlan.plan_id;
                    const isPartialApply = plan.applied_count < plan.total_assignments;

                    return (
                      <label
                        key={plan.plan_id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            setSelectedPlansToUndo(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) {
                                next.add(plan.plan_id);
                              } else {
                                next.delete(plan.plan_id);
                              }
                              return next;
                            });
                          }}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-orange-600 focus:ring-orange-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-heading">
                              Plan from {createdTime}
                            </span>
                            {isCurrentPlan && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                Current
                              </span>
                            )}
                            {isPartialApply && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                                Partial
                              </span>
                            )}
                            {plan.status === 'superseded' && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                                Superseded
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium text-orange-600 dark:text-orange-400">
                              {plan.applied_count}
                            </span>
                            {' '}of {plan.total_assignments} move{plan.total_assignments !== 1 ? 's' : ''} applied
                            {plan.applied_by && (
                              <span className="ml-1">by {plan.applied_by}</span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Select All / None buttons */}
                <div className="flex gap-2 text-sm">
                  <button
                    onClick={() => setSelectedPlansToUndo(new Set(appliedPlans.map(p => p.plan_id)))}
                    className="text-orange-600 dark:text-orange-400 hover:underline"
                  >
                    Select All
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={() => setSelectedPlansToUndo(new Set())}
                    className="text-gray-600 dark:text-gray-400 hover:underline"
                  >
                    Select None
                  </button>
                </div>

                {/* Summary */}
                {selectedPlansToUndo.size > 0 && (
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <p className="text-orange-700 dark:text-orange-300">
                      <span className="font-medium">
                        {appliedPlans
                          .filter(p => selectedPlansToUndo.has(p.plan_id))
                          .reduce((sum, p) => sum + p.applied_count, 0)}
                      </span>
                      {' '}move{appliedPlans.filter(p => selectedPlansToUndo.has(p.plan_id)).reduce((sum, p) => sum + p.applied_count, 0) !== 1 ? 's' : ''}{' '}
                      will be undone from {selectedPlansToUndo.size} plan{selectedPlansToUndo.size !== 1 ? 's' : ''}.
                    </p>
                    <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                      A move the plan took from another driver goes back to that driver, and a
                      street turn the plan performed is reversed. Everything else is unassigned.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Progress/Results */}
            {undoingPreplanned && (
              <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Removing driver assignments...</span>
              </div>
            )}

            {undoPreplannedResults && (
              <div className={`p-3 rounded-lg ${
                undoPreplannedResults.failed > 0
                  ? 'bg-red-50 dark:bg-red-900/20'
                  : 'bg-green-50 dark:bg-green-900/20'
              }`}>
                {undoPreplannedResults.success > 0 && (
                  <p className="text-green-700 dark:text-green-300">
                    Successfully removed {undoPreplannedResults.success} assignment{undoPreplannedResults.success !== 1 ? 's' : ''}.
                  </p>
                )}
                {!!undoPreplannedResults.restored && (
                  <p className="text-green-700 dark:text-green-300">
                    Put {undoPreplannedResults.restored} move{undoPreplannedResults.restored !== 1 ? 's' : ''} back on the driver who had {undoPreplannedResults.restored !== 1 ? 'them' : 'it'} before the plan.
                  </p>
                )}
                {!!undoPreplannedResults.turnsUndone && (
                  <p className="text-green-700 dark:text-green-300">
                    Reversed {undoPreplannedResults.turnsUndone} street turn{undoPreplannedResults.turnsUndone !== 1 ? 's' : ''}.
                  </p>
                )}
                {!!undoPreplannedResults.kept && (
                  <p className="text-blue-700 dark:text-blue-300">
                    {undoPreplannedResults.kept} move{undoPreplannedResults.kept !== 1 ? 's were' : ' was'} already on {undoPreplannedResults.kept !== 1 ? 'their' : 'its'} driver before the plan and stay{undoPreplannedResults.kept !== 1 ? '' : 's'} there.
                  </p>
                )}
                {undoPreplannedResults.staleCleaned && undoPreplannedResults.staleCleaned > 0 && (
                  <p className="text-blue-700 dark:text-blue-300">
                    {undoPreplannedResults.staleCleaned} assignment{undoPreplannedResults.staleCleaned !== 1 ? 's were' : ' was'} already removed outside of preplanning and {undoPreplannedResults.staleCleaned !== 1 ? 'have' : 'has'} been cleaned up.
                  </p>
                )}
                {undoPreplannedResults.success === 0 && !undoPreplannedResults.staleCleaned && undoPreplannedResults.failed === 0
                  && !undoPreplannedResults.restored && !undoPreplannedResults.kept && (
                  <p className="text-label">
                    No assignments found to remove.
                  </p>
                )}
                {undoPreplannedResults.failed > 0 && (
                  <p className="text-red-700 dark:text-red-300">
                    Failed to remove {undoPreplannedResults.failed} assignment{undoPreplannedResults.failed !== 1 ? 's' : ''}.
                  </p>
                )}
                {undoPreplannedResults.errors && undoPreplannedResults.errors.length > 0 && (
                  <ul className="mt-2 text-sm text-red-600 dark:text-red-400 list-disc list-inside max-h-32 overflow-y-auto">
                    {undoPreplannedResults.errors.slice(0, 10).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                    {undoPreplannedResults.errors.length > 10 && (
                      <li>...and {undoPreplannedResults.errors.length - 10} more errors</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        }
        buttons={[
          {
            label: undoingPreplanned ? 'Undoing...' : `Undo ${selectedPlansToUndo.size > 0 ? appliedPlans.filter(p => selectedPlansToUndo.has(p.plan_id)).reduce((sum, p) => sum + p.applied_count, 0) : 0} Moves`,
            onClick: handleConfirmUndoPreplanned,
            variant: 'danger',
            disabled: undoingPreplanned || selectedPlansToUndo.size === 0 || loadingAppliedPlans,
          },
          {
            label: 'Cancel',
            onClick: () => {
              setShowUndoPreplannedModal(false);
              setSelectedPlansToUndo(new Set());
              setUndoPreplannedResults(null);
            },
            variant: 'secondary',
            disabled: undoingPreplanned,
          },
        ]}
      />
      </PermissionsContainer>

      {/* Deadline Reason Note Modal */}
      {showNoteModal && (
        <ReasonNoteModal
          onClose={handleNoteCancel}
          onSubmit={onNoteSubmit}
        />
      )}

      {/* Order Details Drawer */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}

export default SmartPrePlanning;
